import { useEffect, useState, useMemo, useRef, Fragment, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, addDoc, deleteDoc, collection, getDocs, query, orderBy, limit, where, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { QRCodeSVG } from 'qrcode.react';
import { getCurrentWeekRates, getHistoricalRates, toGBP, getRateForCurrency, convertCurrency } from '../../utils/exchangeRates';
import { db, storage, functions } from '../../firebase';
import { generateQuoteLetterPdf } from '../../utils/generateQuoteLetterPdf';
import { generateYourTourQuoteLetterPdf } from '../../utils/generateYourTourQuoteLetterPdf';
import { resizeCrestToJpegDataUrl } from '../../utils/normalizeCrestImage';
import { sendConfirmationLetterEmail, buildConfirmationLetterPdf } from '../../utils/sendConfirmationLetterEmail';
import { usesParentLetters, loadBrandLogoDataUrl, loadSavedCrestDataUrl } from '../../utils/letterAssets';
import { buildMealPlan } from '../../utils/mealPlan';
import { useAuth } from '../../contexts/AuthContext';
import useBasePath from '../../utils/useBasePath';
import { useT } from '../../contexts/LocaleContext';
import { marketMatch } from '../../contexts/MarketContext';
import { createPortalUser } from '../../utils/createUser';
import { useExpenseCategories } from '../../utils/expenseCategories';
import TaskChecklist from '../../components/TaskChecklist';
import SupplierAssignments from '../../components/SupplierAssignments';
import DocumentUpload from '../../components/DocumentUpload';
import SurveyPanel from '../../components/SurveyPanel';
import SocialMediaPlan from '../../components/SocialMediaPlan';
import GameContentChecklist from '../../components/GameContentChecklist';
import MessageThread from '../../components/MessageThread';
import ParticipantCards from '../../components/ParticipantCards';
import TeamInfoEditPanel from '../../components/TeamInfoEditPanel';
import StaffRoomingTab from '../../components/StaffRoomingTab';
import GroupFlightsTab from '../../components/GroupFlightsTab';
import { LLS_GROUPS, isLlsGroupType } from '../../constants/llsCentres';
import TeamsTab from '../../components/TeamsTab';
import { TourPreviewsTab } from '../../components/PortalPreview';
import TeamFixturesSection from '../../components/TeamFixtures';
import { squadAgeInfo } from '../../utils/teamFixtures';
import { buildFixtureFinderText } from '../../utils/fixtureFinder';
import TourNotes from '../../components/TourNotes';
import InvoicePanel from '../../components/InvoicePanel';
import InvoiceUpload from '../../components/InvoiceUpload';
import PadelanteQuote from '../../components/PadelanteQuote';
import ItineraryBuilder from '../../components/ItineraryBuilder';
import ResourcePack from '../../components/ResourcePack';
import ContractSigning from '../../components/ContractSigning';
import SportIcon from '../../components/SportIcon';
import { CrmPipelinePanel } from './CrmDatabase';
import {
  TOUR_CATEGORIES, CATEGORY_LABELS, CATEGORY_STYLES, CATEGORY_DOT,
  CATEGORY_COLORS, CATEGORY_FILLS,
  STAGE_STATUS_SYNC, CATEGORY_STATUS_SYNC,
  isCategoryComplete, nextCategory, showParticipants,
} from '../../utils/statuses';
import useStatusPipeline from '../../hooks/useStatusPipeline';

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };
// Padel and Yoga are booked once per squad, not per traveller (confirmed
// with MST) — same model as Fixtures/Training Sessions. Used by
// copyWebQuoteIntoCostSheet to fix these lines' pricing type, not just
// their unit price, when copying a web quote's extras onto the cost sheet.
// Padel/Yoga are priced per session booked (e.g. 2 sessions of ~20 people
// each for a bigger group) — independent of squads, which only affects
// Fixtures/Training Sessions.
const SESSION_BASED_ADDON_CATEGORIES = ['Padel', 'Yoga'];

const SCAN_URL_BASE  = 'https://mst-sports-tours.web.app/scan';
const SESSION_TTL_MS = 10 * 60 * 1000;

function isMobileDevice() {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function QRScanPanel({ currentUser, onScanned, onCancel }) {
  const [qrUrl,    setQrUrl]    = useState(null);
  const [phase,    setPhase]    = useState('generating');
  const [secsLeft, setSecsLeft] = useState(600);
  const unsubRef  = useRef(null);
  const timerRef  = useRef(null);

  const teardown = useCallback(() => {
    unsubRef.current?.();
    clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    async function createSession() {
      const t = crypto.randomUUID();
      await setDoc(doc(db, 'scanSessions', t), {
        status:    'pending',
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(new Date(Date.now() + SESSION_TTL_MS)),
      });
      setQrUrl(`${SCAN_URL_BASE}/${t}`);
      setPhase('waiting');
      timerRef.current = setInterval(() => {
        setSecsLeft((s) => {
          if (s <= 1) { clearInterval(timerRef.current); setPhase('error'); return 0; }
          return s - 1;
        });
      }, 1000);
      unsubRef.current = onSnapshot(doc(db, 'scanSessions', t), async (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        if (data.status !== 'uploaded') return;
        teardown();
        setPhase('scanning');
        try {
          let downloadUrl = null;
          try { downloadUrl = await getDownloadURL(storageRef(storage, data.storagePath)); } catch {}
          const scanFn = httpsCallable(functions, 'scanReceipt');
          const result = await scanFn({ storagePath: data.storagePath });
          onScanned(result.data, downloadUrl);
        } catch { setPhase('error'); }
      });
    }
    createSession().catch(() => setPhase('error'));
    return teardown;
  }, [currentUser.uid, onScanned, teardown]);

  const mins = String(Math.floor(secsLeft / 60)).padStart(2, '0');
  const secs = String(secsLeft % 60).padStart(2, '0');

  return (
    <div className="p-5 bg-surface rounded-[10px] border border-[#E5E7EB] mb-4">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[14px] font-semibold text-[#111827]">Scan Receipt with Phone</p>
          <p className="text-[12px] text-[#6B7280] mt-0.5">Point your phone camera at the QR code — no login needed.</p>
        </div>
        <button onClick={() => { teardown(); onCancel(); }} className="text-[12px] text-[#6B7280] hover:text-[#374151] ml-4 flex-shrink-0">Cancel</button>
      </div>
      {phase === 'generating' && (
        <div className="flex justify-center py-10">
          <svg className="w-6 h-6 animate-spin text-[#9CA3AF]" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      )}
      {phase === 'waiting' && qrUrl && (
        <div className="flex flex-col sm:flex-row items-center gap-8">
          <div className="p-3 bg-white border border-[#E5E7EB] rounded-[12px] flex-shrink-0">
            <QRCodeSVG value={qrUrl} size={160} level="M" />
          </div>
          <div className="flex-1">
            <ol className="space-y-3">
              {['Open your phone camera and point it at the QR code','Tap the link — it opens in your browser','Take a photo of the receipt or choose one from your photos','Tap "Send" — the form fills in automatically here'].map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-pb text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-[13px] text-[#374151]">{step}</span>
                </li>
              ))}
            </ol>
            <div className="mt-4 flex items-center gap-2">
              <svg className="w-4 h-4 animate-spin text-[#9CA3AF]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <span className="text-[12px] text-[#9CA3AF]">Waiting for phone… expires in {mins}:{secs}</span>
            </div>
          </div>
        </div>
      )}
      {phase === 'scanning' && (
        <div className="flex items-center gap-3 py-6">
          <svg className="w-5 h-5 animate-spin text-pb flex-shrink-0" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <span className="text-[13px] text-[#374151]">Receipt received — scanning with AI…</span>
        </div>
      )}
      {phase === 'error' && (
        <div className="py-3">
          <p className="text-[13px] text-dr mb-2">{secsLeft === 0 ? 'QR code expired.' : 'Something went wrong scanning the receipt.'}</p>
          <button onClick={() => { teardown(); onCancel(); }} className="text-[13px] text-pb hover:text-navy font-medium">Close and try again</button>
        </div>
      )}
    </div>
  );
}

const KIT_COLOUR_HEX = {
  white: '#FFFFFF', black: '#111827', grey: '#6B7280', red: '#DC2626',
  blue: '#1D4ED8', yellow: '#FBBF24', green: '#16A34A', orange: '#EA580C',
  navy: '#1e3a5f', maroon: '#7f1d1d', 'sky blue': '#38bdf8',
  purple: '#7c3aed', pink: '#ec4899',
};
function kitColourHex(name) {
  if (!name) return null;
  if (name.startsWith('#')) return name;
  return KIT_COLOUR_HEX[name.toLowerCase()] || null;
}

const JERSEY_PATH = 'M 8,8 L 22,18 Q 36,8 50,12 Q 64,8 78,18 L 92,8 L 100,30 L 82,42 L 78,110 L 22,110 L 18,42 L 0,30 Z';

function KitModel({ jerseyColour, jerseyColour2, jerseyPattern, shortsColour, socksColour, uid }) {
  const c1  = kitColourHex(jerseyColour)  || '#E5E7EB';
  const c2  = kitColourHex(jerseyColour2) || '#FFFFFF';
  const sc  = kitColourHex(shortsColour)  || '#E5E7EB';
  const sk  = kitColourHex(socksColour)   || '#E5E7EB';
  const pat = jerseyPattern || 'plain';
  return (
    <svg viewBox="0 0 100 220" width="80" height="176" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id={`jclip-${uid}`}><path d={JERSEY_PATH} /></clipPath>
        {pat === 'stripes' && (
          <pattern id={`jpat-${uid}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="5" height="10" fill={c1} />
            <rect x="5" y="0" width="5" height="10" fill={c2} />
          </pattern>
        )}
        {pat === 'hoops' && (
          <pattern id={`jpat-${uid}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="10" height="5" fill={c1} />
            <rect x="0" y="5" width="10" height="5" fill={c2} />
          </pattern>
        )}
      </defs>
      {pat === 'plain' && <path d={JERSEY_PATH} fill={c1} />}
      {(pat === 'stripes' || pat === 'hoops') && (
        <g clipPath={`url(#jclip-${uid})`}>
          <rect x="0" y="0" width="100" height="115" fill={`url(#jpat-${uid})`} />
        </g>
      )}
      {pat === 'halves' && (
        <g clipPath={`url(#jclip-${uid})`}>
          <rect x="0"  y="0" width="50" height="115" fill={c1} />
          <rect x="50" y="0" width="50" height="115" fill={c2} />
        </g>
      )}
      <path d={JERSEY_PATH} fill="none" stroke="#9CA3AF" strokeWidth="1.5" />
      <rect x="24" y="116" width="52" height="48" rx="4" fill={sc} stroke="#9CA3AF" strokeWidth="1.5" />
      <rect x="26" y="170" width="20" height="46" rx="3" fill={sk} stroke="#9CA3AF" strokeWidth="1.5" />
      <rect x="54" y="170" width="20" height="46" rx="3" fill={sk} stroke="#9CA3AF" strokeWidth="1.5" />
    </svg>
  );
}

const QUANTITY_ADDONS = {
  'Fixtures':          { max: 5 },
  'Training Sessions': { max: 5 },
  'Padel':             { max: 3 },
  'Yoga':              { max: 3 },
};

const FALLBACK_ADDON_CATEGORIES = [
  'Hotel & Food', 'Flights', 'Transport', 'Match Tickets', 'Stadium Tour',
  'Fixtures', 'Training Sessions', 'Padel', 'Yoga', 'Waterpark',
].map((name, i) => ({ id: name, name, order: i }));

const ANNOTATION_FIELDS = {
  'Transport':          { label: 'Transport scope', placeholder: 'e.g. Airport transfers only, or full tour transport' },
  'Fixtures':           { label: 'Fixture details', placeholder: 'e.g. 2 fixtures — home and away, details TBC' },
  'Stadium Tour':       { label: 'Stadium details', placeholder: 'e.g. 1 stadium — Camp Nou' },
  'Match Tickets':      { label: 'Match details', placeholder: 'e.g. 2 matches — Barcelona vs Real Madrid, 15 Mar' },
  'Training Sessions':  { label: 'Training details', placeholder: 'e.g. 3 sessions at FC Barcelona Academy' },
  'Padel':              { label: 'Duration', placeholder: 'e.g. 2 hours' },
  'Yoga':               { label: 'Session details', placeholder: 'e.g. 2 sessions, 60 mins each' },
};

const TABS = [
  { key: 'overview',   label: 'Overview',     labelKey: 'tour.tabOverview' },
  { key: 'quote',      label: 'Quote',        labelKey: 'tour.tabQuote' },
  { key: 'ops',        label: 'Ops',          labelKey: 'tour.tabOps' },
  { key: 'itinerary',  label: 'Itinerary',    labelKey: 'tour.tabItinerary' },
  { key: 'rooming',    label: 'Rooming',      labelKey: 'tour.tabRooming' },
  { key: 'flights',    label: 'Group Flights', labelKey: 'tour.tabFlights', groupFlightsOnly: true },
  { key: 'teams',      label: 'Teams',        labelKey: 'tour.tabTeams' },
  { key: 'registers',  label: 'Registers',    labelKey: 'tour.tabRegisters' },
  { key: 'expenses',   label: 'Expenses',     labelKey: 'tour.tabExpenses' },
  { key: 'team_info',  label: 'Team Info',    labelKey: 'tour.tabTeamInfo' },
  { key: 'social',     label: 'Social Media', labelKey: 'tour.tabSocial' },
  { key: 'letters',    label: 'Letters',      labelKey: 'tour.tabLetters' },
  { key: 'resources',  label: 'Resources',    labelKey: 'tour.tabResources' },
  { key: 'messages',   label: 'Messages',     labelKey: 'tour.tabMessages' },
  { key: 'previews',   label: 'Previews',     labelKey: 'tour.tabPreviews', adminOnly: true },
];

// The Padelante padel portal uses a slimmed tour cockpit — no itinerary,
// team info, letters or messages. The remaining tabs render identically to the
// MST cockpit (only Overview is trimmed and Quote is bespoke).
const PADELANTE_TAB_KEYS = new Set(['overview', 'quote', 'ops', 'rooming', 'registers', 'expenses', 'social']);

// CRM-origin tours (converted from the British Clubs & Schools CRM) get a
// permanently trimmed cockpit — same treatment as Padelante, different set.
const CRM_TAB_KEYS = new Set(['overview', 'quote', 'letters', 'messages']);

// Mic Cup and Long Haul tours get the Group Flights tab.
const GROUP_FLIGHTS_GROUP_TYPES = ['mic cup', 'long haul'];
const hasGroupFlightsTab = (v) => GROUP_FLIGHTS_GROUP_TYPES.includes((v || '').trim().toLowerCase());

const CURRENCY_SYMBOLS_EXP = { GBP: '£', EUR: '€', USD: '$' };

const DEFAULT_CHECKLIST_SECTIONS = [
  {
    title: 'Medical',
    items: [
      'Scissors', 'Gloves', 'Celatape', 'Eye drops', 'Alcohol Wipes',
      'Gauze pads', 'Plasters', 'Bandage', 'Instant cold pack',
      'Deep Freeze', 'Deep Heat', 'Paracetamol', 'Buscapidol', 'Ibuprofen',
    ].map((name) => ({ id: name.toLowerCase().replace(/\s+/g, '_'), name, adminQty: '' })),
  },
  {
    title: 'Equipment',
    items: [
      'Footballs', 'Bibs', 'Cones', 'First Aid Kit', 'Pump',
      'Knife', 'Oranges', 'Cutting board', 'Water Stocked', 'Snacks',
    ].map((name) => ({ id: name.toLowerCase().replace(/\s+/g, '_'), name, adminQty: '' })),
  },
];

const DEFAULT_CONFIRMATIONS = [
  { text: 'I have received all the equipment' },
  { text: 'I have answered all the questions' },
];

function fmtExpDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function fmtExpAmt(n, currency = 'GBP') {
  const sym = CURRENCY_SYMBOLS_EXP[currency] || '£';
  return `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function fmt(n, sym = '£') {
  if (n == null || n === '') return '—';
  return `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-0.5">{label}</p>
      <p className="text-[13px] text-[#111827]">{value || '—'}</p>
    </div>
  );
}

function eurToTourCurrency(amountEUR, tourCurrency, rates) {
  if (!tourCurrency || tourCurrency === 'EUR') return amountEUR;
  if (tourCurrency === 'GBP') return parseFloat((amountEUR * (rates?.EUR_GBP || 1)).toFixed(2));
  if (tourCurrency === 'USD') return parseFloat((amountEUR * (rates?.EUR_GBP || 1) / (rates?.USD_GBP || 1)).toFixed(2));
  return amountEUR;
}

// Build the editable static lines from a price code + selected addons.
// Auto lines (Tour Lead, Junior Lead, David's, Referral Fee, Large Cases) are NOT included here —
// they are computed at display/calculation time from the current group inputs.
export function buildStaticCostSheet(priceCode, selectedAddons, guests = 0, custCurrency = 'GBP', rates = null) {
  const addonNames = new Set((selectedAddons || []).map((a) => a.categoryName));
  const lines = [];
  for (const item of priceCode.items || []) {
    if (!item.included) continue;
    if (!addonNames.has(item.categoryName)) continue;
    const requestAddon = (selectedAddons || []).find((a) => a.categoryName === item.categoryName);
    let unitMST = Number(item.costToMST) || 0;
    let label = item.categoryName;
    if (item.variants?.length > 0) {
      const qty = Number(requestAddon?.quantity) || 1;
      const variant = item.variants.find((v) => Number(v.qty) === qty);
      if (variant) {
        unitMST = Number(variant.costToMST) || 0;
        const vl = `${qty} ${qty === 1 ? (item.variantLabel || 'item') : (item.variantLabel ? item.variantLabel + 's' : 'items')}`;
        label = `${item.categoryName} (${vl})`;
      }
    }
    const mstCurrency = item.mstCurrency || priceCode.currency;
    // Cust unit defaults to the MST cost converted into the customer's currency;
    // staff can overwrite it (which flips customerOverridden true and stops it auto-following).
    const unitCustomer = convertCurrency(unitMST, mstCurrency, custCurrency, rates || {});
    lines.push({
      id: `line_${item.categoryName.replace(/\W+/g, '_').toLowerCase()}`,
      label,
      categoryName: item.categoryName,
      // Flights are always per traveller (never MST's own Tour/Junior Leads,
      // who are already in Spain) regardless of how a given price code has
      // this category configured — several existing price codes have it set
      // to 'flat' or 'per_person', which is always wrong for this category.
      pricingType: item.categoryName === 'Flights' ? 'per_traveller' : (item.pricingType || 'flat'),
      mstCurrency,
      unitCostToMST: unitMST,
      unitCostToCustomer: unitCustomer,
      customerOverridden: false,
      note: '',
    });
  }
  // Bespoke items: free-text lines defined on the price code itself. They aren't
  // tour categories, so there's no matching addon to gate them on — always included.
  for (const ci of priceCode.customItems || []) {
    const mstCurrency = ci.mstCurrency || priceCode.currency;
    const unitMST = Number(ci.costToMST) || 0;
    lines.push({
      id: `line_custom_${ci.id}`,
      label: ci.name || 'Bespoke item',
      categoryName: ci.name || 'Bespoke item',
      pricingType: ci.pricingType || 'flat',
      mstCurrency,
      unitCostToMST: unitMST,
      unitCostToCustomer: convertCurrency(unitMST, mstCurrency, custCurrency, rates || {}),
      customerOverridden: false,
      note: ci.note || '',
    });
  }
  // Referral fee: editable internal line (MST-only cost, never charged to customer)
  const referralFeePerPlayer = Number(priceCode.referralFee) || 0;
  if (referralFeePerPlayer > 0) {
    lines.push({
      id: 'referral_fee',
      label: 'Referral Fee',
      categoryName: 'Referral Fee',
      pricingType: 'per_person',
      mstCurrency: priceCode.currency,
      unitCostToMST: referralFeePerPlayer,
      unitCostToCustomer: 0,
      isInternalOnly: true,
      note: `${referralFeePerPlayer} per player`,
    });
  }
  return lines;
}

// Cost sheet lines should list in the same order the customer sees them in
// the quote letter (Hotel & Food, Flights, Transport, ... then optional
// extras), not whatever order they happened to be added in — "Web Quote"
// lines all share that one categoryName, so match on the label's leading
// category name too. Anything unmatched (bespoke items, Referral Fee) sorts
// after every known category, in the order it was already in.
function categorySortIndex(line, orderList) {
  let idx = orderList.indexOf(line.categoryName);
  if (idx === -1) idx = orderList.findIndex((name) => line.label?.startsWith(name));
  return idx === -1 ? orderList.length : idx;
}
function sortByCategoryOrder(lines, categoryOrder) {
  const orderList = (categoryOrder && categoryOrder.length > 0 ? categoryOrder : FALLBACK_ADDON_CATEGORIES).map((c) => c.name);
  return lines
    .map((line, i) => ({ line, i, idx: categorySortIndex(line, orderList) }))
    .sort((a, b) => (a.idx - b.idx) || (a.i - b.i))
    .map((x) => x.line);
}

// Compute all totals from the editable cost sheet + current group inputs + auto lines.
// Returns { sym, totalHeadcount, nights, freePlaces, payingPlayers, totalCostToMST,
//           totalCostToCustomer, costPerPersonToCustomer, computedStatic, autoLines, allLines }
export function recalculateTotals(costSheet, priceCode, guests, customerStaff, tourLeads, juniorLeads, supportersTotal, departureDate, returnDate, largeCaseQty, rates, custCurrency = 'GBP', freePlacesOverride = null, categoryOrder = null, leaderCostBasis = 'nights') {
  const g   = Number(guests)        || 0;
  const cs  = Number(customerStaff) || 0;
  const tl  = Number(tourLeads)     || 0;
  const jl  = Number(juniorLeads)   || 0;
  const sup = Number(supportersTotal) || 0;
  const totalHeadcount = g + cs + tl + jl + sup;
  // Travellers only — excludes MST's own Tour/Junior Leads. Used for costs
  // that are only actually incurred for the group MST books on their behalf
  // (e.g. flights: Tour/Junior Leads are already in Spain and never fly).
  const totalTravellers = g + cs + sup;
  // The whole sheet is displayed and totalled in the customer's billing currency.
  // Each line's own mstCurrency is converted into this for the MST total.
  const tourCurrency = custCurrency || 'GBP';
  const sym = CURRENCY_SYMBOLS[tourCurrency] || '£';

  let nights = 1;
  if (departureDate && returnDate) {
    const dep = new Date(departureDate);
    const ret = new Date(returnDate);
    const diff = Math.round((ret - dep) / (1000 * 60 * 60 * 24));
    if (diff > 0) nights = diff;
  }

  // Static (editable) lines — sorted to match the order customers see on the
  // quote letter, regardless of the order they were added to the sheet in.
  const computedStatic = sortByCategoryOrder(costSheet || [], categoryOrder).map((line) => {
    const uMST  = Number(line.unitCostToMST)      || 0;
    const uCust = Number(line.unitCostToCustomer)  || 0;
    let mstNative = 0, customerTotal = 0, basis = '';
    if (line.pricingType === 'per_person') {
      mstNative     = uMST  * totalHeadcount;
      customerTotal = uCust * totalHeadcount;
      basis = `${totalHeadcount} people × ${sym}${uCust.toFixed(2)}/pp`;
    } else if (line.pricingType === 'per_person_per_night') {
      mstNative     = uMST  * totalHeadcount * nights;
      customerTotal = uCust * totalHeadcount * nights;
      basis = `${totalHeadcount} pp × ${nights} nights × ${sym}${uCust.toFixed(2)}`;
    } else if (line.pricingType === 'per_traveller') {
      mstNative     = uMST  * totalTravellers;
      customerTotal = uCust * totalTravellers;
      basis = `${totalTravellers} travellers (excl. Tour/Junior Leads) × ${sym}${uCust.toFixed(2)}/pp`;
    } else {
      mstNative     = uMST;
      customerTotal = uCust;
      basis = 'Flat';
    }
    const mstCurr = line.mstCurrency || tourCurrency;
    const mstTotal = mstCurr !== tourCurrency && rates
      ? convertCurrency(mstNative, mstCurr, tourCurrency, rates)
      : mstNative;
    return { ...line, mstTotal, mstNative, mstCurrNative: mstCurr, customerTotal, basis };
  });

  // Auto lines (always derived, never stored in the editable costSheet)
  const autoLines = [];

  // Large kit cases
  const flightsItem = (priceCode?.items || []).find((i) => i.categoryName === 'Flights' && i.included);
  const lcMST = Number(flightsItem?.largeCaseCostToMST) || 0;
  const lcCust = Number(flightsItem?.largeCaseCostToCustomer) || 0;
  const lcQty = Number(largeCaseQty) || 0;
  if (lcQty > 0 && flightsItem && (lcMST > 0 || lcCust > 0)) {
    const lcMstCurr = flightsItem.mstCurrency || tourCurrency;
    const lcNative  = lcMST * lcQty;
    const lcMstTotal = lcMstCurr !== tourCurrency && rates
      ? convertCurrency(lcNative, lcMstCurr, tourCurrency, rates)
      : lcNative;
    autoLines.push({ id: 'large_cases', label: `Large kit cases × ${lcQty}`, categoryName: 'Large kit cases',
      pricingType: 'auto', mstCurrency: lcMstCurr, mstTotal: lcMstTotal, customerTotal: lcCust * lcQty,
      basis: `${lcQty} × ${sym}${lcCust.toFixed(2)}`, isAutoLine: true });
  }

  // Leaders are costed by calendar days present, not nights stayed — a
  // 3-night trip is 4 days on the ground (arrival day through departure day).
  // Gated by leaderCostBasis (default 'nights', the pre-fix behavior) so
  // existing requests keep computing exactly as they always have unless a
  // staff member explicitly opts a tour into 'days' — see the "Update to
  // day-based leader cost" action on the Quote tab.
  const leaderDaysIsDayBased = leaderCostBasis === 'days';
  const leaderDays = leaderDaysIsDayBased ? nights + 1 : nights;
  const leaderUnitWord = leaderDaysIsDayBased ? 'day' : 'night';

  // Tour Leads: €100 per day/night each (basis depends on leaderCostBasis)
  if (tl > 0) {
    const eur = 100 * tl * leaderDays;
    const converted = eurToTourCurrency(eur, tourCurrency, rates);
    autoLines.push({ id: 'tour_lead', label: `Tour Lead${tl > 1 ? 's' : ''} (${tl} × €100 per ${leaderUnitWord} × ${leaderDays} ${leaderUnitWord}${leaderDays === 1 ? '' : 's'})`,
      categoryName: 'Tour Lead', pricingType: 'auto', mstCurrency: 'EUR',
      mstTotal: converted, customerTotal: converted,
      basis: `${tl} × €100 × ${leaderDays} ${leaderUnitWord}${leaderDays === 1 ? '' : 's'}`, isAutoLine: true, costEUR: eur });
  }

  // Junior Leads: €50 per day/night each (basis depends on leaderCostBasis)
  if (jl > 0) {
    const eur = 50 * jl * leaderDays;
    const converted = eurToTourCurrency(eur, tourCurrency, rates);
    autoLines.push({ id: 'junior_lead', label: `Junior Lead${jl > 1 ? 's' : ''} (${jl} × €50 per ${leaderUnitWord} × ${leaderDays} ${leaderUnitWord}${leaderDays === 1 ? '' : 's'})`,
      categoryName: 'Junior Lead', pricingType: 'auto', mstCurrency: 'EUR',
      mstTotal: converted, customerTotal: converted,
      basis: `${jl} × €50 × ${leaderDays} ${leaderUnitWord}${leaderDays === 1 ? '' : 's'}`, isAutoLine: true, costEUR: eur });
  }

  // David's commission: per player per day (EUR)
  const davidsCostPerPersonEUR = Number(priceCode?.davidsCostPerPerson) || 0;
  if (davidsCostPerPersonEUR > 0 && g > 0) {
    const eur = davidsCostPerPersonEUR * g * nights;
    const converted = eurToTourCurrency(eur, tourCurrency, rates);
    autoLines.push({ id: 'davids_commission', label: `David's Commission (${g} players × €${davidsCostPerPersonEUR}/day × ${nights} days)`,
      categoryName: "David's Commission", pricingType: 'auto', mstCurrency: 'EUR',
      mstTotal: converted, customerTotal: converted,
      basis: `${g} × €${davidsCostPerPersonEUR} × ${nights}d`, isAutoLine: true, costEUR: eur });
  }

  // Totals — internal-only static lines (referral fee) add to MST but not customer
  let totalMST = 0, totalCustomer = 0;
  for (const l of computedStatic) {
    totalMST += l.mstTotal;
    if (!l.isInternalOnly) totalCustomer += l.customerTotal;
  }
  for (const l of autoLines) { totalMST += l.mstTotal; if (!l.isInternalOnly) totalCustomer += l.customerTotal; }

  const totalPayingGroup = totalTravellers;
  // One free place per 15 paying travellers (15 paying + 1 free = 16, 30 + 2
  // = 32, 45 + 3 = 48), matching the public quote tool. Staff can override the
  // count on the quote tab — a free place's cost is absorbed by the payers, so
  // raising it spreads the same total over fewer people and lifts the per-person.
  const autoFreePlaces = Math.floor(totalPayingGroup / 16);
  const hasOverride = freePlacesOverride !== null && freePlacesOverride !== '' && !Number.isNaN(Number(freePlacesOverride));
  // Never let the override consume the whole group — at least one payer must remain.
  const freePlaces = hasOverride
    ? Math.min(Math.max(0, Math.floor(Number(freePlacesOverride))), Math.max(0, totalPayingGroup - 1))
    : autoFreePlaces;
  const payingPlayers = Math.max(1, totalPayingGroup - freePlaces);
  const costPerPersonToCustomer = payingPlayers > 0 ? totalCustomer / payingPlayers : 0;

  return { sym, totalHeadcount, nights, freePlaces, autoFreePlaces, freePlacesIsManual: hasOverride, payingPlayers,
    totalCostToMST: totalMST, totalCostToCustomer: totalCustomer,
    costPerPersonToCustomer, computedStatic, autoLines,
    allLines: [...computedStatic, ...autoLines] };
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isAdmin } = useAuth();
  const { stagesFor } = useStatusPipeline();
  const base = useBasePath();
  const tr = useT();
  const expenseCategories = useExpenseCategories();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Add-on editing
  const [editingAddons, setEditingAddons]   = useState(false);
  const [addonsDraft, setAddonsDraft]       = useState([]);
  const [addonCategories, setAddonCategories] = useState([]);
  const [savingAddons, setSavingAddons]     = useState(false);

  // Tab state driven by URL hash (#overview, #quote, etc.)
  const validTabKeys = TABS.map((t) => t.key);
  const hashTab = location.hash.replace('#', '');
  const [activeTab, setActiveTab] = useState(
    validTabKeys.includes(hashTab) ? hashTab : 'overview'
  );

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
    navigate({ hash: key }, { replace: true });
  }, [navigate]);

  // Padelante tours use the slimmed cockpit (7 tabs) and a trimmed Overview.
  const isPadelante = request?.brand === 'padelante';
  // CRM-origin tours use an even slimmer cockpit — Overview, Quote, Letters,
  // Messages only. A tour is never both, but check Padelante first regardless.
  const isCrmOrigin = !isPadelante && request?.crmOrigin === true && !request?.crmFullyConverted;
  const showGroupFlights = hasGroupFlightsTab(request?.groupType);
  // Previews (see the tour as a supplier, tour leader, … sees it) is for admins
  // only, on every kind of tour.
  const visibleTabs = (isPadelante ? TABS.filter((t) => PADELANTE_TAB_KEYS.has(t.key) || t.adminOnly)
    : isCrmOrigin ? TABS.filter((t) => CRM_TAB_KEYS.has(t.key) || t.adminOnly)
    : TABS).filter((t) => (!t.adminOnly || isAdmin) && (!t.groupFlightsOnly || showGroupFlights));

  // Sync tab if hash changes via browser back/forward
  useEffect(() => {
    const key = location.hash.replace('#', '');
    if (validTabKeys.includes(key) && key !== activeTab) setActiveTab(key);
  }, [location.hash]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bounce a Padelante or CRM-origin tour off any tab that isn't in its
  // slimmed set (e.g. a stale #itinerary deep-link) back to Overview.
  useEffect(() => {
    if (activeTab === 'previews' && isAdmin) return;
    if (isPadelante && !PADELANTE_TAB_KEYS.has(activeTab)) setActiveTab('overview');
    else if (isCrmOrigin && !CRM_TAB_KEYS.has(activeTab)) setActiveTab('overview');
    else if (request && activeTab === 'flights' && !showGroupFlights) setActiveTab('overview');
  }, [isPadelante, isCrmOrigin, showGroupFlights, request, activeTab, isAdmin]);

  // Exchange rates — locked to the saved quote's rateWeek once a quote exists
  const [exchangeRates, setExchangeRates] = useState(null);

  // Quote state
  const [priceCodes, setPriceCodes] = useState([]);
  const [selectedPriceCodeId, setSelectedPriceCodeId] = useState('');
  const [tourLeadCount, setTourLeadCount] = useState('1');
  const [juniorLeadCount, setJuniorLeadCount] = useState('0');
  const [costSheet, setCostSheet] = useState([]);           // editable static lines
  const [webQuoteCopied, setWebQuoteCopied] = useState(false); // brief confirmation after copying web quote figures in
  // A lead can carry several quote options (see CrmPipelinePanel) — switching
  // which one is active changes request.estimateDetails, but the checkmark
  // above shouldn't keep claiming "copied" for figures from a DIFFERENT
  // quote option. Reset it whenever the active quote changes so staff get a
  // clear prompt to re-copy the now-current one.
  useEffect(() => {
    setWebQuoteCopied(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.activeQuoteAt]);
  const [custCurrency, setCustCurrency] = useState('GBP');  // currency the CUSTOMER is billed in (independent of per-line MST currency)
  const costSheetPriceCodeRef = useRef(null);               // tracks which price code the sheet was built for
  const custCurrencyInitRef = useRef(false);                // ensures customer currency is seeded from Firestore only once
  const [savingQuote, setSavingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [annotations, setAnnotations] = useState({});
  const [largeCaseQuantity, setLargeCaseQuantity] = useState('');
  // Manual free-place count — '' means "use the automatic 1-per-15 figure"
  const [freePlacesOverride, setFreePlacesOverride] = useState('');
  // Customer price slider override (total, not per-person)
  const [customerPriceOverride, setCustomerPriceOverride] = useState(null);
  // Which rows have their note textarea expanded
  const [expandedNotes, setExpandedNotes] = useState({});
  // Reusable quote templates — a saved cost sheet + its related quote settings,
  // global across tours (mirrors ItineraryBuilder's skeletalTemplates pattern).
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [selectedQuoteTemplateId, setSelectedQuoteTemplateId] = useState('');
  const [savingQuoteTemplate, setSavingQuoteTemplate] = useState(false);
  const [applyingQuoteTemplate, setApplyingQuoteTemplate] = useState(false);

  // Multiple quote options per lead — requests/{id}/quotes subcollection.
  // The parent doc's quoteDetails/priceCodeId etc. always mirror whichever
  // quote was last saved here, purely for backward compatibility with older
  // readers (invoices, finance exports) that only know about that field.
  const [quotes, setQuotes] = useState([]);
  const [activeQuoteId, setActiveQuoteId] = useState(null);
  // activeQuoteId is only ever local state — a page reload always starts it
  // at null, which saveQuote() reads as "create a new quote option". Without
  // this, every reload-then-Save-Quote silently created a duplicate instead
  // of updating the one already being worked on. Runs once per page load.
  const autoSelectedQuoteRef = useRef(false);
  // Per-quote dates — each quote option can price a different date range.
  // Defaults to the tour's own dates when starting a brand-new quote.
  const [quoteDepartureDate, setQuoteDepartureDate] = useState('');
  const [quoteReturnDate, setQuoteReturnDate] = useState('');
  const quoteDatesInitRef = useRef(false);
  // Per-quote flight price/time — different dates mean different flight
  // times and fares, so these live with the quote, not the whole tour.
  const [quoteFlightPriceManual, setQuoteFlightPriceManual] = useState('');
  const [quoteFlightTimeManual, setQuoteFlightTimeManual] = useState('');

  // Flight details
  const [flightDetails, setFlightDetails] = useState({ airline: '', outboundFlight: '', outboundTime: '', arrivalTime: '', returnFlight: '', returnTime: '', returnArrivalTime: '' });
  const [savingFlight, setSavingFlight] = useState(false);
  const [resendingLetter, setResendingLetter] = useState(false);
  const [resendLetterSent, setResendLetterSent] = useState(false);
  const [resendLetterError, setResendLetterError] = useState('');

  // Tour names
  const [tourName, setTourName]         = useState('');
  const [internalRef, setInternalRef]   = useState('');
  const [savingTourName, setSavingTourName] = useState(false);
  const [uploadingCrest, setUploadingCrest] = useState(false);
  const overviewCrestInputRef = useRef(null);
  const [editingContacts, setEditingContacts] = useState(false);
  const [contactsDraft, setContactsDraft] = useState([]);
  const [primaryDraft, setPrimaryDraft] = useState({ contactName: '', phone: '', customerEmail: '' });
  const [savingContacts, setSavingContacts] = useState(false);

  // Internal notes

  // Tour Details editing
  const [editingTourDetails, setEditingTourDetails] = useState(false);
  const [tourDetailsDraft, setTourDetailsDraft] = useState({ sport: '', destination: '', departureDate: '', returnDate: '' });
  const [savingTourDetails, setSavingTourDetails] = useState(false);

  // Group Info editing
  const [editingGroupInfo, setEditingGroupInfo] = useState(false);
  const [groupInfoDraft, setGroupInfoDraft] = useState({ groupType: '', llsGroup: '', participants: '', participantsMale: '', participantsFemale: '', supportersMale: '', supportersFemale: '', numberOfSquads: '', customerStaff: '', customerStaffMale: '', customerStaffFemale: '' });
  const [savingGroupInfo, setSavingGroupInfo] = useState(false);

  // Tour leader assignment
  const [tourLeaders, setTourLeaders]     = useState([]);
  const [selectedLeaderId, setSelectedLeaderId] = useState('');
  const [savingLeader, setSavingLeader]   = useState(false);
  const [removingLeaderId, setRemovingLeaderId] = useState(null);

  // Marketing assignment
  const [marketingUsers, setMarketingUsers] = useState([]);
  const [selectedMarketingId, setSelectedMarketingId] = useState('');
  const [savingMarketing, setSavingMarketing] = useState(false);

  // Registers submitted by tour leader
  const [registers, setRegisters] = useState([]);

  // Expenses linked to this request
  const [expenses, setExpenses] = useState([]);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showInvoiceUpload, setShowInvoiceUpload] = useState(false);
  const [addExpenseForm, setAddExpenseForm] = useState({ date: new Date().toISOString().split('T')[0], category: 'Accommodation', description: '', amount: '', currency: 'GBP', paymentStatus: 'paid' });
  const [createInvoiceForExpId, setCreateInvoiceForExpId] = useState(null);
  const [createInvoiceForm, setCreateInvoiceForm] = useState({ supplierName: '', supplierEmail: '', serviceType: '' });
  const [createInvoiceFile, setCreateInvoiceFile] = useState(null);
  const [createInvoiceSaving, setCreateInvoiceSaving] = useState(false);
  const createInvoiceFileRef = useRef(null);
  const [addExpenseSaving, setAddExpenseSaving] = useState(false);
  const [addExpenseError, setAddExpenseError] = useState('');
  const [addExpenseReceiptUrl, setAddExpenseReceiptUrl] = useState('');
  const [expScanMode, setExpScanMode] = useState(null); // null | 'mobile' | 'qr'
  const [expMobileScanPhase, setExpMobileScanPhase] = useState('idle');
  const [expMobileScanError, setExpMobileScanError] = useState('');
  const expScanFileRef = useRef(null);
  // Supplier assignments (bookingSuppliers) for this tour — used for expense linking
  const [tourAssignments, setTourAssignments] = useState([]);

  // Team info (submitted by coach)
  const [teamInfo, setTeamInfo] = useState(null);
  const [teamInfoLoading, setTeamInfoLoading] = useState(false);
  const [editingTeamInfo, setEditingTeamInfo] = useState(false);

  // Team info notes (staff-authored, attributed + timestamped — distinct from Internal Notes)
  const [teamNotes, setTeamNotes] = useState([]);
  const [newTeamNote, setNewTeamNote] = useState('');
  const [savingTeamNote, setSavingTeamNote] = useState(false);

  // Letters
  const [sentLetters, setSentLetters] = useState([]);
  const [letterTemplatesAll, setLetterTemplatesAll] = useState([]);
  const [selectedSendTemplateId, setSelectedSendTemplateId] = useState('');
  const [sendingLetter, setSendingLetter] = useState(false);
  const [sendRecipientMode, setSendRecipientMode] = useState('contact'); // 'contact' | 'custom'
  const [customRecipientEmail, setCustomRecipientEmail] = useState('');
  const [previewingLetter, setPreviewingLetter] = useState(false);
  const [previewingQuotePdf, setPreviewingQuotePdf] = useState(null); // 'quote' | 'confirmation' | null
  const [previewLetterError, setPreviewLetterError] = useState('');
  const [letterUploadName, setLetterUploadName] = useState('');
  const [letterShareWithCustomer, setLetterShareWithCustomer] = useState(true);
  const [uploadingLetter, setUploadingLetter] = useState(false);
  const [letterUploadError, setLetterUploadError] = useState('');
  const letterUploadInputRef = useRef(null);

  // Quote letter PDF sending
  const [sendingQuoteLetter, setSendingQuoteLetter] = useState(false);
  const [quoteLetterError, setQuoteLetterError] = useState('');
  const [quoteLetterSent, setQuoteLetterSent] = useState(false);
  const [showCrestModal, setShowCrestModal] = useState(false);
  const [crestDataUrl, setCrestDataUrl] = useState(null);
  const crestInputRef = useRef(null);

  // Tour ops checklist
  const [checklistSections, setChecklistSections] = useState(
    DEFAULT_CHECKLIST_SECTIONS.map((sec) => ({ ...sec, items: sec.items.map((it) => ({ ...it })) }))
  );
  const [pushingChecklist, setPushingChecklist] = useState(false);
  const [checklistPushed, setChecklistPushed] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'requests', id), (snap) => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setRequest(data);
        if (data.priceCodeId) setSelectedPriceCodeId(data.priceCodeId);
        // Seed the customer billing currency once. Existing quotes keep whatever
        // they were saved with (data.currency); brand-new ones default to GBP,
        // except the Spain→UK portal which bills its customers in EUR.
        if (!custCurrencyInitRef.current) {
          custCurrencyInitRef.current = true;
          const seeded = data.quoteDetails?.custCurrency
            || data.currency
            || (data.market === 'spain_to_uk' ? 'EUR' : 'GBP');
          setCustCurrency(seeded);
        }
        if (data.addonAnnotations) setAnnotations(data.addonAnnotations);
        if (data.tourName != null) setTourName(data.tourName);
        if (data.internalRef != null) setInternalRef(data.internalRef);
        if (data.tourLeadCount != null) {
          setTourLeadCount(String(data.tourLeadCount));
        } else if (data.staffCount != null) {
          setTourLeadCount(String(data.staffCount));
        }
        if (data.juniorLeadCount != null) setJuniorLeadCount(String(data.juniorLeadCount));
        if (data.largeCaseQuantity != null) setLargeCaseQuantity(String(data.largeCaseQuantity));
        if (data.quoteDetails?.freePlacesOverride != null) {
          setFreePlacesOverride(String(data.quoteDetails.freePlacesOverride));
        }
        if (data.quoteDetails?.customerPriceAdjustment != null) {
          setCustomerPriceOverride(data.quoteDetails.customerPriceAdjustment);
        }
        if (!quoteDatesInitRef.current) {
          quoteDatesInitRef.current = true;
          setQuoteDepartureDate(data.quoteDetails?.departureDate || data.departureDate || '');
          setQuoteReturnDate(data.quoteDetails?.returnDate || data.returnDate || '');
        }
        if (data.flightDetails) setFlightDetails({
          airline:           data.flightDetails.airline           || '',
          outboundFlight:    data.flightDetails.outboundFlight    || '',
          outboundTime:      data.flightDetails.outboundTime      || '',
          arrivalTime:       data.flightDetails.arrivalTime       || '',
          returnFlight:      data.flightDetails.returnFlight      || '',
          returnTime:        data.flightDetails.returnTime        || '',
          returnArrivalTime: data.flightDetails.returnArrivalTime || '',
        });
      }
      setLoading(false);
    });
    return unsub;
  }, [id]);

  // Multiple quote options for this lead — live list, oldest first.
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'requests', id, 'quotes'), orderBy('createdAt', 'asc')),
      (snap) => setQuotes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setQuotes([])
    );
    return unsub;
  }, [id]);

  // On first load, default into editing the most recently saved quote option
  // rather than leaving activeQuoteId at null (= "unsaved new quote") — see
  // the comment on autoSelectedQuoteRef above. Only fires once; "+ New quote"
  // remains the only way to deliberately start a blank one afterward.
  useEffect(() => {
    if (autoSelectedQuoteRef.current || activeQuoteId || quotes.length === 0) return;
    autoSelectedQuoteRef.current = true;
    loadQuoteForEdit(quotes[quotes.length - 1]);
  }, [quotes, activeQuoteId]);

  useEffect(() => {
    getDocs(query(collection(db, 'priceCodes'), orderBy('createdAt', 'desc'))).then((snap) => {
      setPriceCodes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  // Lazy-load addon categories when edit mode opens
  useEffect(() => {
    if (!editingAddons || addonCategories.length > 0) return;
    getDocs(query(collection(db, 'tourCategories'), orderBy('order', 'asc')))
      .then((snap) => setAddonCategories(
        snap.empty ? FALLBACK_ADDON_CATEGORIES
          : snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.active !== false)
      ))
      .catch(() => setAddonCategories(FALLBACK_ADDON_CATEGORIES));
  }, [editingAddons, addonCategories.length]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'expenses'), where('requestId', '==', id), orderBy('date', 'desc')),
      (snap) => setExpenses(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [id]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'bookingSuppliers'), where('requestId', '==', id)),
      (snap) => setTourAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [id]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'requests', id, 'registers'), orderBy('takenAt', 'desc')),
      (snap) => setRegisters(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [id]);

  useEffect(() => {
    // Fetch users with tour_leader access — covers both legacy role: string and new roles: string[] format
    Promise.all([
      getDocs(query(collection(db, 'users'), where('roles', 'array-contains', 'tour_leader'))),
      getDocs(query(collection(db, 'users'), where('role', '==', 'tour_leader'))),
    ]).then(([newSnap, legacySnap]) => {
      const seen = new Set();
      const merged = [];
      for (const d of [...newSnap.docs, ...legacySnap.docs]) {
        if (!seen.has(d.id)) { seen.add(d.id); merged.push({ id: d.id, ...d.data() }); }
      }
      setTourLeaders(merged);
    });
  }, []);

  useEffect(() => {
    // Fetch users with marketing access — covers both legacy role: string and new roles: string[] format
    Promise.all([
      getDocs(query(collection(db, 'users'), where('roles', 'array-contains', 'marketing'))),
      getDocs(query(collection(db, 'users'), where('role', '==', 'marketing'))),
    ]).then(([newSnap, legacySnap]) => {
      const seen = new Set();
      const merged = [];
      for (const d of [...newSnap.docs, ...legacySnap.docs]) {
        if (!seen.has(d.id)) { seen.add(d.id); merged.push({ id: d.id, ...d.data() }); }
      }
      setMarketingUsers(merged);
    });
  }, []);

  useEffect(() => {
    if (activeTab !== 'team_info') return;
    setTeamInfoLoading(true);
    getDoc(doc(db, 'requests', id, 'teamInfo', 'data'))
      .then((snap) => setTeamInfo(snap.exists() ? snap.data() : null))
      .finally(() => setTeamInfoLoading(false));
  }, [activeTab, id]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'requests', id, 'sentLetters'), orderBy('sentAt', 'desc')),
      (snap) => setSentLetters(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [id]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'requests', id, 'teamInfoNotes'), orderBy('createdAt', 'desc')),
      (snap) => setTeamNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [id]);

  useEffect(() => {
    getDocs(query(collection(db, 'letterTemplates'), orderBy('createdAt', 'asc'))).then((snap) => {
      setLetterTemplatesAll(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((t) => t.enabled !== false));
    });
  }, []);

  // Exchange rates: use the rate locked at save time when re-opening a quote; current rates for new quotes
  useEffect(() => {
    const savedRateWeek = request?.quoteDetails?.rateWeek;
    if (savedRateWeek) {
      getHistoricalRates(savedRateWeek).then(setExchangeRates).catch(() => getCurrentWeekRates().then(setExchangeRates));
    } else {
      getCurrentWeekRates().then(setExchangeRates).catch(() => {});
    }
  }, [request?.quoteDetails?.rateWeek]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed or restore the editable cost sheet when the price code changes.
  // Uses the saved costSheet from Firestore if available; otherwise builds from price code.
  // The ref guard prevents Firestore snapshots from resetting in-progress edits.
  useEffect(() => {
    if (!selectedPriceCodeId || !request || !exchangeRates) {
      if (!selectedPriceCodeId) { setCostSheet([]); costSheetPriceCodeRef.current = null; setWebQuoteCopied(false); }
      return;
    }
    if (costSheetPriceCodeRef.current === selectedPriceCodeId) return;
    const pc = priceCodes.find((p) => p.id === selectedPriceCodeId);
    if (!pc) return;
    costSheetPriceCodeRef.current = selectedPriceCodeId;
    setWebQuoteCopied(false);
    if (request.quoteDetails?.costSheet && request.priceCodeId === selectedPriceCodeId) {
      let sheet = request.quoteDetails.costSheet;
      // Saved quotes from before the referral-fee-as-editable-line change won't have it — add it
      const hasReferralFee = sheet.some((l) => l.id === 'referral_fee');
      const pcReferralFee = Number(pc.referralFee) || 0;
      if (!hasReferralFee && pcReferralFee > 0) {
        sheet = [...sheet, {
          id: 'referral_fee', label: 'Referral Fee', categoryName: 'Referral Fee',
          pricingType: 'per_person', mstCurrency: pc.currency,
          unitCostToMST: pcReferralFee, unitCostToCustomer: 0,
          isInternalOnly: true, note: `${pcReferralFee} per player`,
        }];
      }
      // Cust prices on already-saved lines are the human-approved numbers — keep
      // them and mark as overridden so later MST edits don't silently rewrite them.
      sheet = sheet.map((l) => ({ ...l, customerOverridden: l.customerOverridden ?? true }));
      setCostSheet(sheet);
    } else {
      setCostSheet(buildStaticCostSheet(pc, request.selectedAddons, request.participants, custCurrency, exchangeRates));
    }
  }, [selectedPriceCodeId, priceCodes, request, exchangeRates]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'quoteTemplates'), orderBy('createdAt', 'desc'), limit(30)),
      (snap) => setQuoteTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, []);

  // Everyone the customer brings — players, supporters and their own staff/teachers.
  // MST tour leads aren't part of it (they're costed as their own quote lines).
  const totalGroupCount = useMemo(() => {
    if (!request) return 0;
    const players = Number(request.participants) || 0;
    const supporters = (Number(request.supportersMale) || 0) + (Number(request.supportersFemale) || 0);
    const custStaff = Number(request.customerStaff) || 0;
    return players + supporters + custStaff;
  }, [request]);

  // Live totals derived from the editable cost sheet + current group inputs
  const calculatedTotals = useMemo(() => {
    if (!selectedPriceCodeId || costSheet.length === 0 || !exchangeRates) return null;
    const pc = priceCodes.find((p) => p.id === selectedPriceCodeId);
    if (!pc) return null;
    const sup = (Number(request?.supportersMale) || 0) + (Number(request?.supportersFemale) || 0);
    return recalculateTotals(
      costSheet, pc,
      request?.participants, request?.customerStaff,
      tourLeadCount, juniorLeadCount,
      sup, quoteDepartureDate || request?.departureDate, quoteReturnDate || request?.returnDate,
      largeCaseQuantity, exchangeRates, custCurrency,
      freePlacesOverride === '' ? null : freePlacesOverride,
      addonCategories,
      request?.leaderCostBasis || 'nights'
    );
  }, [costSheet, selectedPriceCodeId, priceCodes, tourLeadCount, juniorLeadCount,
      largeCaseQuantity, request, exchangeRates, custCurrency, freePlacesOverride,
      quoteDepartureDate, quoteReturnDate, addonCategories]);

  // Apply customer price slider on top of calculated totals
  const displayTotals = useMemo(() => {
    if (!calculatedTotals) return null;
    if (customerPriceOverride === null) return calculatedTotals;
    const { payingPlayers } = calculatedTotals;
    return {
      ...calculatedTotals,
      totalCostToCustomer: customerPriceOverride,
      costPerPersonToCustomer: payingPlayers > 0 ? customerPriceOverride / payingPlayers : 0,
    };
  }, [calculatedTotals, customerPriceOverride]);

  function updateCostSheetLine(lineId, field, rawVal) {
    const stringFields = new Set(['note', 'label', 'mstCurrency', 'pricingType', 'categoryName']);
    setCostSheet((prev) => prev.map((l) => {
      if (l.id !== lineId) return l;
      const val = stringFields.has(field) ? rawVal : (rawVal === '' ? 0 : parseFloat(rawVal) || 0);
      const next = { ...l, [field]: val };
      if (field === 'unitCostToCustomer') {
        // Staff typed their own charge — lock it and stop auto-following.
        next.customerOverridden = true;
      } else if ((field === 'unitCostToMST' || field === 'mstCurrency') && !next.customerOverridden && !next.isInternalOnly) {
        // Cust unit is still auto — re-derive it from the new MST cost/currency.
        next.unitCostToCustomer = convertCurrency(Number(next.unitCostToMST) || 0, next.mstCurrency || custCurrency, custCurrency, exchangeRates || {});
      }
      return next;
    }));
  }

  // Clear a manual Cust-unit override so the line auto-follows the MST cost again.
  function resetCustomerOverride(lineId) {
    setCostSheet((prev) => prev.map((l) => {
      if (l.id !== lineId) return l;
      return {
        ...l,
        customerOverridden: false,
        unitCostToCustomer: convertCurrency(Number(l.unitCostToMST) || 0, l.mstCurrency || custCurrency, custCurrency, exchangeRates || {}),
      };
    }));
  }

  // Keep auto (non-overridden) Cust units in sync when the customer currency or
  // exchange rates change. Deliberately excludes costSheet from deps — MST edits
  // are handled inline in updateCostSheetLine, so this won't loop.
  useEffect(() => {
    if (!exchangeRates) return;
    setCostSheet((prev) => {
      let changed = false;
      const next = prev.map((l) => {
        if (l.customerOverridden || l.isInternalOnly) return l;
        const v = convertCurrency(Number(l.unitCostToMST) || 0, l.mstCurrency || custCurrency, custCurrency, exchangeRates);
        if (v === l.unitCostToCustomer) return l;
        changed = true;
        return { ...l, unitCostToCustomer: v };
      });
      return changed ? next : prev;
    });
  }, [custCurrency, exchangeRates]); // eslint-disable-line react-hooks/exhaustive-deps

  function copyWebQuoteIntoCostSheet() {
    const est = request.estimateDetails;
    if (!est || !selectedPriceCodeId) return;
    const pc = priceCodes.find((p) => p.id === selectedPriceCodeId);
    const pcCurrency = pc?.currency || 'GBP';

    // Ground/Flights/Transport aren't tracked as "addons", so buildStaticCostSheet
    // never pulls them in — but the price code still has real MST costs for them.
    // Reuse those instead of leaving the customer price with no cost behind it.
    const findCost = (categoryName) => {
      const item = (pc?.items || []).find((i) => i.categoryName === categoryName && i.included);
      const cost = Number(item?.costToMST) || 0;
      return cost > 0 ? { costToMST: cost, mstCurrency: item.mstCurrency || pcCurrency } : null;
    };

    let nights = 1;
    if (request.departureDate && request.returnDate) {
      const dep = new Date(request.departureDate);
      const ret = new Date(request.returnDate);
      const diff = Math.round((ret - dep) / (1000 * 60 * 60 * 24));
      if (diff > 0) nights = diff;
    }

    const squads = Math.max(1, Number(request.numberOfSquads) || 1);
    const sessionsFor = (categoryName) => {
      if (categoryName === 'Padel') return Math.max(1, Number(request.padelSessions) || 1);
      if (categoryName === 'Yoga') return Math.max(1, Number(request.yogaSessions) || 1);
      return 1;
    };

    const now = Date.now();
    const newLines = [];
    if (Number(est.hotelGBP) > 0) {
      // Hotel & Food nights are already baked into est.hotelGBP (per_person
      // total for the whole stay), and it already carries MST's markup. Use
      // the raw pre-markup EUR figure the quote tool captured as the MST cost
      // basis instead of the price code's generic per-night rate, since the
      // web quote's actual hotel/room selection is what really applies here.
      const src = findCost('Hotel & Food');
      const mstCostEUR = Number(est.hotelCostEUR) > 0 ? Number(est.hotelCostEUR) : null;
      newLines.push({
        id: `webquote_ground_${now}`, label: 'Hotel & Food (from web quote)', categoryName: 'Web Quote',
        pricingType: 'per_person', mstCurrency: mstCostEUR != null ? 'EUR' : (src?.mstCurrency || pcCurrency),
        unitCostToMST: mstCostEUR != null ? mstCostEUR : (src ? src.costToMST * nights : 0),
        unitCostToCustomer: Number(est.hotelGBP),
        customerOverridden: true,
        note: mstCostEUR != null
          ? "MST cost pulled from the customer's actual hotel/room selection on the web quote."
          : (src
            ? `MST cost pulled from this price code's Hotel & Food rate (${nights} night${nights === 1 ? '' : 's'}) — check it still matches.`
            : "Copied from the customer's instant quote estimate — no Hotel & Food cost found on this price code, check MST cost before sending."),
      });
    }
    if (Number(est.flightPP) > 0) {
      // Flights are booked at cost with no MST margin, so MST cost = customer
      // price — not pulled from the price code (which prices flights with a
      // margin for non-web-quote tours). Priced per traveller, not per_person:
      // Tour/Junior Leads are already in Spain and never get a flight booked,
      // so they shouldn't be multiplied into the flight cost.
      newLines.push({
        id: `webquote_flight_${now + 1}`, label: 'Flights (from web quote)', categoryName: 'Web Quote',
        pricingType: 'per_traveller', mstCurrency: est.currency || pcCurrency,
        unitCostToMST: Number(est.flightPP), unitCostToCustomer: Number(est.flightPP),
        customerOverridden: true,
        note: "Flights are booked at cost — MST cost set equal to the customer price (no margin). Priced for travellers only; Tour/Junior Leads are already in Spain and don't need a flight.",
      });
    }
    if (Number(est.transportTotal) > 0) {
      const src = findCost('Transport');
      newLines.push({
        id: `webquote_transport_${now + 2}`, label: 'Transport (from web quote)', categoryName: 'Web Quote',
        pricingType: 'flat', mstCurrency: src?.mstCurrency || pcCurrency,
        unitCostToMST: src?.costToMST || 0, unitCostToCustomer: Number(est.transportTotal),
        customerOverridden: true,
        note: src
          ? "MST cost pulled from this price code's Transport rate — check it still matches."
          : "Copied from the customer's instant quote estimate — no Transport cost found on this price code, check MST cost before sending.",
      });
    }
    if (Number(est.stadiumPP) > 0) {
      const src = findCost('Stadium Tour');
      newLines.push({
        id: `webquote_stadium_${now + 3}`, label: 'Stadium Tour (from web quote)', categoryName: 'Web Quote',
        pricingType: 'per_person', mstCurrency: src?.mstCurrency || pcCurrency,
        unitCostToMST: src?.costToMST || 0, unitCostToCustomer: Number(est.stadiumPP),
        customerOverridden: true,
        note: src
          ? "MST cost pulled from this price code's Stadium Tour rate — check it still matches."
          : "Copied from the customer's instant quote estimate — no Stadium Tour cost found on this price code, check MST cost before sending.",
      });
    }
    if (Number(est.fixturesTotal) > 0) {
      const src = findCost('Fixtures');
      const qty = Number(request.fixtureQty) || 0;
      newLines.push({
        id: `webquote_fixtures_${now + 4}`, label: 'Fixtures (from web quote)', categoryName: 'Web Quote',
        pricingType: 'flat', mstCurrency: src?.mstCurrency || pcCurrency,
        unitCostToMST: src ? src.costToMST * qty * squads : 0, unitCostToCustomer: Number(est.fixturesTotal),
        customerOverridden: true,
        note: src
          ? `MST cost pulled from this price code's Fixtures rate (${qty} fixture${qty === 1 ? '' : 's'} × ${squads} squad${squads === 1 ? '' : 's'}) — check it still matches.`
          : "Copied from the customer's instant quote estimate — no Fixtures cost found on this price code, check MST cost before sending.",
      });
    }
    if (Number(est.trainingTotal) > 0) {
      const src = findCost('Training Sessions');
      const qty = Number(request.trainingQty) || 0;
      newLines.push({
        id: `webquote_training_${now + 5}`, label: 'Training Sessions (from web quote)', categoryName: 'Web Quote',
        pricingType: 'flat', mstCurrency: src?.mstCurrency || pcCurrency,
        unitCostToMST: src ? src.costToMST * qty * squads : 0, unitCostToCustomer: Number(est.trainingTotal),
        customerOverridden: true,
        note: src
          ? `MST cost pulled from this price code's Training Sessions rate (${qty} session${qty === 1 ? '' : 's'} × ${squads} squad${squads === 1 ? '' : 's'}) — check it still matches.`
          : "Copied from the customer's instant quote estimate — no Training Sessions cost found on this price code, check MST cost before sending.",
      });
    }
    // The optional extras (Padel/Yoga/etc.) the customer ticked on the web
    // quote land on the cost sheet automatically from request.selectedAddons,
    // but buildStaticCostSheet defaults their customer price to the raw MST
    // cost converted at today's FX rate (no margin) rather than this price
    // code's actual configured costToCustomer — the exact figure the web
    // quote charged. Correct any of those lines staff haven't manually
    // touched so the sheet matches what the customer was actually quoted.
    // Padel and Yoga are priced per session booked, not per traveller and
    // not per squad — fix those lines' pricing type too, not just the unit price.
    const fixedIds = [];
    const fixedCostSheet = costSheet.map((line) => {
      if (line.customerOverridden) return line;
      if (!(request.selectedAddons || []).some((a) => a.categoryName === line.categoryName)) return line;
      const item = (pc?.items || []).find((i) => i.categoryName === line.categoryName && i.included);
      if (!item) return line;
      const isSessionBased = SESSION_BASED_ADDON_CATEGORIES.includes(line.categoryName);
      const sessions = sessionsFor(line.categoryName);
      const correctCust = isSessionBased ? (Number(item.costToCustomer) || 0) * sessions : (Number(item.costToCustomer) || 0);
      const correctMST = isSessionBased ? (Number(item.costToMST) || 0) * sessions : Number(line.unitCostToMST) || 0;
      if (!correctCust) return line;
      const alreadyRight = (!isSessionBased || line.pricingType === 'flat')
        && Math.abs(correctCust - Number(line.unitCostToCustomer)) < 0.01
        && Math.abs(correctMST - Number(line.unitCostToMST)) < 0.01;
      if (alreadyRight) return line;
      fixedIds.push(line.id);
      return {
        ...line,
        pricingType: isSessionBased ? 'flat' : line.pricingType,
        unitCostToCustomer: correctCust,
        unitCostToMST: correctMST,
        note: isSessionBased
          ? `Customer price corrected to a flat cost per session (${sessions} session${sessions === 1 ? '' : 's'} × ${item.mstCurrency === 'GBP' ? '£' : '€'}${(Number(item.costToCustomer) || 0).toFixed(2)}) — this is booked per session, not priced per traveller or per squad.`
          : "Customer price corrected to match this price code's rate — this is what the web quote actually charged for it.",
      };
    });

    if (newLines.length === 0 && fixedIds.length === 0) return;
    // Re-clicking after switching to a different quote option previously just
    // appended a second set of "(from web quote)" lines alongside the stale
    // ones instead of replacing them — drop any lines from an earlier copy
    // so this stays a clean refresh, not an accumulating duplicate.
    const withoutStaleWebQuoteLines = fixedCostSheet.filter((l) => l.categoryName !== 'Web Quote');
    setCostSheet([...withoutStaleWebQuoteLines, ...newLines]);
    setExpandedNotes((prev) => {
      const next = { ...prev };
      [...newLines.map((l) => l.id), ...fixedIds].forEach((id) => { next[id] = true; });
      return next;
    });
    setWebQuoteCopied(true);
  }

  // Opts this one tour into calendar-day leader costing (nights + 1) instead
  // of the legacy nights-based figure. Existing tours default to 'nights' and
  // stay that way until a staff member explicitly does this — see
  // recalculateTotals's leaderCostBasis param.
  async function switchLeaderCostBasisToDays() {
    const tl = Number(tourLeadCount) || 0;
    const jl = Number(juniorLeadCount) || 0;
    const extraEUR = tl * 100 + jl * 50;
    const ok = window.confirm(
      "Switch this tour's Tour Lead / Junior Lead cost to calendar days present (arrival through departure) " +
      "instead of nights stayed?\n\n" +
      `This adds one extra day's cost per leader — about €${extraEUR.toFixed(2)} more on this tour's total ` +
      "before any customer price override.\n\nThis only changes this tour. No other quote is affected."
    );
    if (!ok) return;
    await updateDoc(doc(db, 'requests', id), { leaderCostBasis: 'days' });
  }

  function addCustomCostSheetLine() {
    const newId = `custom_${Date.now()}`;
    const newLine = {
      id: newId,
      label: '',
      categoryName: 'Custom',
      pricingType: 'flat',
      mstCurrency: priceCodes.find((p) => p.id === selectedPriceCodeId)?.currency || 'GBP',
      unitCostToMST: 0,
      unitCostToCustomer: 0,
      customerOverridden: false,
      note: '',
    };
    setCostSheet((prev) => [...prev, newLine]);
    // Auto-expand the note / label area so staff immediately see where to name it
    setExpandedNotes((prev) => ({ ...prev, [newId]: true }));
  }

  function removeCostSheetLine(lineId) {
    setCostSheet((prev) => prev.filter((l) => l.id !== lineId));
  }

  async function handleStageToggle(stageKey) {
    if (updating) return;
    setUpdating(true);
    const current = !!request.stageProgress?.[stageKey];
    const updates = { [`stageProgress.${stageKey}`]: !current };
    // Sync legacy status field for customer-facing views
    if (!current && STAGE_STATUS_SYNC[stageKey]) {
      updates.status = STAGE_STATUS_SYNC[stageKey];
    }
    try {
      await updateDoc(doc(db, 'requests', id), updates);
    } finally {
      setUpdating(false);
    }
  }

  async function handleAdvanceCategory() {
    if (updating) return;
    const cat = request.category || 'sales_admin';
    const next = nextCategory(cat);
    if (!next) return;
    if (!isCategoryComplete(cat, request.stageProgress, stagesFor(cat, request))) return;
    setUpdating(true);
    const updates = { category: next };
    if (CATEGORY_STATUS_SYNC[next]) updates.status = CATEGORY_STATUS_SYNC[next];
    try {
      await updateDoc(doc(db, 'requests', id), updates);
    } finally {
      setUpdating(false);
    }
  }

  async function handleGoBackToCategory(targetCategory) {
    if (updating) return;
    if (!window.confirm(`Go back to "${CATEGORY_LABELS[targetCategory]}"? Progress in later stages will be kept but the current stage will change.`)) return;
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'requests', id), { category: targetCategory });
    } finally {
      setUpdating(false);
    }
  }

  async function handleCancelTour() {
    if (updating) return;
    if (!window.confirm('Mark this tour as cancelled?')) return;
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'requests', id), { category: 'cancelled', status: 'cancelled' });
    } finally {
      setUpdating(false);
    }
  }

  async function handleArchiveTour() {
    if (updating) return;
    const archiving = !request.archived;
    if (archiving && !window.confirm('Archive this tour? It will be hidden from the default Tours list and only shown under "Archived".')) return;
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'requests', id), archiving
        ? { archived: true, archivedAt: serverTimestamp() }
        : { archived: false, archivedAt: null });
    } finally {
      setUpdating(false);
    }
  }

  async function handleToggleAutomation() {
    if (updating) return;
    const currentlyOn = request.automationEnabled !== false;
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'requests', id), { automationEnabled: !currentlyOn });
    } finally {
      setUpdating(false);
    }
  }

  async function handleManualSend() {
    if (!selectedSendTemplateId || sendingLetter) return;
    const recipient = (sendRecipientMode === 'contact' ? (request.customerEmail || '') : customRecipientEmail).trim();
    if (!/\S+@\S+\.\S+/.test(recipient)) return;
    setSendingLetter(true);
    try {
      const sepIdx = selectedSendTemplateId.indexOf(':');
      const kind = selectedSendTemplateId.slice(0, sepIdx);
      const itemId = selectedSendTemplateId.slice(sepIdx + 1);
      await addDoc(collection(db, 'letterSendRequests'), {
        requestId: id,
        ...(kind === 'template' ? { templateId: itemId } : { resendSentLetterId: itemId }),
        recipientEmail: recipient,
        manual: true,
        requestedAt: serverTimestamp(),
      });
      setSelectedSendTemplateId('');
      if (sendRecipientMode === 'custom') setCustomRecipientEmail('');
    } finally {
      setSendingLetter(false);
    }
  }

  async function handlePreviewLetter() {
    if (!selectedSendTemplateId || previewingLetter) return;
    setPreviewLetterError('');
    const sepIdx = selectedSendTemplateId.indexOf(':');
    const kind = selectedSendTemplateId.slice(0, sepIdx);
    const itemId = selectedSendTemplateId.slice(sepIdx + 1);
    if (kind === 'sent') {
      const sl = sentLetters.find((l) => l.id === itemId);
      if (sl?.downloadUrl) window.open(sl.downloadUrl, '_blank');
      else setPreviewLetterError('No PDF archived for this letter.');
      return;
    }
    setPreviewingLetter(true);
    try {
      const reqDoc = await addDoc(collection(db, 'letterPreviewRequests'), {
        requestId: id,
        templateId: itemId,
        requestedAt: serverTimestamp(),
      });
      await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          unsub();
          reject(new Error('Preview timed out. Please try again.'));
        }, 20000);
        const unsub = onSnapshot(reqDoc, (snap) => {
          const data = snap.data();
          if (!data?.status) return;
          clearTimeout(timeoutId);
          unsub();
          if (data.status === 'ready' && data.downloadUrl) {
            window.open(data.downloadUrl, '_blank');
            resolve();
          } else {
            reject(new Error(data.error || 'Could not generate a preview. Please try again.'));
          }
        }, (err) => { clearTimeout(timeoutId); unsub(); reject(err); });
      });
      deleteDoc(reqDoc).catch(() => {});
    } catch (err) {
      console.error('Letter preview failed:', err);
      setPreviewLetterError(err.message || 'Could not generate a preview. Please try again.');
    } finally {
      setPreviewingLetter(false);
    }
  }

  /**
   * Archive a letter staff produced outside the portal. It lands in the same
   * sentLetters archive as generated letters, but is never emailed — sharing it
   * with the customer only exposes it in their Documents list.
   */
  async function handleLetterUpload(e) {
    const file = e.target.files?.[0];
    if (!file || uploadingLetter) return;
    setLetterUploadError('');
    if (file.size > 20 * 1024 * 1024) {
      setLetterUploadError('That file is over 20MB — please upload a smaller file.');
      if (letterUploadInputRef.current) letterUploadInputRef.current.value = '';
      return;
    }
    setUploadingLetter(true);
    try {
      const safeName = file.name.replace(/[^\w.\-() ]+/g, '_');
      const path = `requests/${id}/sentLetters/${Date.now()}-${safeName}`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, file, { contentType: file.type || 'application/octet-stream' });
      const downloadUrl = await getDownloadURL(sRef);
      await addDoc(collection(db, 'requests', id, 'sentLetters'), {
        templateName: letterUploadName.trim() || file.name.replace(/\.[^.]+$/, ''),
        subject: letterUploadName.trim() || file.name.replace(/\.[^.]+$/, ''),
        trigger: 'upload',
        manual: true,
        uploaded: true,
        sharedWithCustomer: letterShareWithCustomer,
        fileName: file.name,
        mimeType: file.type || '',
        size: file.size,
        to: letterShareWithCustomer ? (request.customerEmail || '') : '',
        downloadUrl,
        storagePath: path,
        uploadedBy: currentUser?.email || '',
        sentAt: serverTimestamp(),
        status: 'uploaded',
      });
      setLetterUploadName('');
    } catch (err) {
      console.error('Letter upload failed:', err);
      setLetterUploadError('Upload failed. Please try again.');
    } finally {
      setUploadingLetter(false);
      if (letterUploadInputRef.current) letterUploadInputRef.current.value = '';
    }
  }

  async function deleteUploadedLetter(letter) {
    if (!window.confirm(`Remove "${letter.subject || letter.templateName || 'this letter'}" from the archive? This cannot be undone.`)) return;
    try {
      if (letter.storagePath) {
        try { await deleteObject(storageRef(storage, letter.storagePath)); } catch { /* already gone */ }
      }
      await deleteDoc(doc(db, 'requests', id, 'sentLetters', letter.id));
    } catch (err) {
      console.error('Deleting uploaded letter failed:', err);
      alert('Could not remove that letter. Please try again.');
    }
  }

  async function saveQuote() {
    if (!calculatedTotals || !selectedPriceCodeId) return;

    setSavingQuote(true);
    setQuoteError('');
    try {
      const pc = priceCodes.find((p) => p.id === selectedPriceCodeId);
      // The quote is billed to the customer in custCurrency; totals below are already
      // in this currency, so downstream (quote PDF, invoices) that key off request.currency
      // display in it automatically.
      const currency = custCurrency || 'GBP';
      const totals = displayTotals || calculatedTotals;
      // Always lock to current week's rates at save time
      const rates = await getCurrentWeekRates();

      // Build a backward-compatible breakdown array (used by PDF + InvoicePanel).
      // Must not contain undefined — Firestore rejects it.
      const allLines = calculatedTotals.allLines || [];
      const breakdown = allLines.map((l) => {
        const entry = {
          categoryName: l.label || l.categoryName || '',
          // isInternalOnly lines (e.g. Referral Fee) must never reach the customer
          // letter regardless of their own pricingType — previously only checked
          // for 'auto' lines, so the static Referral Fee line (pricingType
          // 'per_person') slipped through as a visible "included" item.
          pricingType: l.isInternalOnly ? 'referral_fee' : (l.pricingType === 'auto' ? 'mst_staff_cost' : (l.pricingType || 'flat')),
          mstCurrency: l.mstCurrNative || l.mstCurrency || currency,
          costToMST: l.mstNative ?? l.mstTotal ?? 0,
          costToMSTConverted: l.mstTotal ?? 0,
          costToCustomer: l.customerTotal ?? 0,
        };
        if (l.unitCostToMST != null)      entry.unitMST = l.unitCostToMST;
        if (l.unitCostToCustomer != null)  entry.unitCustomer = l.unitCostToCustomer;
        if (l.costEUR != null)             entry.costToMSTEUR = l.costEUR;
        if (l.note)                        entry.note = l.note;
        return entry;
      });

      // Strip undefined from costSheet lines before saving (Firestore rejects undefined)
      const cleanCostSheet = costSheet.map((l) =>
        Object.fromEntries(Object.entries(l).filter(([, v]) => v !== undefined))
      );

      const quoteDates = {
        departureDate: quoteDepartureDate || request.departureDate || '',
        returnDate: quoteReturnDate || request.returnDate || '',
      };

      const newQuoteDetails = {
        costSheet: cleanCostSheet,
        custCurrency: currency,
        tourLeadCount: Number(tourLeadCount) || 0,
        juniorLeadCount: Number(juniorLeadCount) || 0,
        totalCostToMST: totals.totalCostToMST,
        totalCostToCustomer: totals.totalCostToCustomer,
        costPerPersonToCustomer: totals.costPerPersonToCustomer,
        breakdown,
        totalHeadcount: calculatedTotals.totalHeadcount,
        nights: calculatedTotals.nights,
        freePlaces: calculatedTotals.freePlaces || 0,
        ...(freePlacesOverride === '' ? {} : { freePlacesOverride: Number(freePlacesOverride) || 0 }),
        payingPlayers: calculatedTotals.payingPlayers || Number(request?.participants) || 0,
        ...(customerPriceOverride !== null ? { customerPriceAdjustment: customerPriceOverride } : {}),
        totalCostToMSTGBP: toGBP(totals.totalCostToMST, currency, rates),
        totalCostToCustomerGBP: toGBP(totals.totalCostToCustomer, currency, rates),
        exchangeRateUsed: getRateForCurrency(currency, rates),
        rateWeek: rates.weekStart,
        ...quoteDates,
        flightPriceManual: quoteFlightPriceManual,
        flightTimeManual: quoteFlightTimeManual,
      };
      await updateDoc(doc(db, 'requests', id), {
        priceCodeId: selectedPriceCodeId,
        priceCodeName: pc?.name || '',
        currency,
        tourLeadCount: Number(tourLeadCount) || 0,
        juniorLeadCount: Number(juniorLeadCount) || 0,
        largeCaseQuantity: Number(largeCaseQuantity) || 0,
        addonAnnotations: annotations,
        quoteDetails: newQuoteDetails,
        quoteNeedsUpdate: false,
      });

      // Mirror into this lead's quotes subcollection — the source of truth for
      // the multi-quote list/send flow. Creates a new option the first time
      // this builder is saved, or updates the one currently being edited.
      const quoteDocFields = {
        priceCodeId: selectedPriceCodeId,
        priceCodeName: pc?.name || '',
        currency,
        addonAnnotations: annotations,
        largeCaseQuantity: Number(largeCaseQuantity) || 0,
        ...newQuoteDetails,
        updatedAt: serverTimestamp(),
      };
      let savedQuoteId = activeQuoteId;
      if (activeQuoteId) {
        await updateDoc(doc(db, 'requests', id, 'quotes', activeQuoteId), quoteDocFields);
      } else {
        const label = `${calculatedTotals.nights} night${calculatedTotals.nights === 1 ? '' : 's'}${quoteDates.departureDate ? ` · ${formatDate(quoteDates.departureDate)}` : ''}`;
        const newQuoteRef = await addDoc(collection(db, 'requests', id, 'quotes'), {
          ...quoteDocFields,
          label,
          status: 'draft',
          createdAt: serverTimestamp(),
        });
        savedQuoteId = newQuoteRef.id;
        setActiveQuoteId(newQuoteRef.id);
      }

      // Web-quote leads (from the public /quote tool) have no portal account
      // yet — the first firm quote is the trigger to create one and invite
      // the customer in, same as the "new customer" flow staff use manually.
      if (!request.customerId && request.customerEmail && request.source === 'instant_quote') {
        try {
          const { uid } = await createPortalUser({
            email: request.customerEmail,
            role: 'customer',
            name: request.contactName || request.groupName || request.customerEmail,
            createdByEmail: currentUser.email,
            brand: request.brand,
          });
          await updateDoc(doc(db, 'requests', id), { customerId: uid });
        } catch (acctErr) {
          console.error('Customer account creation failed:', acctErr);
          setQuoteError('Quote saved, but the customer account could not be created automatically — create one manually from Users.');
        }
      }
      return savedQuoteId;
    } catch (err) {
      console.error('Save quote failed:', err);
      setQuoteError('Failed to save quote. Please try again.');
      return null;
    } finally {
      setSavingQuote(false);
    }
  }

  // Starts a fresh, unsaved quote option — clears the builder back to blank
  // so staff can price a second (or third...) option for the same lead.
  function startNewQuote() {
    setActiveQuoteId(null);
    setSelectedPriceCodeId('');
    setCostSheet([]);
    costSheetPriceCodeRef.current = null;
    setCustomerPriceOverride(null);
    setFreePlacesOverride('');
    setLargeCaseQuantity('');
    setQuoteDepartureDate(request?.departureDate || '');
    setQuoteReturnDate(request?.returnDate || '');
    // Legacy request-level fields as a starting point — a brand-new quote
    // otherwise opens with no flight info at all.
    setQuoteFlightPriceManual(request?.flightPriceManual || '');
    setQuoteFlightTimeManual(request?.flightTimeManual || '');
    setQuoteError('');
    setQuoteLetterSent(false);
  }

  // Loads a previously saved quote option back into the builder for editing
  // or re-sending.
  function loadQuoteForEdit(q) {
    setActiveQuoteId(q.id);
    setSelectedPriceCodeId(q.priceCodeId || '');
    costSheetPriceCodeRef.current = q.priceCodeId || null;
    setCostSheet(q.costSheet || []);
    setCustCurrency(q.custCurrency || 'GBP');
    setTourLeadCount(String(q.tourLeadCount ?? 0));
    setJuniorLeadCount(String(q.juniorLeadCount ?? 0));
    setLargeCaseQuantity(q.largeCaseQuantity != null ? String(q.largeCaseQuantity) : '');
    setAnnotations(q.addonAnnotations || {});
    setCustomerPriceOverride(q.customerPriceAdjustment ?? null);
    setFreePlacesOverride(q.freePlacesOverride != null ? String(q.freePlacesOverride) : '');
    setQuoteDepartureDate(q.departureDate || request?.departureDate || '');
    setQuoteReturnDate(q.returnDate || request?.returnDate || '');
    // Fall back to the legacy request-level fields for quotes saved before
    // this became per-quote, so they don't just look blank.
    setQuoteFlightPriceManual(q.flightPriceManual ?? request?.flightPriceManual ?? '');
    setQuoteFlightTimeManual(q.flightTimeManual ?? request?.flightTimeManual ?? '');
    setQuoteError('');
    setQuoteLetterSent(false);
  }

  // Keeps this quote's Flights cost-sheet line in sync with the "Flight
  // price" field above the cost sheet, so staff have one place to set it
  // rather than two numbers that can drift apart. Same free-text parsing
  // CrmDatabase.js's request-level version uses ("£145pp" etc.).
  function applyQuoteFlightPrice(rawText) {
    setQuoteFlightPriceManual(rawText);
    const match = rawText.match(/[\d,]+(\.\d+)?/);
    if (!match) return;
    const pp = Number(match[0].replace(/,/g, ''));
    if (!(pp >= 0)) return;
    setCostSheet((prev) => {
      // A web-quote-copied line is stored as categoryName 'Web Quote' with a
      // label of "Flights (from web quote)" — match on either shape so this
      // updates that line in place instead of adding a second, duplicate one.
      const idx = prev.findIndex((l) => l.categoryName === 'Flights' || l.label?.startsWith('Flights'));
      if (idx === -1) {
        return [...prev, {
          id: 'line_flights', label: 'Flights', categoryName: 'Flights',
          pricingType: 'per_traveller', mstCurrency: custCurrency,
          unitCostToMST: pp, unitCostToCustomer: pp,
          customerOverridden: true,
          note: 'Flights are booked at cost — MST cost set equal to the customer price (no margin).',
        }];
      }
      const next = [...prev];
      next[idx] = { ...next[idx], unitCostToMST: pp, unitCostToCustomer: pp };
      return next;
    });
  }

  // One quote at a time is "the one being booked" — marking a new one
  // proceeded demotes any previous proceeded quote back to sent.
  async function markQuoteProceeded(quoteId) {
    const prevProceeded = quotes.find((q) => q.status === 'proceeded' && q.id !== quoteId);
    if (prevProceeded) {
      await updateDoc(doc(db, 'requests', id, 'quotes', prevProceeded.id), { status: 'sent' });
    }
    await updateDoc(doc(db, 'requests', id, 'quotes', quoteId), { status: 'proceeded' });
  }

  function handlePriceCodeChange(newId) {
    setSelectedPriceCodeId(newId);
    setCostSheet([]);
    costSheetPriceCodeRef.current = null;
    setCustomerPriceOverride(null);
  }

  // Save the CURRENT (possibly unsaved) cost sheet + related quote settings as
  // a reusable template any tour can load back in — mirrors ItineraryBuilder's
  // skeletalTemplates save/apply pattern, but for the price-code-driven quote.
  async function handleSaveQuoteAsTemplate() {
    if (!selectedPriceCodeId || costSheet.length === 0) return;
    const name = window.prompt('Name this quote template (e.g. "3-night Alicante football, 45 travellers"):');
    if (!name || !name.trim()) return;
    setSavingQuoteTemplate(true);
    try {
      const pc = priceCodes.find((p) => p.id === selectedPriceCodeId);
      const cleanCostSheet = costSheet.map((l) =>
        Object.fromEntries(Object.entries(l).filter(([, v]) => v !== undefined))
      );
      await addDoc(collection(db, 'quoteTemplates'), {
        name: name.trim(),
        priceCodeId: selectedPriceCodeId,
        priceCodeName: pc?.name || '',
        costSheet: cleanCostSheet,
        custCurrency,
        tourLeadCount: Number(tourLeadCount) || 0,
        juniorLeadCount: Number(juniorLeadCount) || 0,
        largeCaseQuantity: Number(largeCaseQuantity) || 0,
        ...(freePlacesOverride === '' ? {} : { freePlacesOverride: Number(freePlacesOverride) || 0 }),
        ...(customerPriceOverride !== null ? { customerPriceAdjustment: customerPriceOverride } : {}),
        createdByEmail: currentUser?.email || '',
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Save quote template failed:', err);
      window.alert('Could not save this as a template. Please try again.');
    } finally {
      setSavingQuoteTemplate(false);
    }
  }

  // Copies a saved template's cost sheet + settings onto THIS tour's quote.
  // Pre-sets the price-code-change ref guard before switching price code so
  // the effect that seeds costSheet from the price code doesn't immediately
  // overwrite the template's lines with a fresh buildStaticCostSheet() pass.
  function handleApplyQuoteTemplate() {
    const template = quoteTemplates.find((t) => t.id === selectedQuoteTemplateId);
    if (!template) return;
    if (!window.confirm(`Apply template "${template.name}"? This replaces the current cost sheet.`)) return;
    setApplyingQuoteTemplate(true);
    try {
      costSheetPriceCodeRef.current = template.priceCodeId;
      setSelectedPriceCodeId(template.priceCodeId);
      setCostSheet((template.costSheet || []).map((l) => ({ ...l, customerOverridden: true })));
      setCustCurrency(template.custCurrency || 'GBP');
      setTourLeadCount(String(template.tourLeadCount ?? 1));
      setJuniorLeadCount(String(template.juniorLeadCount ?? 0));
      setLargeCaseQuantity(template.largeCaseQuantity ? String(template.largeCaseQuantity) : '');
      setFreePlacesOverride(template.freePlacesOverride != null ? String(template.freePlacesOverride) : '');
      setCustomerPriceOverride(template.customerPriceAdjustment ?? null);
      setWebQuoteCopied(false);
      setSelectedQuoteTemplateId('');
    } finally {
      setApplyingQuoteTemplate(false);
    }
  }

  function handleDeleteQuoteTemplate(template) {
    if (!template) return;
    if (!window.confirm(`Delete template "${template.name}"? This can't be undone.`)) return;
    deleteDoc(doc(db, 'quoteTemplates', template.id)).catch((err) => {
      console.error('Delete quote template failed:', err);
      window.alert('Could not delete this template. Please try again.');
    });
  }

  async function saveFlightDetails() {
    setSavingFlight(true);
    try {
      await updateDoc(doc(db, 'requests', id), { flightDetails });
    } finally {
      setSavingFlight(false);
    }
  }

  // Re-sends just the confirmation letter. The letter is normally generated when
  // the quote is sent, before flights are booked — at which point it can only
  // state the meal policy in general terms. Once the flight times are saved this
  // sends a fresh copy naming the exact meals included on each day.
  async function resendConfirmationLetter() {
    setResendLetterError('');
    setResendLetterSent(false);
    if (!request.customerEmail) { setResendLetterError('No customer email on this tour.'); return; }
    if (!request.quoteDetails) { setResendLetterError('Save a quote first — the letter needs the tour costings.'); return; }

    setResendingLetter(true);
    try {
      // Build from the saved flight details rather than the unsaved form state,
      // so the letter can never promise meals off times that were not kept.
      await sendConfirmationLetterEmail(request, 'meals_updated');
      setResendLetterSent(true);
    } catch (err) {
      console.error('Confirmation letter resend failed:', err);
      setResendLetterError(err.message || 'Failed to resend the confirmation letter.');
    } finally {
      setResendingLetter(false);
    }
  }

  async function saveTourName() {
    setSavingTourName(true);
    try {
      await updateDoc(doc(db, 'requests', id), { tourName: tourName.trim(), internalRef: internalRef.trim() });
    } finally {
      setSavingTourName(false);
    }
  }

  async function handleCrestUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCrest(true);
    try {
      const path = `requests/${id}/crest/${file.name}`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, file);
      const url = await getDownloadURL(sRef);
      await updateDoc(doc(db, 'requests', id), { crestUrl: url, crestStoragePath: path });
    } catch (err) {
      console.error('Crest upload failed:', err);
      alert('Failed to upload crest. Please try again.');
    } finally {
      setUploadingCrest(false);
      if (overviewCrestInputRef.current) overviewCrestInputRef.current.value = '';
    }
  }

  function openContactsEditor() {
    const existing = request.keyContacts || [];
    setContactsDraft(existing.length ? existing.map((c) => ({ ...c })) : [{ name: '', phone: '', email: '', inviteToPortal: false }]);
    setPrimaryDraft({
      contactName: request.contactName || '',
      phone: request.phone || '',
      customerEmail: request.customerEmail || '',
    });
    setEditingContacts(true);
  }

  async function saveKeyContacts() {
    const primaryName = (primaryDraft.contactName || '').trim();
    const primaryEmail = (primaryDraft.customerEmail || '').trim();
    if (!primaryName) {
      alert('The primary contact needs a name.');
      return;
    }
    if (!primaryEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(primaryEmail)) {
      alert('The primary contact needs a valid email address — it is the address all tour emails go to.');
      return;
    }
    setSavingContacts(true);
    try {
      const contacts = contactsDraft.filter((c) => c.name.trim());
      // Check each contact email — if provided and not already in users collection, send invite if requested
      for (const c of contacts) {
        if (c.email && c.inviteToPortal) {
          const existing = await getDocs(query(collection(db, 'users'), where('email', '==', c.email)));
          if (existing.empty) {
            await addDoc(collection(db, 'users'), {
              email: c.email,
              name: c.name,
              roles: ['customer'],
              ...(request?.brand ? { brand: request.brand } : {}),
              sendInvite: true,
              createdAt: serverTimestamp(),
            });
          }
        }
      }
      await updateDoc(doc(db, 'requests', id), {
        keyContacts: contacts,
        contactName: primaryName,
        phone: (primaryDraft.phone || '').trim(),
        customerEmail: primaryEmail,
      });
      setEditingContacts(false);
    } catch (err) {
      console.error(err);
      alert('Failed to save contacts. Please try again.');
    } finally {
      setSavingContacts(false);
    }
  }

  async function handleCrestRemove() {
    try {
      if (request.crestStoragePath) {
        await deleteObject(storageRef(storage, request.crestStoragePath)).catch(() => {});
      }
      await updateDoc(doc(db, 'requests', id), { crestUrl: null, crestStoragePath: null });
    } catch (err) {
      console.error('Crest removal failed:', err);
    }
  }

  async function handleAddTeamNote() {
    const text = newTeamNote.trim();
    if (!text) return;
    setSavingTeamNote(true);
    try {
      await addDoc(collection(db, 'requests', id, 'teamInfoNotes'), {
        text,
        authorEmail: currentUser.email || '',
        authorName: currentUser.displayName || currentUser.email || '',
        authorUid: currentUser.uid,
        createdAt: serverTimestamp(),
      });
      setNewTeamNote('');
    } catch (err) {
      console.error('Failed to add team note:', err);
      alert('Failed to add note. Please try again.');
    } finally {
      setSavingTeamNote(false);
    }
  }

  async function handleDeleteTeamNote(noteId) {
    try {
      await deleteDoc(doc(db, 'requests', id, 'teamInfoNotes', noteId));
    } catch (err) {
      console.error('Failed to delete team note:', err);
      alert('Failed to delete note. Please try again.');
    }
  }

  async function saveAddons() {
    setSavingAddons(true);
    try {
      const before = JSON.stringify((request.selectedAddons || []).map((a) => ({ id: a.categoryId, q: a.quantity })));
      const after  = JSON.stringify(addonsDraft.map((a) => ({ id: a.categoryId, q: a.quantity })));
      const changed = before !== after;
      await updateDoc(doc(db, 'requests', id), {
        selectedAddons: addonsDraft,
        ...(changed ? { quoteNeedsUpdate: true } : {}),
      });
      setEditingAddons(false);
    } finally {
      setSavingAddons(false);
    }
  }

  function getAssignedLeaders() {
    const arr = request.tourLeaders || [];
    if (arr.length) return arr;
    if (request.tourLeaderId) return [{ id: request.tourLeaderId, email: request.tourLeaderEmail || '', name: request.tourLeaderName || '' }];
    return [];
  }

  async function saveTourLeader() {
    if (!selectedLeaderId) return;
    const leader = tourLeaders.find((l) => l.id === selectedLeaderId);
    if (!leader) return;
    const current = getAssignedLeaders();
    if (current.length >= 3) return;
    if (current.some((l) => l.id === selectedLeaderId)) return;
    const updated = [...current, { id: leader.id, email: leader.email || '', name: leader.name || leader.email || '' }];
    const primary = updated[0];
    setSavingLeader(true);
    try {
      await updateDoc(doc(db, 'requests', id), {
        tourLeaders:     updated,
        tourLeaderIds:   updated.map((l) => l.id),
        tourLeaderId:    primary.id,
        tourLeaderEmail: primary.email,
        tourLeaderName:  primary.name,
      });
      setSelectedLeaderId('');
    } finally {
      setSavingLeader(false);
    }
  }

  async function removeTourLeader(leaderId) {
    const current = getAssignedLeaders();
    const updated = current.filter((l) => l.id !== leaderId);
    const primary = updated[0] || null;
    setRemovingLeaderId(leaderId);
    try {
      await updateDoc(doc(db, 'requests', id), {
        tourLeaders:     updated,
        tourLeaderIds:   updated.map((l) => l.id),
        tourLeaderId:    primary?.id    || null,
        tourLeaderEmail: primary?.email || '',
        tourLeaderName:  primary?.name  || '',
      });
    } finally {
      setRemovingLeaderId(null);
    }
  }

  async function saveMarketing() {
    if (!selectedMarketingId) return;
    const m = marketingUsers.find((u) => u.id === selectedMarketingId);
    if (!m) return;
    setSavingMarketing(true);
    try {
      await updateDoc(doc(db, 'requests', id), {
        marketingId: m.id,
        marketingEmail: m.email || '',
        marketingName: m.name || m.email || '',
      });
      setSelectedMarketingId('');
    } finally {
      setSavingMarketing(false);
    }
  }

  async function removeMarketing() {
    setSavingMarketing(true);
    try {
      await updateDoc(doc(db, 'requests', id), {
        marketingId: null,
        marketingEmail: '',
        marketingName: '',
      });
    } finally {
      setSavingMarketing(false);
    }
  }

  async function saveTourDetails() {
    setSavingTourDetails(true);
    try {
      await updateDoc(doc(db, 'requests', id), {
        sport: tourDetailsDraft.sport.trim(),
        destination: tourDetailsDraft.destination.trim(),
        departureDate: tourDetailsDraft.departureDate,
        returnDate: tourDetailsDraft.returnDate,
      });
      setEditingTourDetails(false);
    } finally {
      setSavingTourDetails(false);
    }
  }

  async function saveGroupInfo() {
    setSavingGroupInfo(true);
    try {
      const updates = {
        groupType: groupInfoDraft.groupType,
        llsGroup: isLlsGroupType(groupInfoDraft.groupType) ? groupInfoDraft.llsGroup || null : null,
        participants: Number(groupInfoDraft.participants) || 0,
        customerStaff: Number(groupInfoDraft.customerStaff) || 0,
      };
      if (groupInfoDraft.participantsMale !== '') updates.participantsMale = Number(groupInfoDraft.participantsMale) || 0;
      if (groupInfoDraft.participantsFemale !== '') updates.participantsFemale = Number(groupInfoDraft.participantsFemale) || 0;
      if (groupInfoDraft.supportersMale !== '') updates.supportersMale = Number(groupInfoDraft.supportersMale) || 0;
      if (groupInfoDraft.supportersFemale !== '') updates.supportersFemale = Number(groupInfoDraft.supportersFemale) || 0;
      if (groupInfoDraft.customerStaffMale !== '') updates.customerStaffMale = Number(groupInfoDraft.customerStaffMale) || 0;
      if (groupInfoDraft.customerStaffFemale !== '') updates.customerStaffFemale = Number(groupInfoDraft.customerStaffFemale) || 0;
      if (groupInfoDraft.numberOfSquads !== '') updates.numberOfSquads = Number(groupInfoDraft.numberOfSquads) || 0;
      await updateDoc(doc(db, 'requests', id), updates);
      // tourFormTokens are locked to server-only writes (firestore.rules), so
      // keeping any already-issued participant/team-info link's snapshotted
      // groupType in sync happens in onRequestUpdated (functions/index.js),
      // not here.
      setEditingGroupInfo(false);
    } finally {
      setSavingGroupInfo(false);
    }
  }

  // Uploaded crest images (phone photos, full-res PNGs) can be several MB —
  // embedded into the PDF at full resolution that easily pushes the emailed
  // quoteLetterRequests doc's pdfBase64/confirmationLetterPdfBase64 fields
  // past Firestore's 1MB document limit (both PDFs' base64 live on the same
  // doc, and each embeds its own copy of the crest). The crest only ever
  // prints at ~20mm on the page, so downscale it and re-encode as JPEG —
  // PNG compresses badge/photo-style crests far worse at this size.
  function handleCrestFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        setCrestDataUrl(await resizeCrestToJpegDataUrl(ev.target.result));
      } catch (_) {
        setCrestDataUrl(ev.target.result);
      }
    };
    reader.readAsDataURL(file);
  }

  // Sends the quote to Michael for approval rather than straight to the
  // customer — he gets an emailed Approve/Revise decision with this PDF
  // attached, and only his Approve triggers the actual customer send (via
  // the existing quoteLetterRequests pipeline, untouched). The confirmation
  // letter is no longer bundled here at all — it now fires independently
  // once the deposit is marked paid (see sendConfirmationLetterEmail).
  //
  // Note: this function is also passed into PadelanteQuote as onSendQuoteLetter,
  // where activeQuoteId is never set (Padelante saves its quote directly onto
  // the request doc, with no quoteId/status model) — that flow is already a
  // no-op today via the guard below, and is intentionally left that way; the
  // approval gate doesn't cover Padelante yet.
  async function handleSendForApproval() {
    if (!activeQuoteId || !request.quoteDetails) { setQuoteLetterError('Save this quote first before sending the letter.'); return; }
    if (!request.customerEmail) { setQuoteLetterError('No customer email on this tour.'); return; }
    setSendingQuoteLetter(true);
    setQuoteLetterError('');
    setQuoteLetterSent(false);
    try {
      const brandLogoDataUrl = await loadBrandLogoDataUrl(request.brand);
      // The quote we're sending may have its own date range, distinct from
      // the tour's — override it on the clone the PDF is built from.
      const requestForPdf = {
        ...request,
        departureDate: quoteDepartureDate || request.departureDate,
        returnDate: quoteReturnDate || request.returnDate,
      };
      // Prefer a crest freshly uploaded in this modal; otherwise fall back to
      // whatever's already saved on the tour's Overview tab, so a tour that
      // already has a crest doesn't need it re-uploaded here every time.
      let effectiveCrestDataUrl = crestDataUrl;
      if (!effectiveCrestDataUrl && request.crestUrl) {
        try {
          const resp = await fetch(request.crestUrl);
          const blob = await resp.blob();
          const rawCrestDataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          effectiveCrestDataUrl = await resizeCrestToJpegDataUrl(rawCrestDataUrl);
        } catch (_) { /* crest fetch failed — PDF will render without it */ }
      }

      // Your Tour and MST UK → Spain tours get the "1st Letter to Parents"
      // design — the Spanish-market branch and Padelante keep the generic
      // cost-breakdown quote letter (see usesParentLetters).
      const pdfBase64 = usesParentLetters(request)
        ? generateYourTourQuoteLetterPdf(requestForPdf, effectiveCrestDataUrl, brandLogoDataUrl)
        : generateQuoteLetterPdf(requestForPdf, effectiveCrestDataUrl, brandLogoDataUrl);

      // A crest uploaded here is only ever held in local state — persist it
      // to the tour so the deposit-paid confirmation letter and a plain
      // "Resend to customer" (neither of which has an upload step) pick up
      // the same crest automatically. (Only when it's a genuinely new upload —
      // not when we just re-fetched the tour's own already-saved crest above.)
      if (crestDataUrl) {
        try {
          const blob = await (await fetch(crestDataUrl)).blob();
          const path = `requests/${id}/crest/crest.jpg`;
          const sRef = storageRef(storage, path);
          await uploadBytes(sRef, blob);
          const url = await getDownloadURL(sRef);
          await updateDoc(doc(db, 'requests', id), { crestUrl: url, crestStoragePath: path });
        } catch (_) { /* crest persistence failed — this send still has it embedded */ }
      }

      await addDoc(collection(db, 'quoteApprovalRequests'), {
        requestId: id,
        quoteId: activeQuoteId,
        pdfBase64,
        requestedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'requests', id, 'quotes', activeQuoteId), {
        status: 'pending_approval', pendingApprovalAt: serverTimestamp(), revisionComment: null,
      });
      setQuoteLetterSent(true);
      setShowCrestModal(false);
    } catch (err) {
      console.error(err);
      setQuoteLetterError(err.message || 'Failed to send for approval.');
    } finally {
      setSendingQuoteLetter(false);
    }
  }

  // Plain resend of an already-approved quote straight to the customer — no
  // approval detour, since Michael already signed off on this content once.
  // No upload modal here either, so the crest comes from whatever's already
  // saved on the tour (same as the confirmation-letter and resend-letter flows).
  async function handleResendToCustomer() {
    const activeQuote = quotes.find((q) => q.id === activeQuoteId);
    if (!activeQuoteId || activeQuote?.status !== 'sent') return;
    if (!request.customerEmail) { setQuoteLetterError('No customer email on this tour.'); return; }
    setSendingQuoteLetter(true);
    setQuoteLetterError('');
    setQuoteLetterSent(false);
    try {
      const brandLogoDataUrl = await loadBrandLogoDataUrl(request.brand);

      const savedCrestDataUrl = await loadSavedCrestDataUrl(request.crestUrl);

      const requestForPdf = {
        ...request,
        departureDate: quoteDepartureDate || request.departureDate,
        returnDate: quoteReturnDate || request.returnDate,
      };
      const pdfBase64 = usesParentLetters(request)
        ? generateYourTourQuoteLetterPdf(requestForPdf, savedCrestDataUrl, brandLogoDataUrl)
        : generateQuoteLetterPdf(requestForPdf, savedCrestDataUrl, brandLogoDataUrl);

      await addDoc(collection(db, 'quoteLetterRequests'), {
        requestId: id,
        quoteId: activeQuoteId,
        pdfBase64,
        requestedAt: serverTimestamp(),
      });
      setQuoteLetterSent(true);
    } catch (err) {
      console.error(err);
      setQuoteLetterError(err.message || 'Failed to resend quote letter.');
    } finally {
      setSendingQuoteLetter(false);
    }
  }

  // Opens the quote or confirmation letter PDF in a new tab without sending
  // anything — built from exactly the same inputs as the real sends (saved
  // quote, saved crest, this quote's dates), so staff can check a letter
  // while it's awaiting Michael's approval.
  async function handlePreviewQuotePdf(kind) {
    if (!request.quoteDetails) { setQuoteLetterError('Save a quote first — the letter needs the tour costings.'); return; }
    // Open the tab synchronously so the browser doesn't treat it as a popup.
    const win = window.open('', '_blank');
    setPreviewingQuotePdf(kind);
    setQuoteLetterError('');
    try {
      const requestForPdf = {
        ...request,
        departureDate: quoteDepartureDate || request.departureDate,
        returnDate: quoteReturnDate || request.returnDate,
      };
      let pdfBase64;
      if (kind === 'confirmation') {
        pdfBase64 = await buildConfirmationLetterPdf(requestForPdf);
      } else {
        const brandLogoDataUrl = await loadBrandLogoDataUrl(request.brand);
        const savedCrestDataUrl = await loadSavedCrestDataUrl(request.crestUrl);
        pdfBase64 = usesParentLetters(request)
          ? generateYourTourQuoteLetterPdf(requestForPdf, savedCrestDataUrl, brandLogoDataUrl)
          : generateQuoteLetterPdf(requestForPdf, savedCrestDataUrl, brandLogoDataUrl);
      }
      const bytes = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (win) win.location.href = url; else window.open(url, '_blank');
    } catch (err) {
      console.error('Letter preview failed:', err);
      if (win) win.close();
      setQuoteLetterError(err.message || 'Could not generate the preview.');
    } finally {
      setPreviewingQuotePdf(null);
    }
  }

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-[13px] text-[#9CA3AF]">Loading…</p></div>;
  if (!request) return <div className="flex items-center justify-center h-64"><p className="text-[13px] text-[#9CA3AF]">Request not found.</p></div>;

  const selectedPC = priceCodes.find((p) => p.id === selectedPriceCodeId);
  const activeQuote = quotes.find((q) => q.id === activeQuoteId);
  // Saved-quote figures are stored in the customer billing currency (request.currency).
  const sym = CURRENCY_SYMBOLS[request?.currency] || (selectedPC ? CURRENCY_SYMBOLS[selectedPC.currency] : '£') || '£';
  const currentCategory = request.category || 'sales_admin';
  const stageProgress = request.stageProgress || {};
  const annotatableAddons = (request.selectedAddons || []).filter((a) => ANNOTATION_FIELDS[a.categoryName]);
  const currentCatIdx = TOUR_CATEGORIES.indexOf(currentCategory);
  const allCurrentComplete = isCategoryComplete(currentCategory, stageProgress, stagesFor(currentCategory, request));
  const nextCat = nextCategory(currentCategory);
  const isCancelled = currentCategory === 'cancelled';
  const automationOn = request.automationEnabled !== false;
  // Whether the saved flight times are enough for the confirmation letter to
  // name the exact meals — the only reason to resend it.
  const mealPlanIsExact = buildMealPlan(request).known;

  return (
    <div className="space-y-5">

      {/* ─── Page header ──────────────────────────────────────────────────────── */}
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-[12.5px] mb-4" aria-label="Breadcrumb">
        <button onClick={() => navigate(base)} className="text-[#9CA3AF] hover:text-[#6B7280] transition">{tr('tour.breadcrumbDashboard')}</button>
        <svg className="w-3 h-3 text-[#D1D5DB] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        {isCrmOrigin ? (
          <button onClick={() => navigate(`${base}/team-database`)} className="text-[#9CA3AF] hover:text-[#6B7280] transition">Sales</button>
        ) : (
          <button onClick={() => navigate(`${base}/requests`)} className="text-[#9CA3AF] hover:text-[#6B7280] transition">{tr('tour.breadcrumbTours')}</button>
        )}
        <svg className="w-3 h-3 text-[#D1D5DB] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        <span className="text-[#374151] font-bold truncate max-w-[220px]">
          {request.internalRef || request.tourName || `${request.sport} — ${request.destination}`}
        </span>
      </nav>

      {/* Header row */}
      <div className="flex items-start gap-[15px] mb-5">
        {/* Sport tile */}
        {(() => {
          const cat = request.category || 'sales_admin';
          const color = CATEGORY_COLORS[cat] || '#1A4A8A';
          const fill = CATEGORY_FILLS[cat] || '#EBF2FC';
          return (
            <span className="w-[52px] h-[52px] rounded-[12px] flex items-center justify-center flex-shrink-0"
              style={{ background: fill, color }}>
              <SportIcon sport={request.sport} className="w-7 h-7" />
            </span>
          );
        })()}

        {/* Main column */}
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          {/* Title row */}
          <div className="flex items-center gap-[10px] flex-wrap">
            <h2 className="text-[21px] font-extrabold text-navy tracking-[-0.4px] leading-[1.2]">
              {request.internalRef || request.tourName || `${request.sport} — ${request.destination}`}
            </h2>
            {request.brand === 'your_tour' && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-[5px] bg-pb text-white flex-shrink-0">Your Tour</span>
            )}
            {request.brand === 'padelante' && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-[5px] bg-[#8DC63F] text-[#1B4079] flex-shrink-0">Padelante</span>
            )}
            {(() => {
              const cat = request.category || 'sales_admin';
              return (
                <span className={`inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[20px] text-[11px] font-bold whitespace-nowrap flex-shrink-0 ${CATEGORY_STYLES[cat] || 'bg-[#F3F4F6] text-[#374151]'}`}>
                  <span className={`w-[5px] h-[5px] rounded-full ${CATEGORY_DOT[cat] || 'bg-[#9CA3AF]'}`} />
                  {CATEGORY_LABELS[cat] || cat}
                </span>
              );
            })()}
          </div>
          {/* Meta line */}
          <p className="text-[13px] text-[#6B7280]">
            {request.sport}{request.destination ? ` — ${request.destination}` : ''}{totalGroupCount ? ` · ${totalGroupCount} pax` : ''}
            {request.departureDate && ` · ${formatDate(request.departureDate)}–${formatDate(request.returnDate)}`}
            {request.departureDate && (() => {
              const today = new Date(); today.setHours(0,0,0,0);
              const dep = new Date(request.departureDate);
              const dl = Math.round((dep - today) / 864e5);
              return dl > 0 ? ` · departs in ${dl} days` : dl === 0 ? ' · departs today' : '';
            })()}
          </p>
          {/* Sub line */}
          <p className="text-[12px] text-[#9CA3AF]">
            Submitted {request.createdAt?.toDate ? formatDate(request.createdAt.toDate().toISOString().slice(0,10)) : ''}
            {request.customerEmail ? ` by ${request.customerEmail}` : ''}
            {request.createdByStaff ? ` · raised by ${request.createdByStaff}` : ''}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => handleTabChange('messages')}
            className="flex items-center gap-1.5 border border-[#E5E7EB] rounded-[9px] px-3 py-[7px] text-[12.5px] font-bold text-pb hover:bg-blue-light hover:border-pb transition"
          >
            Message
          </button>
          <button
            onClick={() => generateQuoteLetterPdf && setShowCrestModal(true)}
            className="flex items-center gap-1.5 border border-[#E5E7EB] rounded-[9px] px-3 py-[7px] text-[12.5px] font-bold text-pb hover:bg-blue-light hover:border-pb transition"
          >
            Quote PDF
          </button>
        </div>
      </div>

      {/* Amendment banner */}
      {request.customerAmendmentRequest && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-[12px] border mb-5"
          style={{ background: '#FEF6E4', borderColor: 'rgba(232,160,32,.3)' }}>
          <span className="w-7 h-7 rounded-[7px] flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(232,160,32,.18)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v3.75m-9.3 3.4c-.87 1.5.22 3.37 1.95 3.37h14.7c1.73 0 2.82-1.87 1.95-3.37L13.95 3.38c-.87-1.5-3.03-1.5-3.9 0z M12 15.75h.01" />
            </svg>
          </span>
          <div>
            <p className="text-[13px] font-bold text-[#92400E]">Amendment request from customer</p>
            <p className="text-[12.5px] text-[#92400E]/80 mt-0.5">{request.customerAmendmentRequest}</p>
          </div>
        </div>
      )}

      {/* ─── Two-column layout ────────────────────────────────────────────────── */}
      <div className="flex flex-col 1080:flex-row gap-5 items-start">

        {/* ── Left: tabbed main content ────────────────────────────────────── */}
        <div className="flex-1 min-w-0">

          {/* Tab bar — pill style */}
          <div className="bg-white border border-[#E5E7EB] rounded-[11px] p-1 mb-5 overflow-x-auto scrollbar-hide">
            <div className="flex min-w-max gap-[2px]">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`px-[13px] py-[7px] rounded-[7px] text-[12.5px] font-semibold transition whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'bg-navy text-white'
                      : 'text-[#6B7280] hover:bg-surface hover:text-[#374151]'
                  }`}
                >
                  {tr(tab.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {/* ── Overview tab ────────────────────────────────────────────── */}
          {activeTab === 'overview' && (
            <div className="space-y-5">

              {/* CRM pipeline controls — only while this tour is still being
                  worked through the British Clubs & Schools CRM */}
              {isCrmOrigin && (
                <CrmPipelinePanel request={request} requestId={id} canManage />
              )}

              {/* Tour Names */}
              <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
                <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-4">Tour Names</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Internal Reference</label>
                    <input
                      type="text"
                      value={internalRef}
                      onChange={(e) => setInternalRef(e.target.value)}
                      placeholder="e.g. MST-2025-001 or School Name FC"
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                    <p className="text-[11px] text-[#9CA3AF] mt-1">Shown to staff only — for matching tours with internal records. Appears in the tours list and page title.</p>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Customer Tour Name</label>
                    <input
                      type="text"
                      value={tourName}
                      onChange={(e) => setTourName(e.target.value)}
                      placeholder={`${request.sport} — ${request.destination}`}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                    <p className="text-[11px] text-[#9CA3AF] mt-1">Shown to customers in emails, letters, and the customer portal. Defaults to sport — destination if left blank.</p>
                  </div>
                  <button
                    onClick={saveTourName}
                    disabled={savingTourName}
                    className="px-4 py-2 text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
                  >
                    {savingTourName ? 'Saving…' : 'Save names'}
                  </button>
                </div>
              </div>

              {/* Club Crest — not used by the Padelante padel portal */}
              {!isPadelante && (
              <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
                <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-4">Club / School Crest</h3>
                <input ref={overviewCrestInputRef} type="file" accept="image/*" className="hidden" onChange={handleCrestUpload} />
                {request.crestUrl ? (
                  <div className="flex items-center gap-4">
                    <img src={request.crestUrl} alt="Club crest" className="h-16 w-16 object-contain rounded-[8px] border border-[#E5E7EB]" />
                    <div className="space-y-1.5">
                      <button
                        onClick={() => overviewCrestInputRef.current?.click()}
                        disabled={uploadingCrest}
                        className="block text-[12px] font-semibold text-pb hover:text-navy transition disabled:opacity-60"
                      >
                        {uploadingCrest ? 'Uploading…' : 'Replace crest'}
                      </button>
                      <button onClick={handleCrestRemove} className="block text-[12px] text-[#9CA3AF] hover:text-dr transition">
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <button
                      onClick={() => overviewCrestInputRef.current?.click()}
                      disabled={uploadingCrest}
                      className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-[#D1D5DB] rounded-[8px] text-[13px] font-semibold text-[#6B7280] hover:border-pb/40 hover:text-pb transition disabled:opacity-60"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-8-8l-4 4m0 0l4 4m-4-4h12" />
                      </svg>
                      {uploadingCrest ? 'Uploading…' : 'Upload club / school crest'}
                    </button>
                    <p className="text-[11px] text-[#9CA3AF] mt-2">The crest will appear on the skeletal itinerary PDF and customer letters.</p>
                  </div>
                )}
              </div>
              )}

              {/* Tour Details */}
              <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">{tr('tour.secTourDetails')}</h3>
                  {!editingTourDetails && (
                    <button
                      onClick={() => { setTourDetailsDraft({ sport: request.sport || '', destination: request.destination || '', departureDate: request.departureDate || '', returnDate: request.returnDate || '' }); setEditingTourDetails(true); }}
                      className="text-[12px] font-semibold text-pb hover:text-navy transition"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {editingTourDetails ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Sport</label>
                        {/* Padelante is padel-only — sport is fixed */}
                        {isPadelante ? (
                          <div className="w-full border border-[#E5E7EB] bg-[#F9FAFB] rounded-[8px] px-3 py-2 text-[13px] text-[#6B7280]">{tourDetailsDraft.sport || 'Padel'}</div>
                        ) : (
                          <input type="text" value={tourDetailsDraft.sport} onChange={(e) => setTourDetailsDraft((p) => ({ ...p, sport: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                        )}
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Destination</label>
                        {/* Padelante destination is fixed to Alicante */}
                        {isPadelante ? (
                          <div className="w-full border border-[#E5E7EB] bg-[#F9FAFB] rounded-[8px] px-3 py-2 text-[13px] text-[#6B7280]">{tourDetailsDraft.destination || 'Alicante'}</div>
                        ) : (
                          <input type="text" value={tourDetailsDraft.destination} onChange={(e) => setTourDetailsDraft((p) => ({ ...p, destination: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                        )}
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Departure</label>
                        <input type="date" value={tourDetailsDraft.departureDate} onChange={(e) => setTourDetailsDraft((p) => ({ ...p, departureDate: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Return</label>
                        <input type="date" value={tourDetailsDraft.returnDate} onChange={(e) => setTourDetailsDraft((p) => ({ ...p, returnDate: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button onClick={saveTourDetails} disabled={savingTourDetails} className="px-4 py-2 text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
                        {savingTourDetails ? 'Saving…' : 'Save changes'}
                      </button>
                      <button onClick={() => setEditingTourDetails(false)} className="px-4 py-2 text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-[8px] transition">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-5">
                    <Field label="Sport" value={request.sport} />
                    <Field label="Destination" value={request.destination} />
                    <Field label="Departure" value={formatDate(request.departureDate)} />
                    <Field label="Return" value={formatDate(request.returnDate)} />
                  </div>
                )}
              </div>

              {/* Flight Details */}
              <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
                <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-5">Flight Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Airline</label>
                    <input
                      type="text"
                      value={flightDetails.airline}
                      onChange={(e) => setFlightDetails((p) => ({ ...p, airline: e.target.value }))}
                      placeholder="e.g. Jet2, EasyJet"
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Outbound Flight No.</label>
                    <input
                      type="text"
                      value={flightDetails.outboundFlight}
                      onChange={(e) => setFlightDetails((p) => ({ ...p, outboundFlight: e.target.value }))}
                      placeholder="e.g. LS1234"
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Outbound Departure Time</label>
                    <input
                      type="text"
                      value={flightDetails.outboundTime}
                      onChange={(e) => setFlightDetails((p) => ({ ...p, outboundTime: e.target.value }))}
                      placeholder="e.g. 09:30"
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Arrival Time (at destination)</label>
                    <input
                      type="text"
                      value={flightDetails.arrivalTime}
                      onChange={(e) => setFlightDetails((p) => ({ ...p, arrivalTime: e.target.value }))}
                      placeholder="e.g. 12:45"
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Return Flight No.</label>
                    <input
                      type="text"
                      value={flightDetails.returnFlight}
                      onChange={(e) => setFlightDetails((p) => ({ ...p, returnFlight: e.target.value }))}
                      placeholder="e.g. LS1235"
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Return Departure Time</label>
                    <input
                      type="text"
                      value={flightDetails.returnTime}
                      onChange={(e) => setFlightDetails((p) => ({ ...p, returnTime: e.target.value }))}
                      placeholder="e.g. 17:45"
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Return Arrival Time (home)</label>
                    <input
                      type="text"
                      value={flightDetails.returnArrivalTime}
                      onChange={(e) => setFlightDetails((p) => ({ ...p, returnArrivalTime: e.target.value }))}
                      placeholder="e.g. 19:30"
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                  </div>
                </div>
                <button
                  onClick={saveFlightDetails}
                  disabled={savingFlight}
                  className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
                >
                  {savingFlight ? 'Saving…' : 'Save flight details'}
                </button>

                {request.brand === 'your_tour' && (
                  <div className="mt-5 pt-5 border-t border-[#E5E7EB]">
                    <h4 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-2">Confirmation Letter</h4>
                    <p className="text-[12.5px] text-[#6B7280] leading-relaxed mb-3">
                      The confirmation letter goes out with the quote, before flights are booked, so it can only
                      state the meal policy in general terms. Resend it once flight times are added and it will
                      list the exact meals included on each day.
                    </p>
                    <button
                      onClick={resendConfirmationLetter}
                      disabled={resendingLetter || !mealPlanIsExact}
                      className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {resendingLetter ? 'Sending…' : 'Resend confirmation letter'}
                    </button>
                    {!mealPlanIsExact && (
                      <p className="text-[12px] text-[#9CA3AF] mt-2">
                        Add and save the arrival time and return departure time above to enable this.
                      </p>
                    )}
                    {resendLetterSent && (
                      <p className="text-[12.5px] text-green-600 mt-2">
                        Confirmation letter sent to {request.customerEmail}.
                      </p>
                    )}
                    {resendLetterError && (
                      <p className="text-[12.5px] text-red-600 mt-2">{resendLetterError}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Group Info */}
              <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">{tr('tour.secGroupInfo')}</h3>
                  {!editingGroupInfo && (
                    <button
                      onClick={() => { setGroupInfoDraft({ groupType: request.groupType || '', llsGroup: request.llsGroup || '', participants: request.participants != null ? String(request.participants) : '', participantsMale: request.participantsMale != null ? String(request.participantsMale) : '', participantsFemale: request.participantsFemale != null ? String(request.participantsFemale) : '', supportersMale: request.supportersMale != null ? String(request.supportersMale) : '', supportersFemale: request.supportersFemale != null ? String(request.supportersFemale) : '', numberOfSquads: request.numberOfSquads != null ? String(request.numberOfSquads) : '', customerStaff: request.customerStaff != null ? String(request.customerStaff) : '', customerStaffMale: request.customerStaffMale != null ? String(request.customerStaffMale) : '', customerStaffFemale: request.customerStaffFemale != null ? String(request.customerStaffFemale) : '' }); setEditingGroupInfo(true); }}
                      className="text-[12px] font-semibold text-pb hover:text-navy transition"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {editingGroupInfo ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">{tr('tour.secGroupType')}</label>
                        <input type="text" value={groupInfoDraft.groupType} onChange={(e) => setGroupInfoDraft((p) => ({ ...p, groupType: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                      {isLlsGroupType(groupInfoDraft.groupType) && (
                        <div>
                          <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">LLS Group</label>
                          <select value={groupInfoDraft.llsGroup} onChange={(e) => setGroupInfoDraft((p) => ({ ...p, llsGroup: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
                            <option value="">Not set</option>
                            {LLS_GROUPS.map((g) => <option key={g} value={g}>Group {g}</option>)}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Teams / Squads</label>
                        <input type="number" min="0" value={groupInfoDraft.numberOfSquads} onChange={(e) => setGroupInfoDraft((p) => ({ ...p, numberOfSquads: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Players / Students</label>
                        <input type="number" min="0" value={groupInfoDraft.participants} onChange={(e) => setGroupInfoDraft((p) => ({ ...p, participants: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Staff / Teachers</label>
                        <input type="number" min="0" value={groupInfoDraft.customerStaff} onChange={(e) => setGroupInfoDraft((p) => ({ ...p, customerStaff: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Male players</label>
                        <input type="number" min="0" value={groupInfoDraft.participantsMale} onChange={(e) => setGroupInfoDraft((p) => ({ ...p, participantsMale: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Female players</label>
                        <input type="number" min="0" value={groupInfoDraft.participantsFemale} onChange={(e) => setGroupInfoDraft((p) => ({ ...p, participantsFemale: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Male supporters</label>
                        <input type="number" min="0" value={groupInfoDraft.supportersMale} onChange={(e) => setGroupInfoDraft((p) => ({ ...p, supportersMale: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Female supporters</label>
                        <input type="number" min="0" value={groupInfoDraft.supportersFemale} onChange={(e) => setGroupInfoDraft((p) => ({ ...p, supportersFemale: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Male staff / teachers</label>
                        <input type="number" min="0" value={groupInfoDraft.customerStaffMale} onChange={(e) => setGroupInfoDraft((p) => ({ ...p, customerStaffMale: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Female staff / teachers</label>
                        <input type="number" min="0" value={groupInfoDraft.customerStaffFemale} onChange={(e) => setGroupInfoDraft((p) => ({ ...p, customerStaffFemale: e.target.value }))} className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      </div>
                    </div>
                    <div className="bg-surface border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[12.5px] text-[#374151]">
                      Total travelling group:{' '}
                      <strong>
                        {(Number(groupInfoDraft.participants) || 0)
                          + (Number(groupInfoDraft.supportersMale) || 0) + (Number(groupInfoDraft.supportersFemale) || 0)
                          + (Number(groupInfoDraft.customerStaff) || 0)} pax
                      </strong>
                      <span className="text-[#6B7280]"> — players + supporters + staff/teachers. This is the number the quote prices and free places are worked out from (MST tour leads are costed separately).</span>
                    </div>
                    {(() => {
                      // The totals are what the quote prices, so flag it when a
                      // male/female split has been filled in but doesn't add up to it.
                      const mismatches = [
                        ['Players / Students', groupInfoDraft.participants, groupInfoDraft.participantsMale, groupInfoDraft.participantsFemale],
                        ['Staff / Teachers', groupInfoDraft.customerStaff, groupInfoDraft.customerStaffMale, groupInfoDraft.customerStaffFemale],
                      ].filter(([, total, m, f]) => {
                        if (m === '' && f === '') return false;
                        return (Number(m) || 0) + (Number(f) || 0) !== (Number(total) || 0);
                      });
                      if (mismatches.length === 0) return null;
                      return (
                        <div className="bg-amber-light border border-ba/25 rounded-[8px] px-3 py-2 text-[12.5px] text-[#92400E]">
                          {mismatches.map(([label, total, m, f]) => (
                            <p key={label}>
                              <strong>{label}:</strong> the male/female split ({(Number(m) || 0) + (Number(f) || 0)}) doesn&apos;t match the total ({Number(total) || 0}). The total is the figure the quote uses.
                            </p>
                          ))}
                        </div>
                      );
                    })()}
                    <div className="flex gap-2 pt-1">
                      <button onClick={saveGroupInfo} disabled={savingGroupInfo} className="px-4 py-2 text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
                        {savingGroupInfo ? 'Saving…' : 'Save changes'}
                      </button>
                      <button onClick={() => setEditingGroupInfo(false)} className="px-4 py-2 text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-[8px] transition">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  (() => {
                    const players = Number(request.participants) || 0;
                    const suppM = Number(request.supportersMale) || 0;
                    const suppF = Number(request.supportersFemale) || 0;
                    const supporters = suppM + suppF;
                    const custStaff = Number(request.customerStaff) || 0;
                    const totalGroup = totalGroupCount;
                    const genderSub = (m, f) => [m ? `${m} male` : null, f ? `${f} female` : null].filter(Boolean).join(' / ');
                    const playerSub = genderSub(Number(request.participantsMale) || 0, Number(request.participantsFemale) || 0);
                    const suppSub = genderSub(suppM, suppF);
                    const staffSub = genderSub(Number(request.customerStaffMale) || 0, Number(request.customerStaffFemale) || 0);
                    return (
                      <div className="grid grid-cols-2 gap-5">
                        <Field label="Group Type" value={request.groupType} />
                        {isLlsGroupType(request.groupType) && <Field label="LLS Group" value={request.llsGroup ? `Group ${request.llsGroup}` : 'Not set'} />}
                        <Field label="Total Travelling Group" value={totalGroup ? `${totalGroup} pax` : null} />
                        <Field label="Players / Students" value={players ? `${players}${playerSub ? ` (${playerSub})` : ''}` : null} />
                        <Field label="Supporters / Other Travellers" value={supporters ? `${supporters}${suppSub ? ` (${suppSub})` : ''}` : '—'} />
                        <Field label="Staff / Teachers" value={custStaff ? `${custStaff}${staffSub ? ` (${staffSub})` : ''}` : '—'} />
                        {request.numberOfSquads != null && (
                          <Field label="Teams / Squads" value={`${request.numberOfSquads}`} />
                        )}
                      </div>
                    );
                  })()
                )}
              </div>

              {/* Contact */}
              <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">{tr('tour.secContact')}</h3>
                  {!editingContacts && (
                    <button
                      onClick={openContactsEditor}
                      className="text-[12px] font-semibold text-pb hover:text-navy transition"
                    >
                      {(request.keyContacts || []).length > 0 ? 'Edit contacts' : 'Edit / add contacts'}
                    </button>
                  )}
                </div>

                {/* Primary contact — always shown */}
                <div className="mb-4 pb-4 border-b border-[#F1F3F6]">
                  <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Primary Contact</p>
                  {editingContacts ? (
                    <div className="p-3 bg-[#F9FAFB] rounded-[10px] space-y-2">
                      <div className="grid grid-cols-3 gap-2">
                        <input value={primaryDraft.contactName} placeholder="Full name *"
                          onChange={(e) => setPrimaryDraft((p) => ({ ...p, contactName: e.target.value }))}
                          className="border border-[#E5E7EB] rounded-[7px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
                        <input value={primaryDraft.phone} placeholder="Phone"
                          onChange={(e) => setPrimaryDraft((p) => ({ ...p, phone: e.target.value }))}
                          className="border border-[#E5E7EB] rounded-[7px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
                        <input value={primaryDraft.customerEmail} placeholder="Email *" type="email"
                          onChange={(e) => setPrimaryDraft((p) => ({ ...p, customerEmail: e.target.value }))}
                          className="border border-[#E5E7EB] rounded-[7px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white min-w-0" />
                      </div>
                      <p className="text-[11.5px] text-[#9CA3AF]">The primary contact receives all tour emails, so changing the email address changes where they are sent.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-5">
                      <Field label="Contact Name" value={request.contactName} />
                      <Field label="Phone" value={request.phone} />
                      <Field label="Email" value={request.customerEmail} />
                    </div>
                  )}
                </div>

                {/* Additional contacts */}
                {editingContacts ? (
                  <div className="space-y-3">
                    <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Additional Contacts</p>
                    {contactsDraft.map((c, i) => (
                      <div key={i} className="p-3 bg-[#F9FAFB] rounded-[10px] space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                          <input value={c.name} placeholder="Full name *"
                            onChange={(e) => setContactsDraft((p) => p.map((k, idx) => idx === i ? { ...k, name: e.target.value } : k))}
                            className="border border-[#E5E7EB] rounded-[7px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
                          <input value={c.phone || ''} placeholder="Phone"
                            onChange={(e) => setContactsDraft((p) => p.map((k, idx) => idx === i ? { ...k, phone: e.target.value } : k))}
                            className="border border-[#E5E7EB] rounded-[7px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
                          <div className="flex gap-1.5">
                            <input value={c.email || ''} placeholder="Email"
                              onChange={(e) => setContactsDraft((p) => p.map((k, idx) => idx === i ? { ...k, email: e.target.value } : k))}
                              className="flex-1 border border-[#E5E7EB] rounded-[7px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white min-w-0" />
                            <button type="button"
                              title="Remove this contact"
                              aria-label="Remove this contact"
                              onClick={() => {
                                const filled = (c.name || '').trim() || (c.phone || '').trim() || (c.email || '').trim();
                                if (filled && !window.confirm(`Remove ${c.name || 'this contact'} from the key contacts?`)) return;
                                setContactsDraft((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : [{ name: '', phone: '', email: '', inviteToPortal: false }]));
                              }}
                              className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-dr text-lg flex-shrink-0">×</button>
                          </div>
                        </div>
                        {c.email && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={!!c.inviteToPortal}
                              onChange={(e) => setContactsDraft((p) => p.map((k, idx) => idx === i ? { ...k, inviteToPortal: e.target.checked } : k))}
                              className="w-3.5 h-3.5 accent-pb" />
                            <span className="text-[12px] text-[#374151]">Send portal invite to this contact</span>
                          </label>
                        )}
                      </div>
                    ))}
                    <button type="button"
                      onClick={() => setContactsDraft((p) => [...p, { name: '', phone: '', email: '', inviteToPortal: false }])}
                      className="flex items-center gap-1.5 text-[12px] font-semibold text-pb hover:text-navy transition">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                      Add another contact
                    </button>
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => setEditingContacts(false)} disabled={savingContacts}
                        className="px-3 py-1.5 text-[12px] font-semibold text-[#6B7280] border border-[#E5E7EB] rounded-[8px] hover:bg-surface transition">Cancel</button>
                      <button onClick={saveKeyContacts} disabled={savingContacts}
                        className="px-3 py-1.5 text-[12px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
                        {savingContacts ? 'Saving…' : 'Save contacts'}
                      </button>
                    </div>
                  </div>
                ) : (request.keyContacts || []).length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Additional Contacts</p>
                    {(request.keyContacts || []).map((c, i) => (
                      <div key={i} className="grid grid-cols-2 gap-5">
                        <Field label="Contact Name" value={c.name} />
                        <Field label="Phone" value={c.phone} />
                        <Field label="Email" value={c.email} />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              {/* Customer's instant quote estimate — web-quote leads only. Full detail
                  and the "copy into cost sheet" action live on the Quote tab, next to
                  the price code picker; this is just a heads-up that one exists. */}
              {request.source === 'instant_quote' && request.estimateDetails && (
                <div className="bg-amber-light border border-ba/30 rounded-[12px] p-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-[4px] bg-ba text-navy">WEB QUOTE</span>
                    <p className="text-[12.5px] text-[#374151]">
                      Customer's instant quote estimate: <span className="font-bold text-navy">£{Number(request.estimateDetails.total || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</span> total.
                    </p>
                  </div>
                  <button type="button" onClick={() => handleTabChange('quote')} className="text-[12px] font-semibold text-pb hover:text-navy transition flex-shrink-0">
                    View on Quote tab →
                  </button>
                </div>
              )}

              {/* Requested Add-ons */}
              <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Requested Add-ons</h3>
                  {!editingAddons && (
                    <button
                      onClick={() => { setAddonsDraft([...(request.selectedAddons || [])].map((a) => ({ ...a }))); setEditingAddons(true); }}
                      className="text-[12px] font-semibold text-pb hover:text-navy transition"
                    >
                      Edit
                    </button>
                  )}
                </div>

                {editingAddons ? (
                  <div className="space-y-4">
                    {/* Current add-ons */}
                    {addonsDraft.length === 0 && (
                      <p className="text-[12px] text-[#9CA3AF]">No add-ons selected. Use the selector below to add some.</p>
                    )}
                    <div className="space-y-2">
                      {addonsDraft.map((addon, idx) => {
                        const qtyConfig = QUANTITY_ADDONS[addon.categoryName];
                        return (
                          <div key={addon.categoryId || idx} className="flex items-center gap-3 p-3 bg-blue-light/40 border border-pb/15 rounded-[9px]">
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-semibold text-pb">{addon.categoryName}</p>
                            </div>
                            {qtyConfig && (
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button type="button"
                                  onClick={() => setAddonsDraft((prev) => prev.map((a, i) => i === idx ? { ...a, quantity: Math.max(1, (a.quantity || 1) - 1) } : a))}
                                  className="w-7 h-7 rounded-[6px] border border-[#E5E7EB] bg-white text-[#374151] text-[15px] flex items-center justify-center hover:border-pb/40 transition"
                                >−</button>
                                <span className="text-[13px] font-bold text-[#111827] w-6 text-center tabular-nums">{addon.quantity || 1}</span>
                                <button type="button"
                                  onClick={() => setAddonsDraft((prev) => prev.map((a, i) => i === idx ? { ...a, quantity: Math.min(qtyConfig.max, (a.quantity || 1) + 1) } : a))}
                                  className="w-7 h-7 rounded-[6px] border border-[#E5E7EB] bg-white text-[#374151] text-[15px] flex items-center justify-center hover:border-pb/40 transition"
                                >+</button>
                              </div>
                            )}
                            <button type="button"
                              onClick={() => setAddonsDraft((prev) => prev.filter((_, i) => i !== idx))}
                              className="text-[#9CA3AF] hover:text-dr transition flex-shrink-0"
                              title="Remove"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Add new add-on */}
                    {(() => {
                      const existingNames = new Set(addonsDraft.map((a) => a.categoryName));
                      const available = addonCategories.filter((c) => !existingNames.has(c.name));
                      if (!available.length) return null;
                      return (
                        <select
                          defaultValue=""
                          onChange={(e) => {
                            const cat = addonCategories.find((c) => c.id === e.target.value);
                            if (!cat) return;
                            const qtyConfig = QUANTITY_ADDONS[cat.name];
                            setAddonsDraft((prev) => [...prev, { categoryId: cat.id, categoryName: cat.name, notes: '', ...(qtyConfig ? { quantity: 1 } : {}) }]);
                            e.target.value = '';
                          }}
                          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] text-[#374151] focus:outline-none focus:ring-2 focus:ring-pb"
                        >
                          <option value="">+ Add an add-on…</option>
                          {available.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      );
                    })()}

                    <div className="flex gap-2 pt-1">
                      <button onClick={saveAddons} disabled={savingAddons} className="px-4 py-2 text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
                        {savingAddons ? 'Saving…' : 'Save add-ons'}
                      </button>
                      <button onClick={() => setEditingAddons(false)} className="px-4 py-2 text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-[8px] transition">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {(request.selectedAddons || []).length === 0 ? (
                      <p className="text-[12px] text-[#9CA3AF]">No add-ons requested.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {request.selectedAddons.map((addon) => (
                          <div key={addon.categoryId} className="bg-blue-light border border-pb/20 rounded-[8px] px-3 py-2">
                            <p className="text-[13px] font-medium text-pb">{addon.categoryName}{addon.quantity ? ` × ${addon.quantity}` : ''}</p>
                            {addon.notes && <p className="text-[12px] text-pb/70 mt-0.5">{addon.notes}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Customer Preferences */}
              {!isPadelante && request.customerPreferences && Object.keys(request.customerPreferences).length > 0 && (
                <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
                  <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-4">Customer Preferences</h3>
                  <div className="space-y-2">
                    {Object.entries(request.customerPreferences).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-3">
                        <span className="text-[12px] font-medium text-[#6B7280] w-36">{key}</span>
                        <span className="text-[13px] text-[#111827] font-medium">
                          {Array.isArray(value) ? value.join(', ') || '—' : value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Requirements */}
              {!isPadelante && request.notes && (
                <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
                  <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-3">Additional Requirements</h3>
                  <p className="text-[13px] text-[#374151] whitespace-pre-wrap">{request.notes}</p>
                </div>
              )}

              {/* Customer Amendment Request */}
              {request.customerAmendmentRequest && (
                <div className="bg-amber-light border border-ba/25 rounded-[12px] p-5">
                  <p className="text-[11px] font-semibold text-[#92400E] uppercase tracking-[0.9px] mb-1">Customer Amendment Request</p>
                  <p className="text-[13px] text-[#92400E]">{request.customerAmendmentRequest}</p>
                </div>
              )}

              {/* Form links — shown once deposit paid so staff can share manually */}
              {!isPadelante && !isCrmOrigin && (request.teamInfoToken || request.participantRegToken) && (
                <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Tour Contact Forms</h3>
                    {!automationOn && (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-light text-[#92400E]">
                        Automation off — share manually
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {request.teamInfoToken && (
                      <div className="flex items-center justify-between gap-3 p-3 bg-[#F9FAFB] rounded-[8px] border border-[#E5E7EB]">
                        <div className="min-w-0">
                          <p className="text-[12px] font-semibold text-[#374151]">Team Information Form</p>
                          <p className="text-[11px] text-[#9CA3AF] truncate">
                            https://msttoursportal.co.uk/team-info?token={request.teamInfoToken}
                          </p>
                        </div>
                        <button
                          onClick={() => navigator.clipboard.writeText(`https://msttoursportal.co.uk/team-info?token=${request.teamInfoToken}`)}
                          className="text-[11px] font-semibold text-pb hover:text-navy flex-shrink-0 transition"
                        >
                          Copy link
                        </button>
                      </div>
                    )}
                    {request.participantRegToken && (
                      <div className="flex items-center justify-between gap-3 p-3 bg-[#F9FAFB] rounded-[8px] border border-[#E5E7EB]">
                        <div className="min-w-0">
                          <p className="text-[12px] font-semibold text-[#374151]">Participant Registration</p>
                          <p className="text-[11px] text-[#9CA3AF] truncate">
                            https://msttoursportal.co.uk/register?token={request.participantRegToken}
                          </p>
                        </div>
                        <button
                          onClick={() => navigator.clipboard.writeText(`https://msttoursportal.co.uk/register?token=${request.participantRegToken}`)}
                          className="text-[11px] font-semibold text-pb hover:text-navy flex-shrink-0 transition"
                        >
                          Copy link
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Participant list — visible once deposit paid stage is ticked */}
              {!isPadelante && !isCrmOrigin && showParticipants(request.category, request.stageProgress) && (
                <ParticipantCards requestId={id} expectedCount={totalGroupCount} editable />
              )}
            </div>
          )}

          {/* ── Quote tab — Padelante bespoke quote + invoicing ──────────── */}
          {activeTab === 'quote' && isPadelante && (
            <PadelanteQuote
              requestId={id}
              request={request}
              onSendQuoteLetter={handleSendForApproval}
              sendingQuoteLetter={sendingQuoteLetter}
              quoteLetterSent={quoteLetterSent}
              quoteLetterError={quoteLetterError}
            />
          )}

          {/* ── Quote tab ───────────────────────────────────────────────── */}
          {activeTab === 'quote' && !isPadelante && (
            <div className="space-y-4">
              {/* Customer's instant quote estimate — web-quote leads only */}
              {request.source === 'instant_quote' && request.estimateDetails && (
                <div className="bg-amber-light border border-ba/30 rounded-[12px] p-6">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-[4px] bg-ba text-navy">WEB QUOTE</span>
                      <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Customer's Instant Quote Estimate</h3>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <button
                        type="button"
                        onClick={copyWebQuoteIntoCostSheet}
                        disabled={!selectedPriceCodeId}
                        title={!selectedPriceCodeId ? 'Select a price code below first' : 'Add these figures as editable lines in the cost sheet'}
                        className="text-[11.5px] font-semibold text-pb hover:text-navy transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {webQuoteCopied ? '✓ Copied into cost sheet' : 'Copy into cost sheet ↓'}
                      </button>
                      {!selectedPriceCodeId && (
                        <p className="text-[10.5px] text-[#9CA3AF] mt-0.5">Select a price code below first</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-[26px] font-extrabold text-navy">£{Number(request.estimateDetails.total || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</span>
                    <span className="text-[13px] text-[#6B7280]">total · £{Number(request.estimateDetails.perPerson || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })} per person</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12.5px] text-[#6B7280]">
                    {request.estimateDetails.groundPP > 0 && <div><span className="font-semibold text-[#374151]">Ground:</span> £{request.estimateDetails.groundPP} pp</div>}
                    {request.estimateDetails.flightPP > 0 && <div><span className="font-semibold text-[#374151]">Flight:</span> £{request.estimateDetails.flightPP} pp</div>}
                    {request.estimateDetails.selfBookedTotal > 0 && <div><span className="font-semibold text-[#374151]">Self-booked total:</span> £{request.estimateDetails.selfBookedTotal} pp</div>}
                    {request.estimateDetails.transportCost > 0 && <div><span className="font-semibold text-[#374151]">Transport:</span> £{request.estimateDetails.transportCost}</div>}
                  </div>
                  {request.estimateDetails.travellers != null && (() => {
                    const est = request.estimateDetails;
                    const currentTravellers = (Number(request.participants) || 0)
                      + (Number(request.customerStaff) || 0)
                      + (Number(request.supportersMale) || 0) + (Number(request.supportersFemale) || 0);
                    const travellersChanged = currentTravellers > 0 && currentTravellers !== est.travellers;
                    const squadsChanged = request.numberOfSquads != null && request.numberOfSquads !== est.numberOfSquads;
                    return (
                      <div className="mt-3 pt-3 border-t border-ba/20">
                        <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1.5">What this quote was based on</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-[12.5px] text-[#6B7280]">
                          <div>
                            <span className="font-semibold text-[#374151]">Travellers:</span> {est.travellers}
                            {travellersChanged && <span className="text-ba font-semibold"> (now {currentTravellers})</span>}
                          </div>
                          <div>
                            <span className="font-semibold text-[#374151]">Squads:</span> {est.numberOfSquads}
                            {squadsChanged && <span className="text-ba font-semibold"> (now {request.numberOfSquads})</span>}
                          </div>
                          {est.fixtureQty > 0 && <div><span className="font-semibold text-[#374151]">Fixtures:</span> {est.fixtureQty}</div>}
                          {est.trainingQty > 0 && <div><span className="font-semibold text-[#374151]">Training sessions:</span> {est.trainingQty}</div>}
                          {est.nights > 0 && <div><span className="font-semibold text-[#374151]">Nights:</span> {est.nights}</div>}
                          {est.freePlaces != null && <div><span className="font-semibold text-[#374151]">Free places:</span> {est.freePlaces}</div>}
                          {est.hotelName && <div className="col-span-2"><span className="font-semibold text-[#374151]">Hotel:</span> {est.hotelName}{est.boardType ? ` · ${est.boardType}` : ''}{est.roomType ? ` · ${est.roomType}` : ''}</div>}
                          {est.extras?.length > 0 && <div className="col-span-2 sm:col-span-4"><span className="font-semibold text-[#374151]">Extras:</span> {est.extras.join(', ')}</div>}
                        </div>
                        {(travellersChanged || squadsChanged) && (
                          <p className="text-[11.5px] text-ba font-semibold mt-1.5">
                            The scope has changed since this quote was raised — treat the £ figures above as historical, not current.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  <p className="text-[11.5px] text-[#9CA3AF] mt-3">This is what the customer saw on the public quote page — not a firm price. Select a price code below, then copy these in as a starting point.</p>
                </div>
              )}
              {request.quoteNeedsUpdate && (
                <div className="flex items-start gap-3 px-4 py-3 rounded-[12px] border" style={{ background: '#FEF6E4', borderColor: 'rgba(232,160,32,.3)' }}>
                  <svg className="w-4 h-4 text-[#92400E] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.3 3.4c-.87 1.5.22 3.37 1.95 3.37h14.7c1.73 0 2.82-1.87 1.95-3.37L13.95 3.38c-.87-1.5-3.03-1.5-3.9 0z M12 15.75h.01" /></svg>
                  <div>
                    <p className="text-[13px] font-bold text-[#92400E]">Add-ons have changed — reseed the cost sheet</p>
                    <p className="text-[12px] text-[#92400E]/80 mt-0.5">The selected add-ons have been amended. Select a price code to rebuild the cost sheet, review the lines, then save.</p>
                  </div>
                </div>
              )}
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-[14.5px] font-bold text-[#111827]">Your Quotes</h3>
                    <p className="text-[12px] text-[#6B7280] mt-0.5">A lead can carry several quote options — each with its own dates and pricing — sent as separate emails.</p>
                  </div>
                  <button type="button" onClick={startNewQuote}
                    className="px-[14px] py-[7px] text-[12.5px] font-semibold text-pb border border-pb hover:bg-[#F0F7FF] rounded-[8px] transition flex-shrink-0">
                    + New quote
                  </button>
                </div>
                {quotes.length === 0 ? (
                  <p className="text-[12.5px] text-[#9CA3AF] mb-5">No quotes saved yet — build one below and click Save Quote.</p>
                ) : (
                  <div className="space-y-2 mb-5">
                    {quotes.map((q) => {
                      const isActive = q.id === activeQuoteId;
                      const badge = q.status === 'proceeded'
                        ? <span className="inline-flex items-center gap-1.5 px-[10px] py-[3px] rounded-[20px] text-[11px] font-bold bg-green-light text-sg flex-shrink-0"><span className="w-[5px] h-[5px] rounded-full bg-sg flex-shrink-0" />Proceeded</span>
                        : q.status === 'sent'
                        ? <span className="inline-flex items-center gap-1.5 px-[10px] py-[3px] rounded-[20px] text-[11px] font-bold bg-amber-light text-[#92400E] flex-shrink-0"><span className="w-[5px] h-[5px] rounded-full bg-ba flex-shrink-0" />Sent</span>
                        : q.status === 'pending_approval'
                        ? <span className="inline-flex items-center gap-1.5 px-[10px] py-[3px] rounded-[20px] text-[11px] font-bold bg-[#F0F7FF] text-pb flex-shrink-0"><span className="w-[5px] h-[5px] rounded-full bg-pb flex-shrink-0" />Awaiting approval</span>
                        : <span className="inline-flex items-center gap-1.5 px-[10px] py-[3px] rounded-[20px] text-[11px] font-bold bg-[#F3F4F6] text-[#6B7280] flex-shrink-0"><span className="w-[5px] h-[5px] rounded-full bg-[#9CA3AF] flex-shrink-0" />Draft</span>;
                      return (
                        <div key={q.id} className={`flex items-center justify-between gap-3 rounded-[10px] border px-4 py-3 ${isActive ? 'border-pb bg-[#F0F7FF]' : 'border-[#E5E7EB]'}`}>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-[13px] font-semibold text-[#111827] truncate">{q.label || 'Quote option'}</p>
                              {badge}
                            </div>
                            <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">
                              {fmt(q.costPerPersonToCustomer, CURRENCY_SYMBOLS_EXP[q.custCurrency] || '£')} pp · {q.priceCodeName || '—'}
                            </p>
                            {q.status === 'pending_approval' && (
                              <p className="text-[11.5px] text-pb bg-[#F0F7FF] border border-[#DBEAFE] rounded-[6px] px-2 py-1 mt-1.5 inline-block">
                                Sent for approval{q.pendingApprovalAt?.toDate ? ` on ${q.pendingApprovalAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''} — awaiting Michael's approval
                              </p>
                            )}
                            {q.status === 'draft' && q.revisionComment && (
                              <p className="text-[11.5px] text-[#92400E] bg-amber-light rounded-[6px] px-2 py-1 mt-1.5 inline-block">
                                Michael requested changes: "{q.revisionComment}"
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <button type="button" onClick={() => loadQuoteForEdit(q)} className="text-[12px] font-semibold text-pb hover:text-navy transition">
                              {isActive ? 'Editing' : 'Edit'}
                            </button>
                            {q.status !== 'proceeded' && (
                              <button type="button" onClick={() => markQuoteProceeded(q.id)} className="text-[12px] font-semibold text-sg hover:opacity-70 transition">Mark proceeded</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="h-px bg-[#E5E7EB] mb-5" />

                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-[14.5px] font-bold text-[#111827]">{activeQuoteId ? 'Edit Quote' : 'New Quote'} — Cost Sheet</h3>
                    <p className="text-[12px] text-[#6B7280] mt-0.5">Select a price code to seed the cost sheet, edit any line directly, then save.</p>
                  </div>
                  {activeQuoteId
                    ? <span className="inline-flex items-center gap-1.5 px-[10px] py-[4px] rounded-[20px] text-[11.5px] font-bold bg-green-light text-sg"><span className="w-[5px] h-[5px] rounded-full bg-sg flex-shrink-0" />Quote saved</span>
                    : <span className="inline-flex items-center gap-1.5 px-[10px] py-[4px] rounded-[20px] text-[11.5px] font-bold bg-amber-light text-[#92400E]"><span className="w-[5px] h-[5px] rounded-full bg-ba flex-shrink-0" />Draft</span>
                  }
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4 max-w-md">
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1.5">Departure (this quote)</label>
                    <input type="date" value={quoteDepartureDate} onChange={(e) => setQuoteDepartureDate(e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1.5">Return (this quote)</label>
                    <input type="date" value={quoteReturnDate} onChange={(e) => setQuoteReturnDate(e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1.5">Flight price (this quote)</label>
                    <input type="text" placeholder="£200.00pp" value={quoteFlightPriceManual}
                      onChange={(e) => setQuoteFlightPriceManual(e.target.value)}
                      onBlur={(e) => applyQuoteFlightPrice(e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                    <p className="text-[10.5px] text-[#9CA3AF] mt-1">Updates the Flights line below on this quote only</p>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1.5">Flight time (this quote)</label>
                    <input type="text" placeholder="09:30" value={quoteFlightTimeManual}
                      onChange={(e) => setQuoteFlightTimeManual(e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                  </div>
                </div>

                {/* Quote templates — load a saved cost sheet from another tour */}
                {quoteTemplates.length > 0 && (
                  <div className="mb-4 flex items-center gap-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-[8px] px-3 py-2.5">
                    <label className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] flex-shrink-0">Template</label>
                    <select
                      value={selectedQuoteTemplateId}
                      onChange={(e) => setSelectedQuoteTemplateId(e.target.value)}
                      className="flex-1 min-w-0 border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[12.5px] bg-white focus:outline-none focus:ring-2 focus:ring-pb"
                    >
                      <option value="">— Load a saved quote template —</option>
                      {quoteTemplates.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}{t.priceCodeName ? ` (${t.priceCodeName})` : ''}</option>
                      ))}
                    </select>
                    <button type="button" onClick={handleApplyQuoteTemplate} disabled={!selectedQuoteTemplateId || applyingQuoteTemplate}
                      className="px-3 py-1.5 text-[12px] font-semibold text-white bg-pb hover:bg-navy rounded-[6px] transition disabled:opacity-40 flex-shrink-0">
                      {applyingQuoteTemplate ? 'Applying…' : 'Apply'}
                    </button>
                    {selectedQuoteTemplateId && (
                      <button type="button"
                        onClick={() => handleDeleteQuoteTemplate(quoteTemplates.find((t) => t.id === selectedQuoteTemplateId))}
                        className="text-[11.5px] text-[#9CA3AF] hover:text-dr transition flex-shrink-0">Delete</button>
                    )}
                  </div>
                )}

                {/* Row 1: Price code (full width) */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Price Code</label>
                    {isAdmin && (
                      <button type="button" onClick={() => navigate('/staff/price-codes')} className="text-[12px] text-pb hover:text-navy font-medium">Manage →</button>
                    )}
                  </div>
                  <select
                    value={selectedPriceCodeId}
                    onChange={(e) => handlePriceCodeChange(e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                  >
                    <option value="">— Select price code —</option>
                    {priceCodes
                      .filter((pc) => marketMatch(pc, request.market || 'uk_to_spain') || pc.id === selectedPriceCodeId)
                      .map((pc) => (
                        <option key={pc.id} value={pc.id}>{pc.name}{pc.description ? ` (${pc.description})` : ''}</option>
                      ))}
                  </select>
                  {priceCodes.length === 0 && <p className="text-[12px] text-[#9CA3AF] mt-1">No price codes yet.</p>}
                </div>

                {/* Row 2: MST staff counts */}
                <div className="grid grid-cols-2 gap-4 mb-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] mb-1.5 uppercase tracking-[0.9px]">
                      Tour Leads <span className="font-normal normal-case text-[#9CA3AF]">(€100 per {request.leaderCostBasis === 'days' ? 'day' : 'night'} each)</span>
                    </label>
                    <input
                      type="number" min="0"
                      value={tourLeadCount}
                      onChange={(e) => setTourLeadCount(e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                    <p className="text-[11px] text-[#9CA3AF] mt-1">Charged to customer at cost</p>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] mb-1.5 uppercase tracking-[0.9px]">
                      Junior Leads <span className="font-normal normal-case text-[#9CA3AF]">(€50 per {request.leaderCostBasis === 'days' ? 'day' : 'night'} each)</span>
                    </label>
                    <input
                      type="number" min="0"
                      value={juniorLeadCount}
                      onChange={(e) => setJuniorLeadCount(e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                    <p className="text-[11px] text-[#9CA3AF] mt-1">Charged to customer at cost</p>
                  </div>
                </div>
                {(Number(tourLeadCount) > 0 || Number(juniorLeadCount) > 0) && (
                  <div className="mb-4 text-[11px] text-[#6B7280]">
                    {request.leaderCostBasis === 'days' ? (
                      <span>Leader cost is based on calendar days present (arrival through departure).</span>
                    ) : (
                      <span>
                        Leader cost is based on nights stayed (legacy).{' '}
                        <button
                          type="button"
                          onClick={switchLeaderCostBasisToDays}
                          className="underline text-pb hover:text-pb-dark"
                        >
                          Switch this tour to day-based leader costing
                        </button>
                      </span>
                    )}
                  </div>
                )}

                {/* Large kit cases (conditional) */}
                {(request.selectedAddons || []).some((a) => a.categoryName === 'Flights') && (
                  <div className="mb-4 max-w-xs">
                    <label className="block text-[11px] font-semibold text-[#9CA3AF] mb-1.5 uppercase tracking-[0.9px]">
                      Large kit cases <span className="font-normal normal-case">(price set in price code)</span>
                    </label>
                    <input
                      type="number" min="0" max="50"
                      value={largeCaseQuantity}
                      onChange={(e) => setLargeCaseQuantity(e.target.value)}
                      placeholder="0"
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                  </div>
                )}

                {/* Live cost sheet */}
                {selectedPriceCodeId && calculatedTotals && (() => {
                  const { sym: csym, totalHeadcount, nights, freePlaces, autoFreePlaces, freePlacesIsManual, payingPlayers, computedStatic, autoLines } = calculatedTotals;
                  const totals = displayTotals || calculatedTotals;
                  const suppTotal = (Number(request?.supportersMale) || 0) + (Number(request?.supportersFemale) || 0);
                  // The customer's own party — what free places and the per-person
                  // price are worked out from. totalHeadcount adds the MST leads on top.
                  const mstLeadTotal = (Number(tourLeadCount) || 0) + (Number(juniorLeadCount) || 0);
                  const travellingGroup = totalHeadcount - mstLeadTotal;

                  return (
                    <>
                      {/* Headcount strip — the customer's travelling group first, then
                          the same group with MST leads added, then the pricing figures. */}
                      <div className="mb-4 border border-[#E5E7EB] rounded-[9px] overflow-hidden grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-px bg-[#E5E7EB]">
                        <div className="bg-surface px-4 py-3">
                          <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Travelling group</p>
                          <p className="text-[17px] font-bold text-[#111827] leading-none mb-1.5">{travellingGroup}</p>
                          <p className="text-[11px] text-[#6B7280]">
                            {[
                              `${request.participants || 0} players`,
                              (request.customerStaff || 0) > 0 ? `${request.customerStaff} staff/teachers` : null,
                              suppTotal > 0 ? `${suppTotal} supporters` : null,
                            ].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                        <div className="bg-surface px-4 py-3">
                          <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Incl. MST leads</p>
                          <p className="text-[17px] font-bold text-[#111827] leading-none mb-1.5">{totalHeadcount}</p>
                          <p className="text-[11px] text-[#6B7280]">
                            {mstLeadTotal > 0
                              ? [
                                  Number(tourLeadCount) > 0 ? `${tourLeadCount} tour lead${Number(tourLeadCount) > 1 ? 's' : ''}` : null,
                                  Number(juniorLeadCount) > 0 ? `${juniorLeadCount} junior lead${Number(juniorLeadCount) > 1 ? 's' : ''}` : null,
                                ].filter(Boolean).join(' · ')
                              : 'No MST leads added'}
                          </p>
                        </div>
                        <div className="bg-surface px-4 py-3">
                          <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Nights</p>
                          <p className="text-[17px] font-bold text-[#111827] leading-none mb-1.5">{nights}</p>
                          <p className="text-[11px] text-[#6B7280]">Drives per-night costs</p>
                        </div>
                        <div className="bg-surface px-4 py-3">
                          <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Free places</p>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <input
                              type="number" min="0" max={Math.max(0, travellingGroup)}
                              value={freePlacesOverride === '' ? String(autoFreePlaces) : freePlacesOverride}
                              onChange={(e) => setFreePlacesOverride(e.target.value)}
                              title="Cost of a free place is spread across the paying travellers"
                              className={`w-[54px] border rounded-[6px] px-1.5 py-0.5 text-[15px] font-bold text-center focus:outline-none focus:ring-2 focus:ring-sg ${
                                freePlacesIsManual ? 'border-sg bg-green-light text-sg' : 'border-[#E5E7EB] bg-white text-sg'
                              }`}
                            />
                            {freePlacesIsManual && (
                              <button
                                type="button"
                                onClick={() => setFreePlacesOverride('')}
                                className="text-[11px] underline text-[#6B7280] hover:text-[#111827]"
                                title={`Back to the automatic figure (${autoFreePlaces})`}
                              >
                                reset
                              </button>
                            )}
                          </div>
                          <p className="text-[11px] text-[#6B7280]">
                            {freePlacesIsManual ? `Set manually (auto: ${autoFreePlaces})` : '1 per 15 paying'}
                          </p>
                        </div>
                        <div className="bg-surface px-4 py-3 col-span-2 md:col-span-2 xl:col-span-1">
                          <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Paying</p>
                          <p className="text-[17px] font-bold text-pb leading-none mb-1.5">{payingPlayers}</p>
                          <p className="text-[11px] text-[#6B7280]">Per-person price basis</p>
                        </div>
                      </div>

                      {/* Exchange rate warning */}
                      {exchangeRates?.isStale && (
                        <div className="mb-4 bg-amber-light border border-ba/25 rounded-[8px] px-4 py-3 text-[12px] text-[#92400E] flex items-start gap-2">
                          <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                          <span><strong>Exchange rate warning:</strong> No current EUR/GBP rate is set — EUR costs using a fallback rate. Set correct rates in <strong>Admin Settings → Exchange Rates</strong>.</span>
                        </div>
                      )}

                      {/* Unpriced add-ons */}
                      {request.selectedAddons?.length > 0 && selectedPC && (() => {
                        const pricedNames = new Set(costSheet.map((l) => l.categoryName));
                        const unpriced = (request.selectedAddons || []).filter((a) => !pricedNames.has(a.categoryName));
                        if (unpriced.length === 0) return null;
                        return (
                          <div className="mb-4 bg-amber-light border border-ba/25 rounded-[8px] px-4 py-3 text-[12px] text-[#92400E]">
                            <strong>Note:</strong> These requested items are not in this price code and are excluded: {unpriced.map((a) => a.categoryName).join(', ')}.
                          </div>
                        );
                      })()}

                      {/* La Liga notice */}
                      {(request.selectedAddons || []).some((a) => a.categoryName === 'Match Tickets') && (
                        <div className="mb-4 bg-[#FFF1F2] border border-[#FDA4AF] rounded-[8px] px-4 py-3 flex items-start gap-2.5">
                          <svg className="w-4 h-4 text-[#E11D48] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z" /></svg>
                          <p className="text-[12px] text-[#BE123C]"><strong>{request.market === 'spain_to_uk' ? 'Premier League' : 'La Liga'} match ticket notice:</strong> Fixtures confirmed close to arrival — pricing cannot be guaranteed until at least 2 weeks before. Communicate this to the customer.</p>
                        </div>
                      )}

                      {/* Customer billing currency — the Cust column, totals, quote PDF and invoices are all in this currency */}
                      <div className="mb-3 flex items-center justify-end gap-2">
                        <span className="text-[11.5px] text-[#6B7280]">Customer pays in</span>
                        <select
                          value={custCurrency}
                          onChange={(e) => setCustCurrency(e.target.value)}
                          title="Currency the customer is billed in — Cust units auto-convert from each line's MST cost"
                          className="text-[12px] font-semibold text-pb border border-[#BFDBFE] bg-[#EBF2FC] rounded-[6px] py-1 px-2 focus:outline-none focus:ring-1 focus:ring-pb/40 cursor-pointer"
                        >
                          <option value="GBP">£ GBP</option>
                          <option value="EUR">€ EUR</option>
                          <option value="USD">$ USD</option>
                        </select>
                      </div>

                      {/* Editable cost sheet table — Basis shown as subtitle under Item to keep width compact */}
                      <div className="border border-[#E5E7EB] rounded-[9px] overflow-x-auto mb-4">
                        <table className="w-full text-[12px]">
                          <thead className="bg-surface border-b border-[#E5E7EB]">
                            <tr>
                              <th className="text-left px-3 py-2.5 text-[11px] font-bold text-[#6B7280]">Item</th>
                              <th className="text-right px-3 py-2.5 text-[11px] font-bold text-dr">MST Unit</th>
                              <th className="text-right px-3 py-2.5 text-[11px] font-bold text-pb">Cust Unit</th>
                              <th className="text-right px-3 py-2.5 text-[11px] font-bold text-dr">MST Total</th>
                              <th className="text-right px-3 py-2.5 text-[11px] font-bold text-pb">Cust Total</th>
                              <th className="w-8 px-2" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#F3F4F6]">

                            {/* Editable lines */}
                            {computedStatic.map((line) => {
                              const isInternal = !!line.isInternalOnly;
                              const noteOpen = !!expandedNotes[line.id];
                              const isCustom = line.id.startsWith('custom_');
                              const showEurBracket = (line.mstCurrNative === 'EUR' || line.mstCurrency === 'EUR') && csym !== '€' && line.mstNative != null && line.mstNative > 0;
                              return (
                                <Fragment key={line.id}>
                                  <tr className={isInternal ? 'bg-amber-light/20 hover:bg-amber-light/30' : 'hover:bg-[#FAFAFA]'}>
                                    <td className="px-3 py-2 min-w-[160px] max-w-[260px]">
                                      <input
                                        type="text"
                                        value={line.label}
                                        onChange={(e) => updateCostSheetLine(line.id, 'label', e.target.value)}
                                        placeholder={isCustom ? 'Item name…' : ''}
                                        className={`w-full text-[12px] font-medium bg-transparent border-b focus:outline-none px-0 py-0.5 ${
                                          isCustom && !line.label
                                            ? 'border-pb text-[#9CA3AF] placeholder-[#C4B5FD]'
                                            : 'text-[#111827] border-transparent hover:border-[#E5E7EB] focus:border-pb'
                                        }`}
                                      />
                                      {/* Basis + note truncate together on one line — a long note (e.g.
                                          the Flights line's) must not be able to stretch the table wider
                                          than this column; click the ✎ note button for the full text. */}
                                      <p className="text-[10.5px] text-[#9CA3AF] mt-0.5 truncate" title={line.note && !noteOpen ? line.note : undefined}>
                                        {line.basis}
                                        {isInternal && <span className="ml-1.5 text-[10px] text-[#92400E] bg-amber-light border border-ba/20 px-1 py-0.5 rounded">internal</span>}
                                        {line.note && !noteOpen && <span className="ml-1 italic">{line.note}</span>}
                                      </p>
                                    </td>
                                    <td className="px-3 py-2">
                                      <div className="flex items-center justify-end gap-1">
                                        <select
                                          value={line.mstCurrency || 'GBP'}
                                          onChange={(e) => updateCostSheetLine(line.id, 'mstCurrency', e.target.value)}
                                          title="Currency MST pays this in"
                                          className="text-[10px] text-[#6B7280] border border-[#E5E7EB] rounded-[4px] bg-white focus:outline-none focus:ring-1 focus:ring-dr/40 py-0.5 px-1 cursor-pointer"
                                        >
                                          <option value="GBP">£</option>
                                          <option value="EUR">€</option>
                                          <option value="USD">$</option>
                                        </select>
                                        <input
                                          type="number" step="0.01" min="0"
                                          value={line.unitCostToMST}
                                          onChange={(e) => updateCostSheetLine(line.id, 'unitCostToMST', e.target.value)}
                                          className="w-[72px] text-[12px] font-medium text-dr text-right bg-[#FEF8F8] border border-[#FECACA] rounded-[5px] px-2 py-1 focus:outline-none focus:ring-1 focus:ring-dr/40"
                                        />
                                      </div>
                                    </td>
                                    <td className="px-3 py-2">
                                      {isInternal ? (
                                        <span className="block text-right text-[11px] text-[#9CA3AF]">—</span>
                                      ) : (
                                        <>
                                          <div className="flex items-center justify-end gap-1">
                                            {line.customerOverridden ? (
                                              <button type="button" title="Reset to auto (converted MST cost)"
                                                onClick={() => resetCustomerOverride(line.id)}
                                                className="text-[11px] leading-none text-[#9CA3AF] hover:text-pb transition">↺</button>
                                            ) : (
                                              <span title="Auto: converted from MST cost" className="text-[9px] font-semibold text-pb/60 uppercase tracking-[0.5px]">auto</span>
                                            )}
                                            <span className="text-[10px] text-[#9CA3AF]">{csym}</span>
                                            <input
                                              type="number" step="0.01" min="0"
                                              value={line.unitCostToCustomer}
                                              onChange={(e) => updateCostSheetLine(line.id, 'unitCostToCustomer', e.target.value)}
                                              className="w-[72px] text-[12px] font-medium text-pb text-right bg-[#EBF2FC] border border-[#BFDBFE] rounded-[5px] px-2 py-1 focus:outline-none focus:ring-1 focus:ring-pb/40"
                                            />
                                          </div>
                                        </>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                      <span className="text-[12px] font-semibold text-dr">{fmt(line.mstTotal, csym)}</span>
                                      {showEurBracket && <p className="text-[10px] text-[#9CA3AF] mt-0.5">(€{line.mstNative.toFixed(2)})</p>}
                                    </td>
                                    <td className="px-3 py-2 text-right text-[12px] font-semibold tabular-nums">
                                      {isInternal ? <span className="text-[#9CA3AF]">—</span> : <span className="text-pb">{fmt(line.customerTotal, csym)}</span>}
                                    </td>
                                    <td className="px-2 py-2">
                                      <div className="flex flex-col items-center gap-1">
                                        <button type="button" title="Add note"
                                          onClick={() => setExpandedNotes((p) => ({ ...p, [line.id]: !p[line.id] }))}
                                          className={`text-[10px] leading-none transition ${noteOpen ? 'text-pb' : 'text-[#D1D5DB] hover:text-[#9CA3AF]'}`}>✎</button>
                                        <button type="button" title="Remove line"
                                          onClick={() => removeCostSheetLine(line.id)}
                                          className="text-[10px] leading-none text-[#D1D5DB] hover:text-dr transition">×</button>
                                      </div>
                                    </td>
                                  </tr>
                                  {noteOpen && (
                                    <tr className="bg-[#F9FAFB]">
                                      <td colSpan={6} className="px-3 pb-2 pt-1">
                                        <textarea
                                          rows={2}
                                          value={line.note || ''}
                                          onChange={(e) => updateCostSheetLine(line.id, 'note', e.target.value)}
                                          placeholder="Staff note for this line (not shown to customer)…"
                                          autoFocus={isCustom && !line.note}
                                          className="w-full text-[12px] border border-[#E5E7EB] rounded-[6px] px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-pb resize-none text-[#374151]"
                                        />
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              );
                            })}

                            {/* Add custom line */}
                            <tr>
                              <td colSpan={6} className="px-3 py-2">
                                <button type="button" onClick={addCustomCostSheetLine}
                                  className="text-[11.5px] font-semibold text-pb hover:text-navy transition flex items-center gap-1">
                                  <span className="text-[15px] leading-none">+</span> Add custom line
                                </button>
                              </td>
                            </tr>

                            {/* Auto lines separator */}
                            {autoLines.length > 0 && (
                              <tr className="bg-surface">
                                <td colSpan={6} className="px-3 py-1.5">
                                  <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-[0.8px]">Auto lines — calculated from group inputs, not editable</p>
                                </td>
                              </tr>
                            )}

                            {/* Auto lines */}
                            {autoLines.map((line) => {
                              const isInternal = !!line.isInternalOnly;
                              const showEurAuto = line.mstCurrency === 'EUR' && csym !== '€' && line.costEUR != null;
                              return (
                                <tr key={line.id} className={isInternal ? 'bg-amber-light/30' : 'bg-blue-light/20'}>
                                  <td className="px-3 py-2 text-[12px] font-medium text-[#374151]">
                                    {line.label}
                                    <span className={`ml-1.5 text-[10px] font-normal px-1 py-0.5 rounded border ${isInternal ? 'text-[#92400E] bg-amber-light border-ba/20' : 'text-pb bg-blue-light border-pb/20'}`}>
                                      {isInternal ? 'internal' : 'at cost'}
                                    </span>
                                    <p className="text-[10.5px] text-[#9CA3AF] mt-0.5 font-normal">{line.basis}</p>
                                  </td>
                                  <td className="px-3 py-2 text-right text-[11px] text-[#9CA3AF] tabular-nums">
                                    {line.costEUR !== undefined ? <span>€{line.costEUR.toFixed(2)}</span> : '—'}
                                  </td>
                                  <td className="px-3 py-2 text-right text-[11px] text-[#9CA3AF]">
                                    {isInternal ? '—' : 'at cost'}
                                  </td>
                                  <td className="px-3 py-2 text-right tabular-nums">
                                    <span className="text-[12px] font-semibold text-dr">{fmt(line.mstTotal, csym)}</span>
                                    {showEurAuto && <p className="text-[10px] text-[#9CA3AF] mt-0.5">(€{line.costEUR.toFixed(2)})</p>}
                                  </td>
                                  <td className="px-3 py-2 text-right text-[12px] font-semibold tabular-nums">
                                    {isInternal ? <span className="text-[#9CA3AF]">—</span> : <span className="text-pb">{fmt(line.customerTotal, csym)}</span>}
                                  </td>
                                  <td className="px-2 py-2" />
                                </tr>
                              );
                            })}
                          </tbody>

                          {/* Totals footer */}
                          <tfoot className="border-t-2 border-[#E5E7EB]">
                            <tr className="bg-[#F9FAFB]">
                              <td className="px-3 py-2.5 text-[11px] font-bold text-[#374151] text-right">Subtotals</td>
                              <td colSpan={2} />
                              <td className="px-3 py-2.5 text-right text-[13px] font-bold text-dr tabular-nums">{fmt(calculatedTotals.totalCostToMST, csym)}</td>
                              <td className="px-3 py-2.5 text-right text-[13px] font-bold text-pb tabular-nums">{fmt(calculatedTotals.totalCostToCustomer, csym)}</td>
                              <td />
                            </tr>
                            <tr className="bg-[#EBF2FC]/40">
                              <td className="px-3 py-2.5 text-[11px] font-semibold text-pb text-right">
                                Per paying player ({payingPlayers} paying{freePlaces > 0 ? `, ${freePlaces} free` : ''})
                              </td>
                              <td colSpan={2} />
                              <td />
                              <td className="px-3 py-2.5 text-right text-[13px] font-bold text-pb tabular-nums">{fmt(totals.costPerPersonToCustomer, csym)}</td>
                              <td />
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Per-player price slider */}
                      {(() => {
                        const payingPl = calculatedTotals.payingPlayers;
                        const perPlayerFloor = payingPl > 0 ? calculatedTotals.totalCostToMST / payingPl : 0;
                        const perPlayerDefault = calculatedTotals.costPerPersonToCustomer;
                        const sliderMin = Math.floor(perPlayerFloor);
                        const sliderMax = Math.ceil(perPlayerDefault * 1.5);
                        const sliderVal = customerPriceOverride !== null
                          ? Math.round(customerPriceOverride / payingPl)
                          : Math.round(perPlayerDefault);
                        return (
                          <div className="mb-5 bg-[#F0F7FF] border border-pb/20 rounded-[10px] px-4 py-4">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-[12px] font-semibold text-pb">Adjust Price Per Player</p>
                              {customerPriceOverride !== null && (
                                <button type="button" onClick={() => setCustomerPriceOverride(null)}
                                  className="text-[11px] text-[#9CA3AF] hover:text-dr transition">Reset to calculated</button>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <input type="range" min={sliderMin} max={sliderMax} step={1} value={sliderVal}
                                onChange={(e) => setCustomerPriceOverride(Number(e.target.value) * payingPl)}
                                className="flex-1 accent-pb" />
                              <div className="flex items-center border border-[#E5E7EB] rounded-[8px] overflow-hidden bg-white">
                                <span className="px-2 py-1.5 text-[13px] text-[#6B7280] bg-surface border-r border-[#E5E7EB]">{csym}</span>
                                <input type="number" step="1" min={sliderMin} value={sliderVal}
                                  onChange={(e) => setCustomerPriceOverride(Number(e.target.value) * payingPl)}
                                  className="w-24 px-2 py-1.5 text-[13px] font-semibold text-pb focus:outline-none" />
                              </div>
                            </div>
                            <p className="text-[11px] text-[#9CA3AF] mt-1.5">Floor: {fmt(Math.ceil(perPlayerFloor), csym)}/player (MST cost). Adjusts total and profit in real time.</p>
                          </div>
                        );
                      })()}

                      {/* Summary tiles */}
                      {(() => {
                        const grossProfit = totals.totalCostToCustomer - calculatedTotals.totalCostToMST;
                        const postVatProfit = grossProfit * 0.79;
                        const marginPct = calculatedTotals.totalCostToCustomer > 0
                          ? ((grossProfit / totals.totalCostToCustomer) * 100).toFixed(1)
                          : '0.0';
                        const hasPriceAdjust = customerPriceOverride !== null;
                        return (
                          <div className="grid grid-cols-2 1080:grid-cols-5 gap-3 mb-5">
                            {[
                              { label: 'MST cost', value: fmt(calculatedTotals.totalCostToMST, csym), color: '#C0392B', bg: '#FBEAE8', border: 'rgba(192,57,43,.2)' },
                              { label: 'MST cost / traveller', value: fmt(payingPlayers > 0 ? calculatedTotals.totalCostToMST / payingPlayers : 0, csym), sub: 'per paying traveller', color: '#C0392B', bg: '#FBEAE8', border: 'rgba(192,57,43,.2)' },
                              { label: 'Customer price', value: fmt(totals.totalCostToCustomer, csym), sub: hasPriceAdjust ? 'Adjusted' : null, color: '#1A4A8A', bg: '#EBF2FC', border: 'rgba(26,74,138,.2)' },
                              { label: 'Per person', value: fmt(totals.costPerPersonToCustomer, csym), sub: `${payingPlayers} paying${freePlaces > 0 ? `, ${freePlaces} free` : ''}`, color: '#0D2240', bg: '#F9FAFB', border: '#E5E7EB' },
                              { label: 'Post-VAT profit', value: fmt(postVatProfit, csym), sub: `${marginPct}% margin · after 21% VAT`, color: postVatProfit >= 0 ? '#1A7A4A' : '#C0392B', bg: postVatProfit >= 0 ? '#D1FAE5' : '#FBEAE8', border: postVatProfit >= 0 ? 'rgba(26,122,74,.2)' : 'rgba(192,57,43,.2)' },
                            ].map((tile) => (
                              <div key={tile.label} className="rounded-[11px] p-[14px]" style={{ background: tile.bg, border: `1px solid ${tile.border}` }}>
                                <p className="text-[11.5px] font-semibold text-[#6B7280] mb-[6px]">{tile.label}</p>
                                <p className="text-[19px] font-extrabold tabular-nums leading-none" style={{ color: tile.color }}>{tile.value}</p>
                                {tile.sub && <p className="text-[11px] text-[#9CA3AF] mt-1">{tile.sub}</p>}
                              </div>
                            ))}
                          </div>
                        );
                      })()}

                      {/* Add-on annotations */}
                      {annotatableAddons.length > 0 && (
                        <div className="mb-5 space-y-3 border-t border-[#F3F4F6] pt-4">
                          <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">
                            Add-on Details <span className="font-normal">(shown to customer on quote letter)</span>
                          </p>
                          {annotatableAddons.map((addon) => {
                            const field = ANNOTATION_FIELDS[addon.categoryName];
                            return (
                              <div key={addon.categoryName}>
                                <label className="block text-[13px] font-medium text-[#374151] mb-1">{field.label}</label>
                                <input type="text"
                                  value={annotations[addon.categoryName] || ''}
                                  onChange={(e) => setAnnotations((prev) => ({ ...prev, [addon.categoryName]: e.target.value }))}
                                  placeholder={field.placeholder}
                                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="flex gap-3 items-center flex-wrap">
                        <button onClick={saveQuote} disabled={savingQuote}
                          className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
                          {savingQuote ? 'Saving…' : 'Save Quote'}
                        </button>
                        <button type="button" onClick={handleSaveQuoteAsTemplate}
                          disabled={!selectedPriceCodeId || costSheet.length === 0 || savingQuoteTemplate}
                          title="Save this cost sheet so it can be loaded onto another tour"
                          className="px-[18px] py-[9px] text-[13px] font-semibold text-pb border border-pb hover:bg-[#F0F7FF] rounded-[8px] transition disabled:opacity-40">
                          {savingQuoteTemplate ? 'Saving…' : 'Save as Template'}
                        </button>
                        <button type="button"
                          onClick={() => { setShowCrestModal(true); setQuoteLetterError(''); setQuoteLetterSent(false); }}
                          disabled={!activeQuoteId}
                          className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-[#1A7A4A] hover:bg-[#155e38] rounded-[8px] transition disabled:opacity-40"
                          title={!activeQuoteId ? 'Save this quote first' : 'Send to Michael for approval before it reaches the customer'}>
                          Send for approval
                        </button>
                        <button type="button" onClick={() => handlePreviewQuotePdf('quote')}
                          disabled={!request.quoteDetails || !!previewingQuotePdf}
                          className="px-[14px] py-[9px] text-[13px] font-semibold text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB] rounded-[8px] transition disabled:opacity-40"
                          title="Open the quote letter PDF in a new tab — nothing is sent">
                          {previewingQuotePdf === 'quote' ? 'Building…' : 'Preview quote letter'}
                        </button>
                        {usesParentLetters(request) && (
                          <button type="button" onClick={() => handlePreviewQuotePdf('confirmation')}
                            disabled={!request.quoteDetails || !!previewingQuotePdf}
                            className="px-[14px] py-[9px] text-[13px] font-semibold text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB] rounded-[8px] transition disabled:opacity-40"
                            title="Open the confirmation letter PDF (sent automatically once the deposit is paid) — nothing is sent">
                            {previewingQuotePdf === 'confirmation' ? 'Building…' : 'Preview confirmation letter'}
                          </button>
                        )}
                        {quotes.find((q) => q.id === activeQuoteId)?.status === 'sent' && (
                          <button type="button" onClick={handleResendToCustomer} disabled={sendingQuoteLetter}
                            className="px-[18px] py-[9px] text-[13px] font-semibold text-pb border border-pb hover:bg-[#F0F7FF] rounded-[8px] transition disabled:opacity-40"
                            title="Already approved — resend the same quote straight to the customer, no approval needed">
                            {sendingQuoteLetter ? 'Sending…' : 'Resend to customer'}
                          </button>
                        )}
                        {quoteLetterSent && (
                          <span className="text-[12px] text-[#1A7A4A] font-semibold">Sent!</span>
                        )}
                        {quoteError || (quoteLetterError && !showCrestModal)
                          ? <p className="text-[12px] text-dr">{quoteError || quoteLetterError}</p>
                          : activeQuote?.status === 'pending_approval'
                          ? <p className="text-[12px] text-pb font-semibold">
                              Sent for approval{activeQuote.pendingApprovalAt?.toDate ? ` on ${activeQuote.pendingApprovalAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''} — awaiting Michael's approval. The customer gets it once he approves.
                            </p>
                          : <p className="text-[12px] text-[#9CA3AF]">
                              {request.crmOrigin
                                ? 'Sends to Michael for approval first — separate from the CRM pipeline\'s own automated emails.'
                                : 'Sends to Michael for approval — the customer only gets it once he approves.'}
                            </p>
                        }
                      </div>
                    </>
                  );
                })()}

                {/* Saved quote summary when no price code selected */}
                {!selectedPriceCodeId && request.quoteDetails && (
                  <div className="bg-surface border border-[#E5E7EB] rounded-[8px] p-4">
                    <p className="text-[11px] text-[#6B7280] uppercase tracking-[0.9px] font-semibold mb-3">Saved Quote — {request.priceCodeName}</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <p className="text-[11px] text-dr mb-1">Total Cost to MST</p>
                        <p className="font-bold text-dr">{fmt(request.quoteDetails.totalCostToMST, sym)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[11px] text-pb mb-1">Total to Customer</p>
                        <p className="font-bold text-pb">{fmt(request.quoteDetails.totalCostToCustomer, sym)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[11px] text-[#6B7280] mb-1">Per Paying Guest</p>
                        <p className="font-bold text-[#111827]">{fmt(request.quoteDetails.costPerPersonToCustomer, sym)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Invoices */}
              <InvoicePanel requestId={id} request={request} brand={request.brand} />
            </div>
          )}

          {/* ── Ops tab ─────────────────────────────────────────────────── */}
          {activeTab === 'ops' && (
            <div className="space-y-4">
              <TaskChecklist requestId={id} />

              {/* Tour Ops Checklist */}
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
                <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
                  <div>
                    <h3 className="text-[14.5px] font-bold text-[#111827]">Tour Ops Checklist</h3>
                    <p className="text-[12px] text-[#6B7280] mt-0.5">Set quantities, then push to the tour leader</p>
                  </div>
                  <button
                    onClick={async () => {
                      setPushingChecklist(true);
                      setChecklistPushed(false);
                      try {
                        await setDoc(doc(db, 'requests', id, 'tourChecklist', 'data'), {
                          sections: checklistSections.map((sec) => ({
                            title: sec.title,
                            items: sec.items.map((it) => ({
                              id: it.id,
                              name: it.name,
                              adminQty: it.adminQty || '',
                              tourLeaderAcknowledged: false,
                            })),
                          })),
                          confirmations: DEFAULT_CONFIRMATIONS.map((c) => ({ ...c, tourLeaderAcknowledged: false })),
                          pushedAt: serverTimestamp(),
                          pushedBy: request?.assignedLeaderId || '',
                        });
                        setChecklistPushed(true);
                      } finally {
                        setPushingChecklist(false);
                      }
                    }}
                    disabled={pushingChecklist}
                    className="flex items-center gap-1.5 px-[14px] py-[8px] bg-navy hover:opacity-90 text-white text-[12.5px] font-bold rounded-[9px] transition disabled:opacity-60 flex-shrink-0"
                  >
                    {pushingChecklist ? 'Pushing…' : 'Push to Leader'}
                  </button>
                </div>

                {checklistPushed && (
                  <div className="mx-[18px] mt-[14px] px-4 py-3 bg-green-light border border-sg/25 rounded-[9px] flex items-center gap-2">
                    <svg className="w-4 h-4 text-sg flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-[13px] text-sg font-semibold">Checklist pushed to tour leader.</p>
                  </div>
                )}

                <div className="divide-y divide-[#F1F3F6]">
                  {checklistSections.map((sec, si) => (
                    <div key={si}>
                      <div className="px-[18px] py-[8px] bg-surface">
                        <p className="text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.8px]">{sec.title}</p>
                      </div>
                      <div className="divide-y divide-[#F9FAFB]">
                        {sec.items.map((item, ii) => (
                          <div key={item.id} className="flex items-center justify-between px-[18px] py-[10px] gap-4 hover:bg-surface transition-colors">
                            <p className="text-[13px] text-[#374151] flex-1">{item.name}</p>
                            <input
                              type="number"
                              min="0"
                              value={item.adminQty}
                              onChange={(e) => {
                                setChecklistSections((prev) => prev.map((s, sIdx) =>
                                  sIdx !== si ? s : {
                                    ...s,
                                    items: s.items.map((it, iIdx) =>
                                      iIdx !== ii ? it : { ...it, adminQty: e.target.value }
                                    ),
                                  }
                                ));
                                setChecklistPushed(false);
                              }}
                              placeholder="Qty"
                              className="w-[64px] border border-[#E5E7EB] rounded-[7px] px-2 py-1.5 text-[13px] text-right focus:outline-none focus:ring-2 focus:ring-pb"
                            />
                          </div>
                        ))}
                        {/* Add custom item row */}
                        <div className="flex items-center gap-2 px-[18px] py-[10px] border-t border-[#F1F3F6]">
                          <input
                            type="text"
                            placeholder="Add custom item…"
                            value={sec._newItemName || ''}
                            onChange={(e) => {
                              setChecklistSections((prev) => prev.map((s, sIdx) =>
                                sIdx !== si ? s : { ...s, _newItemName: e.target.value }
                              ));
                            }}
                            className="flex-1 border border-[#E5E7EB] rounded-[7px] px-3 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const name = sec._newItemName?.trim();
                              if (!name) return;
                              setChecklistSections((prev) => prev.map((s, sIdx) =>
                                sIdx !== si ? s : {
                                  ...s,
                                  _newItemName: '',
                                  items: [...s.items, { id: `custom_${Date.now()}`, name, adminQty: '' }],
                                }
                              ));
                              setChecklistPushed(false);
                            }}
                            className="px-3 py-1.5 bg-navy hover:opacity-90 text-white text-[12px] font-bold rounded-[7px] transition"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <SupplierAssignments requestId={id} request={request} />
              <DocumentUpload requestId={id} />
              <SurveyPanel requestId={id} request={request} />
            </div>
          )}

          {/* ── Itinerary tab ───────────────────────────────────────────── */}
          {activeTab === 'itinerary' && (
            <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
              <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
                <div>
                  <h3 className="text-[14.5px] font-bold text-[#111827]">{tr('tour.secItinerary')}</h3>
                  <p className="text-[12px] text-[#6B7280] mt-0.5">{request.destination}</p>
                </div>
              </div>
              <div className="p-[18px]">
                <ItineraryBuilder requestId={id} request={request} />
              </div>
            </div>
          )}

          {/* ── Rooming tab ──────────────────────────────────────────────── */}
          {activeTab === 'rooming' && (
            <StaffRoomingTab requestId={id} request={request} />
          )}

          {/* ── Group Flights tab (Mic Cup, Long Haul) ───────────────────── */}
          {activeTab === 'flights' && showGroupFlights && (
            <GroupFlightsTab requestId={id} request={request} />
          )}

          {/* ── Teams tab ────────────────────────────────────────────────── */}
          {activeTab === 'teams' && (
            <TeamsTab requestId={id} request={request} mode="staff" />
          )}

          {/* ── Registers tab ────────────────────────────────────────────── */}
          {activeTab === 'registers' && (() => {
            function fmtTs(ts) {
              if (!ts) return '—';
              const d = ts.toDate ? ts.toDate() : new Date(ts);
              return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            }
            return (
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
                <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
                  <div>
                    <h3 className="text-[14.5px] font-bold text-[#111827]">Tour Leader Registers</h3>
                    <p className="text-[12px] text-[#6B7280] mt-0.5">{registers.length} submitted</p>
                  </div>
                </div>

                {registers.length === 0 ? (
                  <div className="text-center px-[18px] py-12 text-[#9CA3AF]">
                    <p className="text-[13px]">No registers submitted yet.</p>
                    <p className="text-[12px] mt-1">Registers appear here once the tour leader takes attendance.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#F1F3F6]">
                    {registers.map((reg) => {
                      const full = reg.totalPresent === reg.totalParticipants;
                      return (
                      <div key={reg.id} className="px-[18px] py-[14px]">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-bold text-[#111827]">{reg.description}</p>
                            <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">
                              {fmtTs(reg.takenAt)} · {reg.takenBy || 'Tour leader'}
                            </p>
                          </div>
                          <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold ${
                            full ? 'bg-green-light text-sg' : 'bg-amber-light text-[#92400E]'
                          }`}>
                            <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${full ? 'bg-sg' : 'bg-ba'}`} />
                            {reg.totalPresent ?? '?'}/{reg.totalParticipants ?? '?'} present
                          </span>
                        </div>
                        {(() => {
                          const absentees = reg.attendanceDetails
                            ? reg.attendanceDetails.filter((p) => !p.present)
                            : [];
                          if (absentees.length === 0) return null;
                          return (
                            <div className="px-4 pb-3 border-t border-[#F1F3F6] pt-2.5">
                              <p className="text-[10px] font-extrabold text-dr uppercase tracking-[0.6px] mb-[7px]">Absent ({absentees.length})</p>
                              <div className="flex flex-wrap gap-[6px]">
                                {absentees.map((p) => (
                                  <span key={p.id} className="text-[11.5px] bg-red-light text-dr border border-[#F3C6C1] px-[9px] py-[2px] rounded-[20px]">
                                    {p.name}{p.travellerType ? ` · ${p.travellerType}` : ''}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Expenses tab ─────────────────────────────────────────────── */}
          {activeTab === 'expenses' && (() => {
            const tourCurrency = request.currency || 'GBP';
            const hasQuote = !!request.quoteDetails;
            const tourComplete = ['completed', 'cancelled'].includes(request.status);

            // GBP-equivalent totals — use locked gbpEquivalent where available, fall back to amount for legacy records
            const quoteCostGBP = Number(request.quoteDetails?.totalCostToMSTGBP ?? request.quoteDetails?.totalCostToMST) || 0;
            const quoteOriginal = Number(request.quoteDetails?.totalCostToMST) || 0;
            const isNonGBP = tourCurrency !== 'GBP';

            const expWithGBP = expenses.map((e) => ({ ...e, _gbp: e.gbpEquivalent != null ? e.gbpEquivalent : Number(e.amount) || 0 }));
            const paidGBP   = expWithGBP.filter((e) => (e.paymentStatus || 'paid') === 'paid').reduce((s, e) => s + e._gbp, 0);
            const toPayGBP  = expWithGBP.filter((e) => e.paymentStatus === 'to_pay').reduce((s, e) => s + e._gbp, 0);
            const remainingGBP = quoteCostGBP - paidGBP;
            const remainingInclGBP = quoteCostGBP - paidGBP - toPayGBP;

            // Collect unique rate weeks used across expenses for footnote
            const rateWeeks = [...new Set(expenses.filter((e) => e.rateWeek).map((e) => e.rateWeek))].sort();
            const fmtWeek = (w) => { const [y, m, d] = w.split('-'); return `${d}/${m}/${y}`; };
            const hasConversions = expenses.some((e) => e.gbpEquivalent != null && (e.currency || 'GBP') !== 'GBP');

            return (
            <div className="space-y-5">

              {/* ── Tour Budget panel ── */}
              {hasQuote && (() => {
                const spentPct = quoteCostGBP > 0 ? Math.max(0, Math.min(100, Math.round((paidGBP / quoteCostGBP) * 100))) : 0;
                return (
                <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
                  <div className="flex items-start justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
                    <div>
                      <h3 className="text-[14.5px] font-bold text-[#111827]">Tour Budget</h3>
                      <p className="text-[12px] text-[#6B7280] mt-0.5">
                        {hasConversions ? 'Expenses converted to GBP · MST quote cost vs recorded expenses' : 'MST quote cost vs recorded expenses'}
                      </p>
                    </div>
                  </div>
                  <div className="px-[18px] py-[16px] space-y-[9px]">
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-[#6B7280]">MST quote cost</span>
                      <div className="text-right">
                        <span className="font-semibold text-[#111827] tabular-nums">{fmtExpAmt(quoteCostGBP, 'GBP')}</span>
                        {isNonGBP && <span className="ml-2 text-[11px] text-[#9CA3AF]">({fmtExpAmt(quoteOriginal, tourCurrency)})</span>}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-[#6B7280]">Less: paid expenses</span>
                      <span className="font-semibold text-dr tabular-nums">− {fmtExpAmt(paidGBP, 'GBP')}</span>
                    </div>
                    {toPayGBP > 0 && (
                      <div className="flex items-center justify-between text-[13px]">
                        <span className="text-[#6B7280]">Less: invoices to pay</span>
                        <span className="font-semibold tabular-nums" style={{ color: '#E8A020' }}>− {fmtExpAmt(toPayGBP, 'GBP')}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-[13px] pt-[10px] border-t border-[#F1F3F6]">
                      <span className="font-bold text-[#111827]">Remaining</span>
                      <span className={`font-extrabold text-[16px] tabular-nums ${remainingGBP >= 0 ? 'text-sg' : 'text-dr'}`}>
                        {fmtExpAmt(toPayGBP > 0 ? remainingInclGBP : remainingGBP, 'GBP')}
                      </span>
                    </div>
                    {/* Spend bar */}
                    <div className="pt-[6px]">
                      <div className="flex justify-between text-[11px] text-[#9CA3AF] mb-[5px]">
                        <span>Spent {spentPct}%</span>
                        <span>Budget {fmtExpAmt(quoteCostGBP, 'GBP')}</span>
                      </div>
                      <div className="h-[9px] bg-surface border border-[#E5E7EB] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${spentPct}%`, background: spentPct > 90 ? '#C0392B' : '#1A4A8A' }} />
                      </div>
                    </div>
                    {/* Match ticket */}
                    {request.matchTicket?.status === 'confirmed' && (() => {
                      const mt = request.matchTicket;
                      // No count entered → everyone attending needs one: the whole travelling
                      // group plus MST staff (same default as InvoicePanel).
                      const numTickets = Number(mt.numberOfTickets) || (totalGroupCount + (Number(request.staffCount) || 1));
                      const mtCostToMSTEUR = (Number(mt.costToMSTPerTicketEUR) || 0) * numTickets;
                      const mtCostToMSTGBP = toGBP(mtCostToMSTEUR, 'EUR', exchangeRates || {});
                      const mtChargeToCustomer = (Number(mt.chargeToCustomerPerTicket) || 0) * numTickets;
                      const mtProfit = mtChargeToCustomer - mtCostToMSTGBP;
                      return (
                        <div className="pt-[10px] border-t border-[#F1F3F6] space-y-[7px]">
                          <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px]">{request.market === 'spain_to_uk' ? 'Premier League' : 'La Liga'} Match Ticket ({numTickets} tickets)</p>
                          <div className="flex items-center justify-between text-[13px]"><span className="text-[#6B7280]">Cost to MST</span><span className="font-semibold text-[#111827] tabular-nums">{fmtExpAmt(mtCostToMSTEUR, 'EUR')}{exchangeRates && <span className="ml-2 text-[11px] text-[#9CA3AF]">({fmtExpAmt(mtCostToMSTGBP, 'GBP')})</span>}</span></div>
                          <div className="flex items-center justify-between text-[13px]"><span className="text-[#6B7280]">Charge to customer</span><span className="font-semibold text-[#111827] tabular-nums">{fmtExpAmt(mtChargeToCustomer, tourCurrency)}</span></div>
                          <div className="flex items-center justify-between text-[13px] pt-[7px] border-t border-[#F1F3F6]"><span className="text-[#6B7280]">Match ticket profit</span><span className={`font-bold tabular-nums ${mtProfit >= 0 ? 'text-sg' : 'text-dr'}`}>{fmtExpAmt(mtProfit, 'GBP')}</span></div>
                        </div>
                      );
                    })()}
                    {rateWeeks.length > 0 && <p className="text-[11px] text-[#9CA3AF]">Rates w/c {rateWeeks.map(fmtWeek).join(', ')}</p>}
                    {!tourComplete && (
                      <div className="flex items-start gap-2.5 bg-amber-light border border-ba/25 rounded-[9px] px-4 py-3">
                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#92400E' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                        <p className="text-[12px]" style={{ color: '#92400E' }}>Additional costs may not yet be logged. Remaining figures may not reflect the full picture until the tour is complete.</p>
                      </div>
                    )}
                  </div>
                </div>
                );
              })()}

              {/* ── Expense list ── */}
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
                <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
                  <div>
                    <h3 className="text-[14.5px] font-bold text-[#111827]">Recorded Expenses</h3>
                    <p className="text-[12px] text-[#6B7280] mt-0.5">{expenses.length} {expenses.length === 1 ? 'entry' : 'entries'}</p>
                  </div>
                  {!showAddExpense && !showInvoiceUpload && (
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setShowInvoiceUpload(true)}
                        className="text-[12.5px] font-bold text-pb hover:text-navy transition"
                      >
                        + Upload invoice
                      </button>
                      <button
                        onClick={() => { setShowAddExpense(true); setAddExpenseError(''); }}
                        className="text-[12.5px] font-bold text-pb hover:text-navy transition"
                      >
                        + Log expense
                      </button>
                    </div>
                  )}
                </div>

                {showInvoiceUpload && (
                  <div className="px-[18px] py-[16px] border-b border-[#F1F3F6]">
                    <InvoiceUpload
                      tour={{
                        requestId: id,
                        requestSummary: request?.internalRef || request?.tourName || `${request?.sport} — ${request?.destination}`,
                      }}
                      extraFields={{ submittedBy: currentUser?.email, submittedRole: 'staff' }}
                      onClose={() => setShowInvoiceUpload(false)}
                    />
                  </div>
                )}

                {showAddExpense && (() => {
                  function handleExpScanClick() {
                    if (isMobileDevice()) {
                      setExpScanMode('mobile');
                      setExpMobileScanPhase('idle');
                      setExpMobileScanError('');
                      expScanFileRef.current?.click();
                    } else {
                      setExpScanMode('qr');
                    }
                  }
                  async function handleExpScanFile(e) {
                    const file = e.target.files?.[0];
                    if (!file) { setExpScanMode(null); return; }
                    setExpMobileScanPhase('uploading');
                    setExpMobileScanError('');
                    try {
                      const path = `receipts/${currentUser.uid}/staff-${Date.now()}`;
                      const sref = storageRef(storage, path);
                      await uploadBytes(sref, file);
                      let dlUrl = null;
                      try { dlUrl = await getDownloadURL(sref); } catch {}
                      setExpMobileScanPhase('scanning');
                      const scanFn = httpsCallable(functions, 'scanReceipt');
                      const result = await scanFn({ storagePath: path });
                      handleExpScanned(result.data, dlUrl);
                    } catch (err) {
                      setExpMobileScanError(err?.message || 'Something went wrong scanning the receipt.');
                      setExpMobileScanPhase('error');
                    }
                  }
                  function handleExpScanned(data, dlUrl) {
                    data = data || {};
                    setAddExpenseForm((p) => ({
                      ...p,
                      date:        data.date        || p.date,
                      amount:      data.amount      != null ? String(data.amount) : p.amount,
                      currency:    data.currency    || p.currency,
                      category:    data.category    || p.category,
                      description: data.description || p.description,
                    }));
                    if (dlUrl) setAddExpenseReceiptUrl(dlUrl);
                    setExpScanMode(null);
                    setExpMobileScanPhase('idle');
                  }
                  return (
                  <div className="px-[18px] py-[16px] border-b border-[#F1F3F6] bg-surface">
                    <input ref={expScanFileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleExpScanFile} />
                    {expScanMode === 'qr' && <QRScanPanel currentUser={currentUser} onScanned={handleExpScanned} onCancel={() => setExpScanMode(null)} />}
                    {expScanMode === 'mobile' && (expMobileScanPhase === 'uploading' || expMobileScanPhase === 'scanning') && (
                      <div className="border border-[#E5E7EB] rounded-[10px] p-4 mb-4 bg-white flex items-center gap-3">
                        <svg className="w-5 h-5 animate-spin text-pb flex-shrink-0" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                        <span className="text-[13px] text-[#374151]">{expMobileScanPhase === 'uploading' ? 'Uploading photo…' : 'Reading receipt with AI…'}</span>
                      </div>
                    )}
                    {expScanMode === 'mobile' && expMobileScanPhase === 'error' && (
                      <div className="border border-[#E5E7EB] rounded-[10px] p-4 mb-4 bg-white">
                        <p className="text-[13px] text-dr mb-2">{expMobileScanError || 'Could not read the receipt.'}</p>
                        <button onClick={() => { setExpScanMode(null); setExpMobileScanPhase('idle'); }} className="text-[13px] text-pb hover:text-navy font-medium">Dismiss</button>
                      </div>
                    )}
                    <p className="text-[12px] font-bold text-[#374151] mb-3">Log an expense</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Date *</label>
                        <input type="date" value={addExpenseForm.date} onChange={(e) => setAddExpenseForm((p) => ({ ...p, date: e.target.value }))}
                          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Amount *</label>
                        <div className="flex gap-1.5">
                          <select value={addExpenseForm.currency} onChange={(e) => setAddExpenseForm((p) => ({ ...p, currency: e.target.value }))}
                            className="border border-[#E5E7EB] rounded-[8px] px-2 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
                            <option>GBP</option><option>EUR</option><option>USD</option>
                          </select>
                          <input type="number" step="0.01" min="0" value={addExpenseForm.amount} onChange={(e) => setAddExpenseForm((p) => ({ ...p, amount: e.target.value }))}
                            placeholder="0.00" className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Category *</label>
                        <select value={addExpenseForm.category} onChange={(e) => setAddExpenseForm((p) => ({ ...p, category: e.target.value }))}
                          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
                          {expenseCategories.map((c) => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Description *</label>
                      <input type="text" value={addExpenseForm.description} onChange={(e) => setAddExpenseForm((p) => ({ ...p, description: e.target.value }))}
                        placeholder="e.g. Hotel — Alicante 3 nights"
                        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <label className="text-[11px] font-medium text-[#6B7280]">Payment status:</label>
                      {['paid', 'to_pay'].map((s) => (
                        <button key={s} type="button"
                          onClick={() => setAddExpenseForm((p) => ({ ...p, paymentStatus: s }))}
                          className={`px-3 py-1 text-[11px] font-bold rounded-[6px] transition ${addExpenseForm.paymentStatus === s ? (s === 'paid' ? 'bg-green-light text-sg' : 'bg-amber-light text-[#92400E]') : 'bg-white border border-[#E5E7EB] text-[#9CA3AF]'}`}>
                          {s === 'paid' ? 'Paid' : 'To Pay'}
                        </button>
                      ))}
                    </div>
                    {addExpenseError && <p className="text-[12px] text-dr mb-2">{addExpenseError}</p>}
                    <div className="flex gap-2">
                      <button
                        disabled={addExpenseSaving}
                        onClick={async () => {
                          setAddExpenseError('');
                          if (!addExpenseForm.date || !addExpenseForm.amount || !addExpenseForm.description.trim()) {
                            setAddExpenseError('Date, amount and description are required.'); return;
                          }
                          setAddExpenseSaving(true);
                          try {
                            await addDoc(collection(db, 'expenses'), {
                              requestId: id,
                              requestSummary: request?.internalRef || request?.tourName || `${request?.sport} — ${request?.destination}`,
                              date:          addExpenseForm.date,
                              amount:        Number(addExpenseForm.amount),
                              currency:      addExpenseForm.currency,
                              category:      addExpenseForm.category,
                              description:   addExpenseForm.description.trim(),
                              paymentStatus: addExpenseForm.paymentStatus,
                              receiptUrl:    addExpenseReceiptUrl || null,
                              submittedBy:   currentUser?.email,
                              submittedRole: 'staff',
                              createdAt:     serverTimestamp(),
                            });
                            setShowAddExpense(false);
                            setAddExpenseForm({ date: new Date().toISOString().split('T')[0], category: 'Accommodation', description: '', amount: '', currency: 'GBP', paymentStatus: 'paid' });
                            setAddExpenseReceiptUrl('');
                            setExpScanMode(null);
                          } catch (err) {
                            console.error(err);
                            setAddExpenseError('Failed to save. Please try again.');
                          } finally { setAddExpenseSaving(false); }
                        }}
                        className="px-4 py-2 text-[12px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
                      >
                        {addExpenseSaving ? 'Saving…' : 'Save expense'}
                      </button>
                      <button type="button" onClick={handleExpScanClick}
                        className="px-4 py-2 text-[12px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 rounded-[8px] transition flex items-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {addExpenseReceiptUrl ? 'Receipt attached ✓' : 'Scan receipt'}
                      </button>
                      <button type="button" onClick={() => { setShowAddExpense(false); setAddExpenseError(''); setAddExpenseReceiptUrl(''); setExpScanMode(null); }}
                        className="px-4 py-2 text-[12px] text-[#6B7280] hover:text-[#374151]">Cancel</button>
                    </div>
                  </div>
                  );
                })()}

                {expenses.length === 0 && !showAddExpense && !showInvoiceUpload ? (
                  <div className="px-[18px] py-12 text-center">
                    <p className="text-[13px] text-[#9CA3AF] mb-2">No expenses logged for this tour yet.</p>
                    <button onClick={() => setShowAddExpense(true)} className="text-[13px] font-semibold text-pb hover:text-navy transition">
                      + Log the first expense
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-[#F1F3F6]">
                    {expenses.map((exp) => {
                      const isPaid = (exp.paymentStatus || 'paid') === 'paid';
                      const linkedAssignment = exp.linkedSupplierAssignmentId
                        ? tourAssignments.find((a) => a.id === exp.linkedSupplierAssignmentId)
                        : null;
                      const unlinkableAssignments = tourAssignments.filter(
                        (a) => !expenses.some((e) => e.id !== exp.id && e.linkedSupplierAssignmentId === a.id)
                      );
                      return (
                        <div key={exp.id} className="flex items-center gap-3 px-[18px] py-[13px] hover:bg-surface transition-colors">
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-[#111827]">{exp.description || '—'}</p>
                            <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">{exp.category || '—'} · {fmtExpDate(exp.date)}</p>
                            {/* Linked invoice badge / link / create */}
                            {linkedAssignment ? (
                              <span className="inline-flex items-center gap-1 mt-1 text-[10.5px] font-semibold text-[#1A4A8A] bg-[#EFF6FF] border border-[#93C5FD] px-2 py-0.5 rounded-full">
                                📎 {linkedAssignment.supplierName || 'Supplier invoice'}
                                {linkedAssignment.invoiceUrl && (
                                  <a href={linkedAssignment.invoiceUrl} target="_blank" rel="noopener noreferrer"
                                    className="text-[#93C5FD] hover:text-pb ml-0.5" title="View invoice">↗</a>
                                )}
                                <button
                                  onClick={() => updateDoc(doc(db, 'expenses', exp.id), { linkedSupplierAssignmentId: null })}
                                  className="text-[#93C5FD] hover:text-dr leading-none ml-0.5"
                                  title="Remove link"
                                >×</button>
                              </span>
                            ) : createInvoiceForExpId === exp.id ? (
                              <div className="mt-1 bg-[#F9FAFB] border border-[#E5E7EB] rounded-[8px] p-2.5 space-y-2 w-[280px]">
                                <p className="text-[11px] font-bold text-[#374151]">Create &amp; link supplier invoice</p>
                                <input
                                  value={createInvoiceForm.supplierName}
                                  onChange={(e) => setCreateInvoiceForm((p) => ({ ...p, supplierName: e.target.value }))}
                                  placeholder="Supplier name *"
                                  className="w-full text-[11px] border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-pb"
                                />
                                <input
                                  value={createInvoiceForm.supplierEmail}
                                  onChange={(e) => setCreateInvoiceForm((p) => ({ ...p, supplierEmail: e.target.value }))}
                                  placeholder="Supplier email (optional)"
                                  className="w-full text-[11px] border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-pb"
                                />
                                <input
                                  value={createInvoiceForm.serviceType}
                                  onChange={(e) => setCreateInvoiceForm((p) => ({ ...p, serviceType: e.target.value }))}
                                  placeholder="Service type *"
                                  className="w-full text-[11px] border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-pb"
                                />
                                <input ref={createInvoiceFileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden"
                                  onChange={(e) => setCreateInvoiceFile(e.target.files?.[0] || null)} />
                                <button type="button" onClick={() => createInvoiceFileRef.current?.click()}
                                  className="w-full border border-dashed border-[#D1D5DB] rounded-[6px] px-2 py-1.5 text-[11px] text-[#6B7280] hover:border-pb hover:text-pb transition text-center">
                                  {createInvoiceFile ? `📎 ${createInvoiceFile.name}` : 'Attach invoice file (optional)'}
                                </button>
                                <div className="flex gap-1.5">
                                  <button
                                    disabled={createInvoiceSaving || !createInvoiceForm.supplierName.trim() || !createInvoiceForm.serviceType.trim()}
                                    onClick={async () => {
                                      setCreateInvoiceSaving(true);
                                      try {
                                        const assignment = await addDoc(collection(db, 'bookingSuppliers'), {
                                          requestId:     id,
                                          sport:         request.sport || '',
                                          destination:   request.destination || '',
                                          departureDate: request.departureDate || '',
                                          returnDate:    request.returnDate || '',
                                          supplierName:  createInvoiceForm.supplierName.trim(),
                                          supplierEmail: createInvoiceForm.supplierEmail.trim(),
                                          serviceType:   createInvoiceForm.serviceType.trim(),
                                          staffNotes:    '',
                                          status:        'confirmed',
                                          createdAt:     serverTimestamp(),
                                          uploadedByStaff: true,
                                          invoiceAmount:   exp.amount || null,
                                          invoiceCurrency: exp.currency || 'GBP',
                                        });
                                        if (createInvoiceFile) {
                                          const path = `invoices/${id}/${assignment.id}/${createInvoiceFile.name}`;
                                          const snap = await uploadBytes(storageRef(storage, path), createInvoiceFile);
                                          const url  = await getDownloadURL(snap.ref);
                                          await updateDoc(doc(db, 'bookingSuppliers', assignment.id), {
                                            invoiceUrl: url, invoiceFileName: createInvoiceFile.name, invoiceUploadedAt: serverTimestamp(),
                                          });
                                        }
                                        await updateDoc(doc(db, 'expenses', exp.id), { linkedSupplierAssignmentId: assignment.id });
                                        setCreateInvoiceForExpId(null);
                                        setCreateInvoiceForm({ supplierName: '', supplierEmail: '', serviceType: '' });
                                        setCreateInvoiceFile(null);
                                      } catch (err) {
                                        console.error(err);
                                        alert('Failed to create invoice. Please try again.');
                                      } finally { setCreateInvoiceSaving(false); }
                                    }}
                                    className="flex-1 text-[11px] font-bold text-white bg-pb hover:bg-navy rounded-[6px] py-1.5 transition disabled:opacity-50"
                                  >
                                    {createInvoiceSaving ? 'Saving…' : 'Create & link'}
                                  </button>
                                  <button type="button"
                                    onClick={() => { setCreateInvoiceForExpId(null); setCreateInvoiceFile(null); }}
                                    className="text-[11px] text-[#9CA3AF] hover:text-[#374151] px-1.5">✕</button>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-1 flex flex-col gap-0.5">
                                {unlinkableAssignments.length > 0 && (
                                  <select
                                    defaultValue=""
                                    onChange={(e) => {
                                      if (e.target.value) updateDoc(doc(db, 'expenses', exp.id), { linkedSupplierAssignmentId: e.target.value });
                                      e.target.value = '';
                                    }}
                                    className="text-[10.5px] text-[#9CA3AF] border-0 bg-transparent focus:outline-none cursor-pointer hover:text-pb"
                                  >
                                    <option value="">+ Link to supplier invoice…</option>
                                    {unlinkableAssignments.map((a) => (
                                      <option key={a.id} value={a.id}>{a.supplierName || a.id}{a.invoiceAmount ? ` (${a.invoiceCurrency || 'GBP'} ${a.invoiceAmount})` : ''}</option>
                                    ))}
                                  </select>
                                )}
                                <button
                                  onClick={() => {
                                    setCreateInvoiceForExpId(exp.id);
                                    setCreateInvoiceForm({ supplierName: exp.description || '', supplierEmail: '', serviceType: exp.category || '' });
                                    setCreateInvoiceFile(null);
                                  }}
                                  className="text-[10.5px] font-semibold text-pb hover:text-navy transition text-left"
                                >
                                  + Create &amp; link supplier invoice
                                </button>
                              </div>
                            )}
                          </div>
                          {exp.storedOnly ? (
                            <span className="inline-flex items-center px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold bg-[#F3F4F6] text-[#6B7280] flex-shrink-0">
                              Stored
                            </span>
                          ) : (
                            <button
                              onClick={() => updateDoc(doc(db, 'expenses', exp.id), {
                                paymentStatus: isPaid ? 'to_pay' : 'paid',
                              })}
                              className={`inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold transition flex-shrink-0 ${
                                isPaid ? 'bg-green-light text-sg hover:opacity-80' : 'bg-amber-light text-[#92400E] hover:opacity-80'
                              }`}
                            >
                              <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${isPaid ? 'bg-sg' : 'bg-ba'}`} />
                              {isPaid ? 'Paid' : 'To Pay'}
                            </button>
                          )}
                          {exp.receiptUrl ? (
                            <a href={exp.receiptUrl} target="_blank" rel="noopener noreferrer"
                              className="text-[12px] font-bold text-pb hover:text-navy transition flex-shrink-0">
                              {exp.invoiceFileName ? 'Invoice' : 'Receipt'}
                            </a>
                          ) : <span className="w-[46px]" />}
                          <span className="text-[13.5px] font-extrabold text-[#111827] tabular-nums flex-shrink-0 min-w-[80px] text-right">
                            {exp.storedOnly ? '—' : fmtExpAmt(exp.amount, exp.currency || 'GBP')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* ── Team Info tab ───────────────────────────────────────────── */}
          {activeTab === 'team_info' && (
            <div className="space-y-4">
              {/* Staff notes — attributed + timestamped, separate from Internal Notes */}
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
                <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
                  <h3 className="text-[14.5px] font-bold text-[#111827]">Notes</h3>
                  <span className="text-[12px] text-[#6B7280]">{teamNotes.length} note{teamNotes.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="px-[18px] py-[16px] space-y-3">
                  <div className="flex flex-col gap-2">
                    <textarea
                      value={newTeamNote}
                      onChange={(e) => setNewTeamNote(e.target.value)}
                      rows={2}
                      placeholder="Add a note about the team, coach or squad…"
                      className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-2 text-[13px] text-[#374151] focus:outline-none focus:ring-2 focus:ring-pb resize-y"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={handleAddTeamNote}
                        disabled={savingTeamNote || !newTeamNote.trim()}
                        className="px-[16px] py-[8px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60"
                      >
                        {savingTeamNote ? 'Adding…' : 'Add note'}
                      </button>
                    </div>
                  </div>

                  {teamNotes.length === 0 ? (
                    <p className="text-[13px] text-[#9CA3AF] text-center py-3">No notes yet.</p>
                  ) : (
                    <div className="divide-y divide-[#F1F3F6] border-t border-[#F1F3F6]">
                      {teamNotes.map((note) => (
                        <div key={note.id} className="py-3 group">
                          <p className="text-[13.5px] text-[#111827] whitespace-pre-wrap">{note.text}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <p className="text-[11.5px] text-[#9CA3AF]">
                              {note.authorName || note.authorEmail || 'Unknown'}
                              {note.createdAt?.toDate && ` · ${note.createdAt.toDate().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                            </p>
                            <button
                              onClick={() => handleDeleteTeamNote(note.id)}
                              className="ml-auto text-[11.5px] font-semibold text-[#9CA3AF] hover:text-dr opacity-0 group-hover:opacity-100 transition"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {teamInfoLoading ? (
                <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-10 text-center shadow-[0_1px_2px_rgba(16,24,40,.04)]">
                  <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
                </div>
              ) : !teamInfo ? (
                <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-10 text-center shadow-[0_1px_2px_rgba(16,24,40,.04)]">
                  <p className="text-[14px] font-bold text-[#111827] mb-2">No team info submitted yet</p>
                  <p className="text-[13px] text-[#6B7280]">
                    The coach will receive a link to the team info form once the deposit is marked as paid.
                    {request.teamInfoToken && (
                      <> The link has been sent — form URL:{' '}
                        <span className="font-mono text-pb text-[12px]">/team-info?token={request.teamInfoToken}</span>.
                      </>
                    )}
                  </p>
                </div>
              ) : editingTeamInfo ? (
                <TeamInfoEditPanel
                  requestId={id}
                  teamInfo={teamInfo}
                  onSave={(updated) => { setTeamInfo(updated); setEditingTeamInfo(false); }}
                  onCancel={() => setEditingTeamInfo(false)}
                />
              ) : (
                <>
                  <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
                    <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
                      <h3 className="text-[14.5px] font-bold text-[#111827]">Coach & Team Information</h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingTeamInfo(true)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-pb border border-pb/30 rounded-[8px] hover:bg-pb/5 transition"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>
                        <span className="inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold bg-green-light text-sg">
                          <span className="w-[5px] h-[5px] rounded-full bg-sg flex-shrink-0" />
                          Submitted{teamInfo.submittedAt ? ` ${teamInfo.submittedAt?.toDate?.()?.toLocaleDateString('en-GB') || ''}` : ''}
                        </span>
                      </div>
                    </div>
                    <div className="px-[18px] py-[16px]">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        {[
                          ['Squads', teamInfo.numSquads],
                          teamInfo.goalkeepersTotal != null ? ['Total Goalkeepers', teamInfo.goalkeepersTotal] : null,
                        ].filter(Boolean).map(([label, val]) => (
                          <div key={label}>
                            <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[3px]">{label}</p>
                            <p className="text-[13.5px] font-semibold text-[#111827]">{val ?? '—'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {(teamInfo.squads || []).map((sq, i) => (
                    <div key={i} className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
                      <div className="flex items-center justify-between px-[18px] py-[12px] border-b border-[#F1F3F6] bg-surface">
                        <div className="min-w-0">
                          <p className="text-[13px] font-bold text-[#111827]">{sq.label || `Squad ${i + 1}`}</p>
                          {squadAgeInfo(sq, request).label && (
                            <p className="text-[11.5px] text-[#6B7280]">{squadAgeInfo(sq, request).label}</p>
                          )}
                        </div>
                        {/* Fixture finder export */}
                        {(() => {
                          const lines = buildFixtureFinderText(sq, request);
                          return (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(lines);
                                alert('Fixture finder text copied to clipboard (in Spanish)!');
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-[#6B7280] border border-[#E5E7EB] rounded-[8px] hover:text-pb hover:border-pb transition"
                              title="Copy fixture finder text for WhatsApp / messaging"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                              Copy Fixture Finder
                            </button>
                          );
                        })()}
                      </div>
                      <div className="px-[18px] py-[16px]">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
                          {[['Gender', sq.gender], ['Year(s) of Birth', Array.isArray(sq.yearsOfBirth) && sq.yearsOfBirth.length ? sq.yearsOfBirth.join(', ') : sq.yearOfBirth], ['Players / Match', sq.playersPerMatch], ['Ability', sq.abilityLevel]].map(([label, val]) => (
                            <div key={label}>
                              <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[3px]">{label}</p>
                              <p className="text-[13px] font-semibold text-[#111827]">{val || '—'}</p>
                            </div>
                          ))}
                        </div>
                        <div className="space-y-5">
                          {[
                            { title: 'Home Kit', jersey: sq.homeJerseyColour, jersey2: sq.homeJerseyColour2, pattern: sq.homeJerseyPattern, shorts: sq.homeShortsColour, socks: sq.homeSocksColour },
                            ...(!sq.onlyOneKit ? [{ title: 'Away Kit', jersey: sq.awayJerseyColour, jersey2: sq.awayJerseyColour2, pattern: sq.awayJerseyPattern, shorts: sq.awayShortsColour, socks: sq.awaySocksColour }] : []),
                          ].map(({ title, jersey, jersey2, pattern, shorts, socks }, kitIdx) => (
                            <div key={title}>
                              <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-3">{title}</p>
                              <div className="flex items-start gap-5">
                                <KitModel
                                  uid={`kit-${i}-${kitIdx}`}
                                  jerseyColour={jersey}
                                  jerseyColour2={jersey2}
                                  jerseyPattern={pattern}
                                  shortsColour={shorts}
                                  socksColour={socks}
                                />
                                <div className="flex flex-wrap gap-4 items-end pt-1">
                                  {[
                                    ['Jersey', jersey],
                                    ...(jersey2 ? [['Jersey 2', jersey2]] : []),
                                    ['Shorts', shorts],
                                    ['Socks', socks],
                                  ].map(([label, val]) => (
                                    <div key={label} className="text-center">
                                      <div className="w-9 h-9 rounded-[8px] border-2 border-[#E5E7EB] mb-1"
                                        style={{ background: kitColourHex(val) || '#F3F4F6' }} />
                                      <p className="text-[11px] text-[#6B7280]">{label}</p>
                                      <p className="text-[11px] font-semibold text-[#111827]">{val || '—'}</p>
                                    </div>
                                  ))}
                                  {pattern && pattern !== 'plain' && (
                                    <p className="text-[12px] text-[#6B7280] self-end mb-1">Pattern: <span className="font-semibold text-[#374151] capitalize">{pattern}</span></p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {sq.onlyOneKit && (
                            <p className="text-[12px] text-[#9CA3AF]">One kit only</p>
                          )}
                        </div>

                        {/* Preferred fixture (fixture finder) */}
                        {sq.preferredFixture && (sq.preferredFixture.preferredDate || sq.preferredFixture.preferredTime || sq.preferredFixture.preferredLocation) && (
                          <div className="mt-4 pt-4 border-t border-[#F1F3F6]">
                            <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-2">Preferred Fixture (Fixture Finder)</p>
                            <div className="flex flex-wrap gap-4">
                              {sq.preferredFixture.preferredDate && <div><p className="text-[10px] text-[#9CA3AF] uppercase font-bold tracking-wide mb-0.5">Date</p><p className="text-[13px] font-semibold text-[#111827]">{new Date(sq.preferredFixture.preferredDate + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p></div>}
                              {sq.preferredFixture.preferredTime && <div><p className="text-[10px] text-[#9CA3AF] uppercase font-bold tracking-wide mb-0.5">Time</p><p className="text-[13px] font-semibold text-[#111827]">{sq.preferredFixture.preferredTime} KO</p></div>}
                              {sq.preferredFixture.preferredLocation && <div><p className="text-[10px] text-[#9CA3AF] uppercase font-bold tracking-wide mb-0.5">Location</p><p className="text-[13px] font-semibold text-[#111827]">{sq.preferredFixture.preferredLocation}</p></div>}
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  ))}
                </>
              )}

              {!editingTeamInfo && (
                <TeamFixturesSection requestId={id} request={request} canEdit />
              )}
            </div>
          )}

          {/* ── Social Media tab ────────────────────────────────────────── */}
          {activeTab === 'social' && (
            <div className="space-y-4">
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-5">
                <h3 className="text-[14px] font-bold text-[#111827] mb-1">Social Media Plan</h3>
                <p className="text-[12px] text-[#9CA3AF] mb-4">What marketing wants captured on this tour — visible to the assigned tour leader.</p>
                <SocialMediaPlan requestId={id} role="staff" />
              </div>

              {isPadelante && (
                <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-5">
                  <h3 className="text-[14px] font-bold text-[#111827] mb-1">Game Content Checklist</h3>
                  <p className="text-[12px] text-[#9CA3AF] mb-4">The shot list for game day — the tour leader ticks each item off as it is captured.</p>
                  <GameContentChecklist requestId={id} />
                </div>
              )}
            </div>
          )}

          {/* ── Letters tab ─────────────────────────────────────────────── */}
          {activeTab === 'letters' && (
            <div className="space-y-4">

              {/* Tour Contract e-signing — MST and Your Tour UK → Spain tours
                  (both contracted with MST Sports Tours Ltd) */}
              {['mst', 'your_tour'].includes(request.brand || 'mst') && request.market !== 'spain_to_uk' && (
                <ContractSigning requestId={id} request={request} />
              )}

              {/* Send a Letter */}
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-[18px]">
                <h3 className="text-[14.5px] font-bold text-[#111827] mb-[14px]">Send a Letter</h3>
                {letterTemplatesAll.length === 0 && sentLetters.length === 0 ? (
                  <p className="text-[13px] text-[#9CA3AF]">No letter templates available. Create one in Admin Settings.</p>
                ) : (
                  <>
                    <div className="flex gap-3 items-end">
                      <div className="flex-1">
                        <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">Letter</label>
                        <select
                          value={selectedSendTemplateId}
                          onChange={(e) => setSelectedSendTemplateId(e.target.value)}
                          className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                        >
                          <option value="">— Select letter —</option>
                          {letterTemplatesAll.length > 0 && (
                            <optgroup label="Templates">
                              {letterTemplatesAll.map((t) => (
                                <option key={`template:${t.id}`} value={`template:${t.id}`}>{t.name}</option>
                              ))}
                            </optgroup>
                          )}
                          {sentLetters.length > 0 && (
                            <optgroup label="Previously sent">
                              {sentLetters.map((sl) => (
                                <option key={`sent:${sl.id}`} value={`sent:${sl.id}`}>
                                  {sl.subject || sl.templateName || 'Letter'} ({sl.uploaded ? 'Uploaded' : sl.trigger === 'resend' ? 'Resend' : sl.manual ? 'Manual' : 'Automatic'})
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      </div>
                      <button
                        onClick={handlePreviewLetter}
                        disabled={!selectedSendTemplateId || previewingLetter}
                        className="px-[18px] py-[9px] text-[13px] font-bold text-[#374151] bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] rounded-[9px] transition disabled:opacity-60 flex-shrink-0"
                      >
                        {previewingLetter ? 'Loading…' : 'Preview'}
                      </button>
                      <button
                        onClick={handleManualSend}
                        disabled={
                          !selectedSendTemplateId ||
                          sendingLetter ||
                          !/\S+@\S+\.\S+/.test(sendRecipientMode === 'contact' ? (request.customerEmail || '') : customRecipientEmail.trim())
                        }
                        className="px-[18px] py-[9px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60 flex-shrink-0"
                      >
                        {sendingLetter ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                    {previewLetterError && (
                      <p className="text-[12.5px] text-dr mt-2">{previewLetterError}</p>
                    )}

                    <div className="mt-3 pt-3 border-t border-[#F1F3F6] flex items-center gap-3 flex-wrap">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={sendRecipientMode === 'contact'}
                          onChange={(e) => setSendRecipientMode(e.target.checked ? 'contact' : 'custom')}
                          className="w-4 h-4 rounded border-[#D1D5DB] text-pb cursor-pointer"
                        />
                        <span className="text-[12.5px] text-[#374151]">
                          Send to tour contact{request.customerEmail ? ` (${request.customerEmail})` : ' — no email on file'}
                        </span>
                      </label>
                      {sendRecipientMode === 'custom' && (
                        <input
                          type="email"
                          value={customRecipientEmail}
                          onChange={(e) => setCustomRecipientEmail(e.target.value)}
                          placeholder="name@example.com"
                          className="flex-1 min-w-[200px] border border-[#E5E7EB] rounded-[9px] px-3 py-[7px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                        />
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Upload a Letter */}
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-[18px]">
                <h3 className="text-[14.5px] font-bold text-[#111827]">Upload a Letter</h3>
                <p className="text-[12px] text-[#6B7280] mt-1 mb-[14px]">
                  Add a letter written outside the portal to this tour's archive. It is not emailed — tick the box below to make it visible in the customer's Documents.
                </p>
                <div className="flex gap-3 items-end flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">Letter name (optional)</label>
                    <input
                      type="text"
                      value={letterUploadName}
                      onChange={(e) => setLetterUploadName(e.target.value)}
                      placeholder="Defaults to the file name"
                      className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                  </div>
                  <input
                    ref={letterUploadInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={handleLetterUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => letterUploadInputRef.current?.click()}
                    disabled={uploadingLetter}
                    className="px-[18px] py-[9px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60 flex-shrink-0"
                  >
                    {uploadingLetter ? 'Uploading…' : 'Choose File & Upload'}
                  </button>
                </div>
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={letterShareWithCustomer}
                    onChange={(e) => setLetterShareWithCustomer(e.target.checked)}
                    className="w-4 h-4 rounded border-[#D1D5DB] text-pb cursor-pointer"
                  />
                  <span className="text-[12.5px] text-[#374151]">Share with the customer in their portal</span>
                </label>
                {letterUploadError && (
                  <p className="text-[12.5px] text-dr mt-2">{letterUploadError}</p>
                )}
              </div>

              {/* Sent Letters */}
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
                <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
                  <h3 className="text-[14.5px] font-bold text-[#111827]">Letters</h3>
                  {(() => {
                    const uploaded = sentLetters.filter((l) => l.uploaded).length;
                    const sent = sentLetters.length - uploaded;
                    return (
                      <span className="text-[12px] text-[#6B7280]">
                        {sent} sent{uploaded > 0 && ` · ${uploaded} uploaded`}
                      </span>
                    );
                  })()}
                </div>
                {sentLetters.length === 0 ? (
                  <div className="px-[18px] py-10 text-center">
                    <p className="text-[13px] text-[#9CA3AF]">No letters have been sent or uploaded for this tour yet.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#F1F3F6]">
                    {sentLetters.map((sl) => (
                      <div key={sl.id} className="flex items-center gap-3 px-[18px] py-[13px]">
                        <span className="w-[34px] h-[34px] rounded-[9px] bg-blue-light flex items-center justify-center flex-shrink-0">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1A4A8A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 4h16v16H4z" /><path d="M22 6l-10 7L2 6" />
                          </svg>
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-bold text-[#111827] truncate">{sl.subject || sl.templateName || 'Letter'}</p>
                          <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">
                            {sl.uploaded
                              ? `Uploaded${sl.uploadedBy ? ` by ${sl.uploadedBy}` : ''} · ${sl.sharedWithCustomer ? 'Shared with customer' : 'Internal only'}`
                              : `${sl.trigger === 'resend' ? 'Resend' : sl.manual ? 'Manual' : `Auto · ${sl.trigger || ''}`} · To: ${sl.to || request.customerEmail || '—'}`}
                          </p>
                        </div>
                        {sl.downloadUrl && (
                          <a href={sl.downloadUrl} target="_blank" rel="noopener noreferrer"
                            className="flex-shrink-0 inline-flex items-center gap-1.5 px-[10px] py-[6px] text-[12px] font-semibold text-[#1A4A8A] bg-blue-light hover:bg-[#DCE7F5] rounded-[8px] transition">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </a>
                        )}
                        {sl.uploaded && (
                          <button
                            onClick={() => deleteUploadedLetter(sl)}
                            title="Remove this uploaded letter"
                            className="flex-shrink-0 px-[10px] py-[6px] text-[12px] font-semibold text-dr/70 hover:text-dr hover:bg-red-light rounded-[8px] transition"
                          >
                            Remove
                          </button>
                        )}
                        <div className="text-right flex-shrink-0">
                          <span className={`inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold ${sl.uploaded ? 'bg-blue-light text-pb' : 'bg-green-light text-sg'}`}>
                            <span className={`w-[5px] h-[5px] rounded-full ${sl.uploaded ? 'bg-pb' : 'bg-sg'}`} />
                            {sl.uploaded ? 'Uploaded' : 'Sent'}
                          </span>
                          {sl.sentAt?.toDate && (
                            <p className="text-[11px] text-[#9CA3AF] mt-1">
                              {sl.sentAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          )}

          {/* ── Resources tab ───────────────────────────────────────────── */}
          {activeTab === 'resources' && (
            <ResourcePack requestId={id} request={request} />
          )}

          {/* ── Previews tab (admins) ────────────────────────────────────── */}
          {activeTab === 'previews' && isAdmin && (
            <TourPreviewsTab requestId={id} request={request} />
          )}

          {/* ── Messages tab ────────────────────────────────────────────── */}
          {activeTab === 'messages' && (
            <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
              <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
                <div>
                  <h3 className="text-[14.5px] font-bold text-[#111827]">Messages</h3>
                  <p className="text-[12px] text-[#6B7280] mt-0.5">Private thread with {request.contactName || request.customerEmail}</p>
                </div>
              </div>
              <MessageThread requestId={id} senderRole="staff" />
            </div>
          )}

          {/* ── Quote approval crest modal ─────────────────────────────────── */}
          {showCrestModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
              <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-6 max-w-sm w-full shadow-xl">
                <h3 className="text-[15px] font-bold text-[#111827] mb-1">Send for approval</h3>
                <p className="text-[13px] text-[#6B7280] mb-5">
                  A branded PDF quote will be generated and emailed to <strong>info@mstsportstours.com</strong> for approval.
                  It only reaches <strong>{request.customerEmail}</strong> once approved. Optionally upload a club crest to include on the letter.
                </p>

                <div className="mb-4">
                  <p className="text-[12px] font-semibold text-[#374151] mb-2">Club crest (optional)</p>
                  <input
                    ref={crestInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleCrestFile}
                    className="hidden"
                  />
                  {crestDataUrl ? (
                    <div className="flex items-center gap-3">
                      <img src={crestDataUrl} alt="Crest preview" className="w-12 h-12 object-contain rounded border border-[#E5E7EB]" />
                      <button
                        type="button"
                        onClick={() => { setCrestDataUrl(null); if (crestInputRef.current) crestInputRef.current.value = ''; }}
                        className="text-[12px] text-dr/70 hover:text-dr transition"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => crestInputRef.current?.click()}
                      className="w-full py-2.5 border-2 border-dashed border-[#D1D5DB] rounded-[8px] text-[13px] text-[#6B7280] hover:border-pb hover:text-pb transition"
                    >
                      Upload crest image
                    </button>
                  )}
                </div>

                {quoteLetterError && (
                  <p className="text-[12px] text-dr mb-3">{quoteLetterError}</p>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCrestModal(false)}
                    className="flex-1 py-2.5 text-[13px] font-semibold text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-[8px] transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSendForApproval}
                    disabled={sendingQuoteLetter}
                    className="flex-1 py-2.5 text-[13px] font-semibold text-white bg-[#1A7A4A] hover:bg-[#155e38] rounded-[8px] transition disabled:opacity-60"
                  >
                    {sendingQuoteLetter ? 'Sending…' : 'Send for approval'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* ── Right: sidebar — every card in it is tour-pipeline stuff that
            doesn't apply until a CRM record converts, so the column is
            dropped entirely rather than left as empty reserved space. ── */}
        {!isCrmOrigin && (
        <div className="w-full 1080:w-[280px] 1080:flex-shrink-0 flex flex-col gap-4">

          {/* ── Status Pipeline — not relevant until a CRM record actually
              becomes a tour; the CRM panel in Overview covers pipeline
              progress until then ── */}
          {!isCrmOrigin && (
          <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
              <h3 className="text-[14px] font-bold text-[#111827]">Status Pipeline</h3>
              {!isCancelled && (
                <span className="text-[11.5px] font-semibold text-[#6B7280]">
                  {TOUR_CATEGORIES.filter((_, i) => i < currentCatIdx).length}/{TOUR_CATEGORIES.length} done
                </span>
              )}
            </div>

            <div className="divide-y divide-[#F1F3F6]">
              {isCancelled ? (
                <div className="px-5 py-6 text-center">
                  <span className={`inline-flex items-center gap-1 px-[10px] py-[4px] rounded-[20px] text-[12px] font-bold bg-red-light text-dr`}>
                    Cancelled
                  </span>
                </div>
              ) : (
                TOUR_CATEGORIES.map((catKey, catIdx) => {
                  const isPast    = catIdx < currentCatIdx;
                  const isCurrent = catKey === currentCategory;
                  const isFuture  = catIdx > currentCatIdx;
                  const stages    = stagesFor(catKey, request);
                  const catColor  = CATEGORY_COLORS[catKey] || '#1A4A8A';
                  const catFill   = CATEGORY_FILLS[catKey] || '#EBF2FC';

                  return (
                    <div key={catKey}
                      className={isCurrent ? 'border-l-[3px]' : ''}
                      style={isCurrent ? { borderLeftColor: catColor, background: `${catFill}55` } : {}}>
                      {/* Category header */}
                      <div className={`flex items-center gap-3 px-4 py-3 ${isFuture ? 'opacity-50' : ''}`}>
                        {/* Stage dot */}
                        <span className="flex-shrink-0 w-[18px] h-[18px] rounded-full flex items-center justify-center"
                          style={{
                            background: isPast ? '#1A7A4A' : isCurrent ? catColor : '#E5E7EB',
                            boxShadow: isCurrent ? `0 0 0 3px ${catColor}33` : undefined,
                          }}>
                          {isPast && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </span>
                        <span className="flex-1 text-[12.5px] font-semibold leading-tight"
                          style={{ color: isCurrent ? catColor : isPast ? '#374151' : '#9CA3AF' }}>
                          {CATEGORY_LABELS[catKey]}
                        </span>
                        {isPast && (
                          <button
                            onClick={() => handleGoBackToCategory(catKey)}
                            disabled={updating}
                            className="text-[11px] text-[#9CA3AF] hover:text-[#374151] transition flex-shrink-0"
                          >
                            ← Back
                          </button>
                        )}
                      </div>

                      {/* Sub-stages for current */}
                      {isCurrent && (
                        <div className="px-4 pb-4 space-y-1.5">
                          {stages.map((stage) => {
                            const ticked = !!stageProgress[stage.key];
                            return (
                              <label
                                key={stage.key}
                                className={`flex items-start gap-2.5 cursor-pointer ${updating ? 'opacity-60' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={ticked}
                                  onChange={() => handleStageToggle(stage.key)}
                                  disabled={updating}
                                  className="mt-0.5 flex-shrink-0"
                                  style={{ accentColor: '#1A4A8A' }}
                                />
                                <span className={`text-[12px] leading-[1.45] ${ticked ? 'line-through text-[#9CA3AF]' : 'text-[#374151]'}`}>
                                  {stage.label}
                                </span>
                              </label>
                            );
                          })}

                          {/* Advance / hint */}
                          {nextCat && (
                            <div className="pt-3 border-t border-[#E5E7EB]/60 mt-2">
                              {allCurrentComplete ? (
                                <button
                                  onClick={handleAdvanceCategory}
                                  disabled={updating}
                                  className="w-full text-center text-[12px] font-bold py-2 rounded-[9px] transition text-white bg-navy hover:opacity-90 disabled:opacity-60"
                                >
                                  Advance to {CATEGORY_LABELS[nextCat]} →
                                </button>
                              ) : (
                                <p className="text-[11px] text-[#9CA3AF] text-center">Complete all stages to advance</p>
                              )}
                            </div>
                          )}

                          {!nextCat && allCurrentComplete && (
                            <div className="pt-3 border-t border-[#E5E7EB]/60 mt-2">
                              <p className="text-[12px] text-sg font-bold text-center">All stages complete ✓</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Archive + Cancel actions */}
            <div className="px-5 py-3 border-t border-[#F1F3F6] flex items-center justify-between gap-3 flex-wrap">
              <button
                onClick={handleArchiveTour}
                disabled={updating}
                className="text-[12px] font-semibold text-[#6B7280] hover:text-navy transition disabled:opacity-60"
              >
                {request.archived ? 'Unarchive tour' : 'Archive tour'}
              </button>
              {!isCancelled && (
                <button
                  onClick={handleCancelTour}
                  disabled={updating}
                  className="text-[12px] font-semibold text-dr/70 hover:text-dr transition disabled:opacity-60"
                >
                  Mark as cancelled
                </button>
              )}
            </div>
          </div>
          )}

          {/* Tour Leader — hidden until this graduates out of the CRM */}
          {!isCrmOrigin && (
          <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-5">
            <h3 className="text-[14px] font-bold text-[#111827] mb-3">Tour Leaders <span className="text-[11px] font-normal text-[#9CA3AF]">(up to 3)</span></h3>
            {(() => {
              const assigned = getAssignedLeaders();
              const assignedIds = new Set(assigned.map((l) => l.id));
              const available = tourLeaders.filter((l) => !assignedIds.has(l.id));
              return (
                <div className="space-y-2">
                  {assigned.length === 0 && (
                    <p className="text-[12px] text-[#9CA3AF]">No tour leaders assigned yet.</p>
                  )}
                  {assigned.map((l, idx) => (
                    <div key={l.id} className="flex items-center justify-between gap-2 p-2.5 bg-[#FFF7ED] border border-[#FED7AA] rounded-[9px]">
                      <div className="min-w-0">
                        {idx === 0 && assigned.length > 1 && (
                          <span className="text-[10px] font-bold text-[#C2410C] uppercase tracking-[0.7px] block">Primary</span>
                        )}
                        <p className="text-[13px] font-bold text-[#C2410C] truncate">{l.name || l.email}</p>
                        {l.email && l.name && <p className="text-[11px] text-[#EA580C] truncate">{l.email}</p>}
                      </div>
                      <button
                        onClick={() => removeTourLeader(l.id)}
                        disabled={removingLeaderId === l.id}
                        className="text-[#EA580C]/60 hover:text-dr flex-shrink-0 transition disabled:opacity-40"
                        title="Remove"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ))}
                  {assigned.length < 3 && tourLeaders.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                      <select
                        value={selectedLeaderId}
                        onChange={(e) => setSelectedLeaderId(e.target.value)}
                        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                      >
                        <option value="">— Add a tour leader —</option>
                        {available.map((l) => (
                          <option key={l.id} value={l.id}>{l.name || l.email}</option>
                        ))}
                      </select>
                      {selectedLeaderId && (
                        <button
                          onClick={saveTourLeader}
                          disabled={savingLeader}
                          className="w-full text-center text-[12px] font-bold py-2 rounded-[8px] transition text-white bg-pb hover:bg-navy disabled:opacity-60"
                        >
                          {savingLeader ? 'Saving…' : 'Add'}
                        </button>
                      )}
                    </div>
                  )}
                  {assigned.length >= 3 && (
                    <p className="text-[11px] text-[#9CA3AF]">Maximum of 3 tour leaders assigned.</p>
                  )}
                  {tourLeaders.length === 0 && (
                    <p className="text-[12px] text-[#9CA3AF]">No tour leaders in the system yet.</p>
                  )}
                </div>
              );
            })()}
          </div>
          )}

          {/* Marketing — hidden until this graduates out of the CRM */}
          {!isCrmOrigin && (
          <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-5">
            <h3 className="text-[14px] font-bold text-[#111827] mb-3">Marketing</h3>
            {request.marketingId ? (
              <div className="flex items-center justify-between gap-2 p-2.5 bg-[#FCE7F3] border border-[#F9A8D4] rounded-[9px]">
                <div className="min-w-0">
                  <p className="text-[13px] font-bold text-[#9D174D] truncate">{request.marketingName || request.marketingEmail}</p>
                  {request.marketingEmail && request.marketingName && <p className="text-[11px] text-[#BE185D] truncate">{request.marketingEmail}</p>}
                </div>
                <button
                  onClick={removeMarketing}
                  disabled={savingMarketing}
                  className="text-[#BE185D]/60 hover:text-dr flex-shrink-0 transition disabled:opacity-40"
                  title="Remove"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ) : marketingUsers.length > 0 ? (
              <div className="space-y-1.5">
                <select
                  value={selectedMarketingId}
                  onChange={(e) => setSelectedMarketingId(e.target.value)}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                >
                  <option value="">— Assign a marketing contact —</option>
                  {marketingUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.name || u.email}</option>
                  ))}
                </select>
                {selectedMarketingId && (
                  <button
                    onClick={saveMarketing}
                    disabled={savingMarketing}
                    className="w-full text-center text-[12px] font-bold py-2 rounded-[8px] transition text-white bg-pb hover:bg-navy disabled:opacity-60"
                  >
                    {savingMarketing ? 'Saving…' : 'Assign'}
                  </button>
                )}
              </div>
            ) : (
              <p className="text-[12px] text-[#9CA3AF]">No marketing accounts in the system yet.</p>
            )}
          </div>
          )}

          {/* Automated Emails — hidden until this graduates out of the CRM */}
          {!isCrmOrigin && (
          <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[14px] font-bold text-[#111827]">Automated Emails</h3>
              <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${
                automationOn ? 'bg-green-light text-sg' : 'bg-[#F3F4F6] text-[#9CA3AF]'
              }`}>
                {automationOn ? 'On' : 'Off'}
              </span>
            </div>
            <p className="text-[12px] text-[#6B7280] mb-3">
              {automationOn
                ? 'Automatic emails will be sent for this tour (supplier approvals, tour leader notifications, reminders).'
                : 'Automatic emails are disabled — no emails will be sent automatically for this tour.'}
            </p>
            <button
              onClick={handleToggleAutomation}
              disabled={updating}
              className={`w-full text-[12px] font-bold py-2 rounded-[9px] transition disabled:opacity-60 border ${
                automationOn
                  ? 'border-dr/30 text-dr/80 hover:text-dr hover:border-dr/60 bg-white'
                  : 'border-sg/40 text-sg hover:bg-green-light bg-white'
              }`}
            >
              {automationOn ? 'Turn off automation' : 'Turn on automation'}
            </button>
          </div>
          )}

          {/* Notes — hidden until this graduates out of the CRM */}
          {!isCrmOrigin && (
            <TourNotes request={request} legacyNotes={request.internalNotes || ''} />
          )}

        </div>
        )}
      </div>
    </div>
  );
}
